import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { SignatureModalValue } from '../components/signature/SignatureModal';
import {
  createSignatureRecord,
  listSignatureRecordsForTarget,
  type SignatureRecord,
} from './signatures';
import type { TermsAndConditions } from './termsAndConditions';
import { apiFetch } from '../api/http';
import { refreshServiceTicketLogisticsDistance } from '../api/serviceTickets';
import { tripZoneForMiles, tripZonesFromCatalog } from '../domain/service';
import { estimateRoadMilesBetweenZips } from '../utils/zipDistance';
import { notifyLaborCalendar } from './laborCalendar';
import { getSupabaseClient } from './supabase';
import { serviceTicketFromSignedPayload } from '../../../shared/serviceSignedCustomerView.js';
import { AppError, runSupabaseQuery, toPartialSaveError } from '../utils/appErrors';
import {
  projectServiceWorkOrder,
  serviceWorkOrderLines,
  serviceWorkOrderSections,
  type ServiceWorkOrderProjection,
} from '../../../shared/serviceWorkOrderProjection.js';

type ClientOption = { supabase?: SupabaseClient };

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export type ServiceTicketStatus = 'open' | 'in_progress' | 'waiting_parts' | 'awaiting_return' | 'closed' | 'cancelled';
export type ServiceTicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type ServiceTicketType = 'asset_repair' | 'external';
export type ServiceTicketSource = 'portal' | 'workflow_engine';
/** Display name for tickets the Workflow Engine created from a synced service transfer. */
export const WORKFLOW_ENGINE_LABEL = 'Workflow Engine';
export type TicketAssetStatus = 'diagnose' | 'repair' | 'qa' | 'complete';
export type ServicePartStatus = 'planned' | 'ordered' | 'received' | 'consumed' | 'cancelled';
export type ServiceAssetOutcome = 'internal_repair' | 'vendor_repair' | 'retire_dispose' | 'replace_order' | 'cancelled';
export type ServiceReturnDestination = 'original_location' | 'service_inventory' | 'base_inventory';
export type ServiceLineItemType = 'item' | 'charge' | 'material' | 'other';
export type ServiceChargeCategory = 'travel' | 'diagnostic' | 'hourly' | 'additional';
export type ServiceChargeUnit = 'flat' | 'mile' | 'hour' | 'each';
export type ServiceTaskApplicability = 'internal' | 'external' | 'both' | 'pm_visit';
export type ServiceTicketTaskStatus = 'open' | 'completed' | 'cancelled';
export type ServiceTicketTimePreference = 'anytime' | 'general_am' | 'general_pm' | 'exact';
export type ClientServiceAgreementStatus = 'active' | 'expired' | 'cancelled' | 'awaiting_installation';
export type ServicePmVisitStatus = 'scheduled' | 'task_created' | 'completed' | 'cancelled';

export const SERVICE_TICKET_TIME_PREFERENCE_OPTIONS: Array<{ value: ServiceTicketTimePreference; label: string }> = [
  { value: 'anytime', label: 'Any Time' },
  { value: 'general_am', label: 'General AM' },
  { value: 'general_pm', label: 'General PM' },
  { value: 'exact', label: 'Exact time' },
];

export const SERVICE_TICKET_TIME_PREFERENCE_LABELS: Record<ServiceTicketTimePreference, string> = SERVICE_TICKET_TIME_PREFERENCE_OPTIONS
  .reduce((labels, option) => ({ ...labels, [option.value]: option.label }), {} as Record<ServiceTicketTimePreference, string>);

// The stored value for the final stage stays `closed`; every surface labels it "Completed".
// Anything reading logged action text must accept both spellings â€” see
// SERVICE_TICKET_COMPLETION_ACTION_TEXT below.
export const SERVICE_TICKET_STATUS_LABELS: Record<ServiceTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_parts: 'Waiting Parts',
  awaiting_return: 'Awaiting Return',
  closed: 'Completed',
  cancelled: 'Cancelled',
};

export type ExternalServiceTicketTitleRequest = {
  customerName?: string | null;
  serviceLocationName?: string | null;
  serviceRoom?: string | null;
  serviceAddress?: string | null;
  contactName?: string | null;
  priority?: ServiceTicketPriority | null;
  requestedServiceDate?: string | null;
  problemReported: string;
  timeoutMs?: number;
};

export const EXTERNAL_SERVICE_TICKET_TITLE_TIMEOUT_MS = 4500;
const DEFAULT_EXTERNAL_SERVICE_DIAGNOSTIC_CHARGE_CODE = 'flat_rate_diagnostic_labor';
const DEFAULT_EXTERNAL_SERVICE_TRIP_CHARGE_CODES_BY_RADIUS: Record<number, string> = {
  30: 'zone_1_trip_charge',
  60: 'zone_2_trip_charge',
  120: 'zone_3_trip_charge',
};

export type ServiceSettings = {
  serviceLocationId: string | null;
  serviceLocationName: string | null;
  replacementSourceLocationId: string | null;
  replacementSourceLocationName: string | null;
};

export type ServiceAssetRow = {
  asset_id: string;
  item_id: string | null;
  barcode: string | null;
  serial_number: string | null;
  status: string | null;
  service_status?: string | null;
  home_location_id?: string | null;
  current_location_id?: string | null;
  date_in_service?: string | null;
  received_at?: string | null;
  purchase_cost?: number | null;
  home_location?: { name?: string | null } | null;
  current_location?: { name?: string | null } | null;
  items?: {
    item_code?: string | null;
    name_internal?: string | null;
    replacement_cost?: number | null;
    purchase_cost_avg?: number | null;
    purchase_cost_last?: number | null;
  } | null;
};

export type ServiceTicketRow = {
  customer_view_source_hash?: string | null;
  customer_view_terms?: Pick<TermsAndConditions, 'tac_id' | 'name' | 'content'> | null;
  customer_view_published?: boolean;
  customer_view_unpublished?: boolean;
  updated_at?: string | null;
  service_ticket_id: string;
  doc_no: string | null;
  /** Internal GP reference; never used as the ticket or transfer UUID. */
  gp_service_ticket_no?: string | null;
  opened_at: string;
  opened_by: string | null;
  location_id: string | null;
  ticket_type?: ServiceTicketType;
  /** workflow_engine = created from a synced transfer (R2 today); portal = created by a Portal user. */
  source?: ServiceTicketSource | null;
  /** Inbound service transfer this Asset Ticket represents. */
  inbound_transfer_id?: string | null;
  client_id?: string | null;
  client_branch_id?: number | string | null;
  is_pm_visit?: boolean;
  pm_visit_id?: string | null;
  /** Cancelling a PM ticket releases its visit; reopening needs coverage chosen again. */
  pm_coverage_released?: boolean | null;
  service_agreement_assignment_id?: string | null;
  service_agreement_tier_id?: string | null;
  service_location_name?: string | null;
  service_room?: string | null;
  service_address_line1?: string | null;
  service_address_line2?: string | null;
  service_city?: string | null;
  service_state?: string | null;
  service_postal_code?: string | null;
  service_country?: string | null;
  service_contact_name?: string | null;
  service_contact_email?: string | null;
  service_contact_phone?: string | null;
  bill_to_name?: string | null;
  bill_to_address_line1?: string | null;
  bill_to_address_line2?: string | null;
  bill_to_city?: string | null;
  bill_to_state?: string | null;
  bill_to_postal_code?: string | null;
  bill_to_country?: string | null;
  bill_to_attn?: string | null;
  bill_to_email?: string | null;
  bill_to_phone?: string | null;
  payment_terms?: string | null;
  service_technician_profile_id?: string | null;
  requested_service_date?: string | null;
  requested_service_time_preference?: ServiceTicketTimePreference | null;
  requested_service_start_time?: string | null;
  scheduled_start_at?: string | null;
  invoice_date?: string | null;
  completed_date?: string | null;
  service_performed?: string | null;
  /** Why an external ticket has no Completed / After photo (required from non-admins). */
  completion_photo_waiver_reason?: string | null;
  sa_discount_percent?: number | null;
  tax_percent?: number | null;
  tax_region_code?: string | null;
  tax_sourcing_basis?: string | null;
  order_received_location_id?: string | null;
  tax_calculation_status?: string | null;
  tax_calculated_at?: string | null;
  tax_context?: Record<string, unknown> | null;
  subtotal_amount?: number | null;
  tax_amount?: number | null;
  total_amount?: number | null;
  client_visible?: boolean | null;
  updated_by?: string | null;
  priority: ServiceTicketPriority;
  status: ServiceTicketStatus;
  summary: string | null;
  description: string | null;
  location?: {
    name?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
    location_venues?: Array<{
      is_default?: boolean | null;
      venues?: {
        id?: string | null;
        venue_name?: string | null;
        address_line1?: string | null;
        address_line2?: string | null;
        city?: string | null;
        state?: string | null;
        postal_code?: string | null;
        country?: string | null;
      } | null;
    }> | null;
  } | null;
  opened_by_profile?: { full_name?: string | null; email?: string | null } | null;
  service_technician?: { full_name?: string | null; email?: string | null } | null;
  client?: ClientReferenceRow | null;
  client_branch?: ClientBranchReferenceRow | null;
  service_agreement_tier?: ServiceAgreementTierRow | null;
  service_ticket_line_items?: ServiceTicketLineItemRow[] | null;
  service_ticket_labor_lines?: ServiceTicketLaborLineRow[] | null;
  service_ticket_sections?: ServiceTicketSectionRow[] | null;
  /** Set on the client's public ticket JSON: the lines the client sees, grouped by section. */
  work_order?: ServiceTicketWorkOrder | null;
  service_ticket_tasks?: ServiceTicketTaskRow[] | null;
  service_ticket_logistics?: ServiceTicketLogisticsRow[] | null;
  service_ticket_assets?: ServiceTicketAssetRow[] | null;
};

export function normalizeServiceTicketTimePreference(value: unknown): ServiceTicketTimePreference {
  return value === 'general_am' || value === 'general_pm' || value === 'exact' || value === 'anytime'
    ? value
    : 'anytime';
}

export function normalizeServiceTicketTimeInput(value?: string | null): string {
  if (!value) return '';
  const match = /^(\d{2}):(\d{2})/.exec(value);
  if (!match) return '';
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour > 23 || minute > 59) return '';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function serviceTicketStartTimeForPreference(
  preference: ServiceTicketTimePreference,
  exactTime?: string | null,
): string {
  if (preference === 'general_pm') return '13:00';
  if (preference === 'exact') return normalizeServiceTicketTimeInput(exactTime) || '09:00';
  return '09:00';
}

function localTimeFromIso(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export function serviceTicketRequestedTimeDraft(ticket: Pick<ServiceTicketRow, 'requested_service_time_preference' | 'requested_service_start_time' | 'scheduled_start_at'>): {
  preference: ServiceTicketTimePreference;
  startTime: string;
} {
  const preference = normalizeServiceTicketTimePreference(ticket.requested_service_time_preference);
  const storedTime = normalizeServiceTicketTimeInput(ticket.requested_service_start_time);
  const scheduledTime = localTimeFromIso(ticket.scheduled_start_at);
  const startTime = preference === 'exact'
    ? storedTime || scheduledTime || '09:00'
    : serviceTicketStartTimeForPreference(preference, storedTime || scheduledTime);
  return { preference, startTime };
}

function formatClockLabel(value: string): string {
  const time = normalizeServiceTicketTimeInput(value);
  if (!time) return '--';
  const [hour, minute] = time.split(':').map(Number);
  const date = new Date(2000, 0, 1, hour, minute, 0, 0);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function serviceTicketRequestedTimeLabel(ticket: Pick<ServiceTicketRow, 'requested_service_time_preference' | 'requested_service_start_time' | 'scheduled_start_at'>): string {
  const draft = serviceTicketRequestedTimeDraft(ticket);
  if (draft.preference === 'exact') return formatClockLabel(draft.startTime);
  return SERVICE_TICKET_TIME_PREFERENCE_LABELS[draft.preference];
}

function dateOnly(value?: string | null): string | null {
  const text = String(value ?? '').trim();
  return text ? text.slice(0, 10) : null;
}

export function serviceTicketTaxExemptionDate(
  ticket: Pick<ServiceTicketRow, 'requested_service_date' | 'scheduled_start_at' | 'opened_at'>,
): string | null {
  return dateOnly(ticket.requested_service_date)
    ?? dateOnly(ticket.scheduled_start_at)
    ?? dateOnly(ticket.opened_at);
}

export function isServiceTicketCustomerTaxExempt(
  ticket: Pick<ServiceTicketRow, 'client' | 'requested_service_date' | 'scheduled_start_at' | 'opened_at'>,
): boolean {
  if (!ticket.client?.tax_exempt) return false;
  const exemptionDate = serviceTicketTaxExemptionDate(ticket);
  const expiresOn = dateOnly(ticket.client.tax_exempt_expires_on);
  if (!expiresOn || !exemptionDate) return true;
  return exemptionDate < expiresOn;
}

export type ClientReferenceRow = {
  id: string;
  business_name: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  set_discount_percent?: number | null;
  tax_exempt?: boolean | null;
  tax_exempt_expires_on?: string | null;
  payment_terms?: string | null;
  payment_term?: PaymentTermsReferenceRow | PaymentTermsReferenceRow[] | null;
};

export type ClientBranchReferenceRow = {
  id: number | string;
  branch_name: string | null;
  billing_address_line1?: string | null;
  billing_address_line2?: string | null;
  billing_city?: string | null;
  billing_state?: string | null;
  billing_postal_code?: string | null;
  billing_country?: string | null;
  shipping_address_line1?: string | null;
  shipping_address_line2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_postal_code?: string | null;
  shipping_country?: string | null;
  set_discount_percent?: number | null;
  payment_terms?: string | null;
  payment_term?: PaymentTermsReferenceRow | PaymentTermsReferenceRow[] | null;
};

type PaymentTermsReferenceRow = {
  payment_terms_id?: string | null;
  payment_terms_code?: string | null;
  description?: string | null;
};

export type ServiceAgreementTierRow = {
  tier_id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  term_months: number;
  pm_visit_count: number;
  pm_frequency_months: number | null;
  parts_labor_discount_percent: number;
  response_level: string | null;
  onsite_support: string | null;
  manufacturer_warranty: string | null;
  loaner_equipment: string | null;
  effective_start_date: string;
  effective_end_date: string | null;
  active: boolean;
  sort_order: number;
};

export type ClientServiceAgreementRow = {
  client_service_location_id?: string | null;
  source_agreement_option_id?: string | null;
  coverage_snapshot?: Record<string, unknown> | null;
  accepted_signature_record_id?: string | null;
  source_installation_job_id?: string | null;
  warranty_start_date?: string | null;
  warranty_end_date?: string | null;
  assignment_id: string;
  client_id: string;
  client_branch_id: number | string | null;
  tier_id: string;
  start_date: string;
  term_years: number;
  end_date: string;
  status: ClientServiceAgreementStatus;
  notes: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  tier?: ServiceAgreementTierRow | null;
  client_branch?: ClientBranchReferenceRow | null;
};

export type ServicePmVisitRow = {
  pm_visit_id: string;
  agreement_assignment_id: string;
  client_id: string;
  client_branch_id: number | string | null;
  tier_id: string;
  sequence_no: number;
  due_date: string;
  reminder_date: string;
  dashboard_task_id: string | null;
  status: ServicePmVisitStatus;
  generated_ticket_id: string | null;
  completed_at?: string | null;
  completed_by?: string | null;
  tier?: ServiceAgreementTierRow | null;
};

export type ServiceChargeCatalogRow = {
  charge_id: string;
  code: string;
  label: string;
  category: ServiceChargeCategory;
  charge_type_id?: string | null;
  taxable?: boolean | null;
  tax_category?: string | null;
  amount: number;
  unit: ServiceChargeUnit;
  radius_miles: number | null;
  after_hours: boolean;
  included_labor_hours: number;
  effective_start_date: string;
  effective_end_date: string | null;
  active: boolean;
  sort_order: number;
};

export type ServiceLaborCatalogRow = {
  labor_id: string;
  code: string;
  name: string;
  charge_type_id?: string | null;
  taxable?: boolean | null;
  tax_category?: string | null;
  uom: 'hourly' | 'half_day' | 'full_day';
  /** Regular-time (RT) rate per unit. */
  hourly_rate: number;
  /** The overtime (OT) rate per unit. Double time is always twice hourly_rate. */
  after_hours_rate: number | null;
  effective_start_date: string;
  effective_end_date: string | null;
  active: boolean;
  sort_order: number;
};

export type ServiceTaskTemplateRow = {
  task_template_id: string;
  code: string | null;
  title: string;
  description: string | null;
  applies_to: ServiceTaskApplicability;
  add_by_default: boolean;
  active: boolean;
  sort_order: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ServiceTicketLineItemRow = {
  pm_managed?: boolean;
  line_item_id: string;
  service_ticket_id: string;
  line_type: ServiceLineItemType;
  item_id: string | null;
  charge_id: string | null;
  description: string | null;
  quantity: number;
  unit_cost?: number | null;
  margin_percent?: number | null;
  unit_price: number;
  discount_percent: number;
  taxable: boolean;
  tax_category?: string | null;
  sort_order: number;
  /** The charges-grid section this line sits under; null keeps it with the unsectioned lines. */
  section_id?: string | null;
  /** Hidden from the client but still billed (Alex, 2026-09-30). */
  non_printable?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  charge?: ServiceChargeCatalogRow | null;
  item?: { item_code?: string | null; name_internal?: string | null } | null;
};

export type ServiceTicketSectionGrid = 'charges' | 'labor';

/** What the client sees of a ticket's lines; see serviceTicketWorkOrder. */
export type ServiceTicketWorkOrder = ServiceWorkOrderProjection<ServiceTicketLineItemRow, ServiceTicketLaborLineRow>;

/** A heading over a ticket's charges or labor lines. It groups lines and never prices. */
export type ServiceTicketSectionRow = {
  section_id: string;
  service_ticket_id: string;
  grid: ServiceTicketSectionGrid;
  label: string;
  sort_order: number;
  /** Show a subtotal row after the section's lines; it includes Non-Printable lines. */
  show_subtotal: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ServiceTicketLaborLineRow = {
  labor_line_id: string;
  service_ticket_id: string;
  labor_id: string | null;
  /** Legacy: who worked is on the ticket's /schedule visits. Old rows keep a value; nothing writes it. */
  technician_profile_id: string | null;
  role_name: string | null;
  /** Legacy, like technician_profile_id: visits carry the dates. */
  service_date?: string | null;
  description: string | null;
  uom: 'hourly' | 'half_day' | 'full_day';
  /** Regular-time (RT) units per person, in the row's uom. */
  hours: number;
  /** Regular-time (RT) rate per unit. */
  hourly_rate: number;
  /** Overtime (OT) units per person and the OT rate per unit. */
  ot_units?: number | null;
  ot_rate?: number | null;
  /** Double-time (DT) units per person; they bill at twice hourly_rate (serviceLaborDtRate). */
  dt_units?: number | null;
  /** Internal note; never shown to the client. */
  notes?: string | null;
  /** Set on the client's public ticket JSON, which leaves out labor rates and notes. */
  extended_amount?: number | null;
  crew_quantity?: number | null;
  billable: boolean;
  taxable?: boolean | null;
  tax_category?: string | null;
  sort_order: number;
  /** The labor-grid section this line sits under; null keeps it with the unsectioned lines. */
  section_id?: string | null;
  /** Hidden from the client but still billed (Alex, 2026-09-30). */
  non_printable?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  labor?: ServiceLaborCatalogRow | null;
  technician?: { full_name?: string | null; email?: string | null } | null;
};

export type ServiceTicketTaskRow = {
  task_id: string;
  service_ticket_id: string;
  task_template_id: string | null;
  title: string;
  description: string | null;
  status: ServiceTicketTaskStatus;
  assigned_profile_id: string | null;
  priority?: 'low' | 'normal' | 'high' | 'urgent' | null;
  due_at?: string | null;
  completed_at: string | null;
  completed_by_profile_id: string | null;
  sort_order: number;
  created_at?: string | null;
  updated_at?: string | null;
  assignee?: { full_name?: string | null; email?: string | null } | null;
  completed_by?: { full_name?: string | null; email?: string | null } | null;
};

export type ServiceTicketLogisticsRow = {
  logistics_id: string;
  service_ticket_id: string;
  origin_location_id: string | null;
  destination_client_id: string | null;
  destination_client_branch_id: number | string | null;
  origin_snapshot: Record<string, unknown>;
  destination_snapshot: Record<string, unknown>;
  total_miles: number | null;
  travel_hours: number | null;
  notes: string | null;
  collapsed: boolean;
};

export type ServiceTicketScheduleVehicleAssignmentRow = {
  assignment_id: string;
  shift_id: string;
  vehicle_id: string;
  status: 'assigned' | 'cancelled' | 'removed';
  display_name: string | null;
  vehicle_type: string | null;
  license_plate: string | null;
  capacity: string | null;
};

export type ServiceTicketScheduleShiftRow = {
  shift_id: string;
  service_ticket_id: string;
  service_ticket_labor_line_id?: string | null;
  site_location_id: string | null;
  starts_at: string;
  ends_at: string | null;
  schedule_status: string;
  updated_at?: string | null;
  vehicle_assignments: ServiceTicketScheduleVehicleAssignmentRow[];
};

export type ServiceTicketChatConversationRow = {
  conversation_id: string;
  conversation_type: 'group';
  entity_id?: string | null;
  title?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
};

export type ActiveAssetServiceTicket = {
  serviceTicketAssetId: string;
  serviceTicketId: string;
  ticketNumber: string | null;
  ticketStatus: ServiceTicketStatus | string;
  ticketAssetStatus: TicketAssetStatus | string | null;
  issueNotes: string | null;
  summary: string | null;
  openedAt: string | null;
};

export type ServiceTicketTransferLineRow = {
  line_no?: number | null;
  status?: string | null;
  qty?: number | null;
  ordered_qty?: number | null;
  shipped_qty?: number | null;
  received_qty?: number | null;
  shipped_at?: string | null;
  received_at?: string | null;
};

/**
 * A ticket item. Rows keyed `line:` are the inbound transfer's lines (item + quantity);
 * rows keyed `asset:` are identified serialized assets on those lines. Portal-created
 * tickets without a transfer only have asset rows (no key).
 */
export type ServiceTicketAssetRow = {
  service_ticket_asset_id: string;
  service_ticket_id: string;
  asset_id: string | null;
  product_id: string | null;
  transfer_line_id?: string | null;
  item_id?: string | null;
  quantity?: number | null;
  source_line_key?: string | null;
  item?: { item_code?: string | null; name_internal?: string | null } | null;
  transfer_line?: ServiceTicketTransferLineRow | null;
  issue_notes: string | null;
  status: TicketAssetStatus;
  original_location_id?: string | null;
  current_location_id_at_intake?: string | null;
  service_location_id?: string | null;
  inbound_transfer_id?: string | null;
  return_transfer_id?: string | null;
  replacement_transfer_id?: string | null;
  replacement_asset_id?: string | null;
  vendor_po_id?: string | null;
  replacement_po_id?: string | null;
  outcome?: ServiceAssetOutcome | null;
  return_destination?: ServiceReturnDestination | null;
  diagnosis_notes?: string | null;
  findings_notes?: string | null;
  work_performed_notes?: string | null;
  recommendation_notes?: string | null;
  repair_cost_estimate?: number | null;
  repair_cost_actual?: number | null;
  replacement_cost_snapshot?: number | null;
  asset_age_months?: number | null;
  recommendation_score?: number | null;
  recommendation_label?: string | null;
  vendor_id?: string | null;
  vendor_tracking_ref?: string | null;
  sent_to_vendor_at?: string | null;
  returned_from_vendor_at?: string | null;
  disposal_notes?: string | null;
  retired_at?: string | null;
  completed_at?: string | null;
  asset?: ServiceAssetRow | null;
  replacement_asset?: ServiceAssetRow | null;
};

export type ServiceActionRow = {
  service_action_id: string;
  service_ticket_id: string;
  action_at: string;
  action_by: string | null;
  action_by_profile?: { full_name?: string | null; email?: string | null } | null;
  action: string | null;
  details: Record<string, unknown> | null;
};

export type ServicePartRow = {
  service_part_id: string;
  service_ticket_id: string;
  description: string | null;
  vendor_id: string | null;
  qty: number | null;
  unit_cost: number | null;
  po_line_id: string | null;
  status: ServicePartStatus;
  vendor?: { name?: string | null } | null;
};

export type ServicePurchaseOrderRow = {
  po_id: string;
  doc_no: string | null;
  service_ticket_id: string | null;
  vendor_id: string | null;
  location_id: string | null;
  status: string;
  ordered_at: string | null;
  expected_delivery_at: string | null;
  notes: string | null;
  total_amount: number | null;
  created_at?: string | null;
  vendor?: { name?: string | null } | null;
  location?: { name?: string | null } | null;
};

export type ServicePurchaseOrderLineRow = {
  po_line_id: string;
  po_id: string;
  description: string | null;
  item_id: string | null;
  qty: number | null;
  unit_cost: number | null;
  received_qty: number | null;
  status: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ReceivedReplacementAssetCandidateRow = ServiceAssetRow & {
  po_id: string;
  po_doc_no: string | null;
  po_status: string | null;
  po_line_id: string;
  po_line_description: string | null;
  po_line_received_qty: number;
  po_location_id: string | null;
  po_location_name: string | null;
  receipt_source: 'receipt_audit' | 'received_asset';
};

type ServiceTicketAssetReplacementLookupRow = Pick<ServiceTicketAssetRow,
  'service_ticket_asset_id' | 'service_ticket_id' | 'original_location_id' | 'replacement_asset_id' | 'replacement_po_id'
> & {
  asset_id: string | null;
  asset?: ServiceAssetRow | ServiceAssetRow[] | null;
};

/** Replacements apply to an identified serialized asset, never to a bare transfer line. */
function identifiedReplacementRow(row: ServiceTicketAssetReplacementLookupRow): (ServiceTicketAssetReplacementLookupRow & { asset_id: string }) | null {
  return row.asset_id ? (row as ServiceTicketAssetReplacementLookupRow & { asset_id: string }) : null;
}

type ReplacementPurchaseOrderLookupRow = Pick<ServicePurchaseOrderRow,
  'po_id' | 'doc_no' | 'location_id' | 'status' | 'created_at'
> & {
  location?: { name?: string | null } | Array<{ name?: string | null }> | null;
  lines?: ServicePurchaseOrderLineRow[] | null;
};

type PurchaseOrderReceiptIdRow = {
  po_receipt_id: string | null;
};

type PurchaseOrderReceiptAssetLineRow = {
  po_receipt_line_id: string;
  po_line_id: string | null;
  barcode: string | null;
  serial_number: string | null;
  applied_received_qty: number | null;
  decision: string | null;
  created_at?: string | null;
};

type ServiceReplacementLinkRow = {
  service_ticket_asset_id: string;
  replacement_asset_id: string | null;
};

export type AssetServiceHistoryRow = {
  service_ticket_asset_id: string;
  service_ticket_id: string;
  ticket_doc_no: string | null;
  ticket_status: ServiceTicketStatus;
  ticket_summary: string | null;
  ticket_description: string | null;
  ticket_asset_status: TicketAssetStatus;
  issue: string | null;
  resolution: string | null;
  date_sent: string | null;
  date_returned: string | null;
  service_tech: string | null;
  owner_location: string | null;
};

export type ItemServiceHistoryRow = AssetServiceHistoryRow & {
  asset_id: string | null;
  asset_barcode: string | null;
  asset_serial: string | null;
  asset_label: string | null;
  quantity: number | null;
  is_asset_linked: boolean;
};

// â”€â”€â”€ Query Keys â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export const serviceKeys = {
  all: ['service-tickets'] as const,
  list: (filters?: Record<string, unknown>) => ['service-tickets', 'list', filters] as const,
  detail: (id: string) => ['service-tickets', 'detail', id] as const,
  actions: (id: string) => ['service-tickets', 'actions', id] as const,
  parts: (id: string) => ['service-tickets', 'parts', id] as const,
  purchaseOrders: (id: string) => ['service-tickets', 'purchase-orders', id] as const,
  transfers: (inboundTransferId: string) => ['service-tickets', 'transfers', inboundTransferId] as const,
  settings: () => ['service-tickets', 'settings'] as const,
  tiers: () => ['service-tickets', 'tiers'] as const,
  charges: () => ['service-tickets', 'charges'] as const,
  estimateOrigin: () => ['service-tickets', 'estimate-origin'] as const,
  laborCatalog: () => ['service-tickets', 'labor-catalog'] as const,
  taskTemplates: () => ['service-tickets', 'task-templates'] as const,
  tasks: (ticketId: string) => ['service-tickets', 'tasks', ticketId] as const,
  clientAgreements: (clientId: string) => ['service-tickets', 'client-agreements', clientId] as const,
  clientTickets: (clientId: string) => ['service-tickets', 'client-tickets', clientId] as const,
  clientPmVisits: (clientId: string) => ['service-tickets', 'client-pm-visits', clientId] as const,
  replacementCandidates: (ticketAssetId: string) => ['service-tickets', 'replacement-candidates', ticketAssetId] as const,
  receivedReplacementCandidates: (ticketAssetId: string) => ['service-tickets', 'received-replacement-candidates', ticketAssetId] as const,
  assetHistory: (assetId: string) => ['service-tickets', 'asset-history', assetId] as const,
  itemHistory: (itemId: string) => ['service-tickets', 'item-history', itemId] as const,
  signatures: (ticketId: string) => ['service-tickets', 'signatures', ticketId] as const,
  scheduleShift: (ticketId: string) => ['service-tickets', 'schedule-shift', ticketId] as const,
  scheduleShifts: (ticketId: string) => ['service-tickets', 'schedule-shifts', ticketId] as const,
  processPermission: (ticketId: string) => ['service-tickets', 'process-permission', ticketId] as const,
  chatConversation: (ticketId: string) => ['service-tickets', 'chat-conversation', ticketId] as const,
  counts: (filters?: Record<string, unknown>) => ['service-tickets', 'counts', filters] as const,
};

// â”€â”€â”€ Select strings â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const TICKET_LIST_SELECT = `
  service_ticket_id, doc_no, opened_at, opened_by, location_id, updated_at,
  ticket_type, source, inbound_transfer_id, client_id, client_branch_id, service_agreement_tier_id,
  service_technician_profile_id, priority, status, summary, description, requested_service_date, total_amount,
  location:locations!fk_st_loc(
    name,address_line1,address_line2,city,state,postal_code,country,
    location_venues!left(
      is_default,
      venues!inner(id,venue_name,address_line1,address_line2,city,state,postal_code,country)
    )
  ),
  client:clients!service_tickets_client_id_fkey(id, business_name),
  client_branch:client_branches!service_tickets_client_branch_id_fkey(id, branch_name),
  service_agreement_tier:service_agreement_tiers!service_tickets_service_agreement_tier_id_fkey(tier_id, code, name),
  service_technician:user_profiles!service_tickets_service_technician_profile_id_fkey(full_name, email)
`.replace(/\s+/g, ' ').trim();

const TICKET_TASK_DETAIL_EMBED = `
  service_ticket_tasks(
    task_id, service_ticket_id, task_template_id, title, description, status, assigned_profile_id,
    priority, due_at, completed_at, completed_by_profile_id, sort_order, created_at, updated_at,
    assignee:user_profiles!service_ticket_tasks_assigned_profile_id_fkey(full_name, email),
    completed_by:user_profiles!service_ticket_tasks_completed_by_profile_id_fkey(full_name, email)
  )
`.replace(/\s+/g, ' ').trim();

// 20261001003526: sections and Non-Printable lines. The legacy selects strip both.
const SERVICE_LINE_SECTION_COLUMNS = ', section_id, non_printable';
const TICKET_SECTIONS_EMBED =
  'service_ticket_sections(section_id, service_ticket_id, grid, label, sort_order, show_subtotal, created_at, updated_at)';

const TICKET_DETAIL_SELECT = `
  service_ticket_id, doc_no, gp_service_ticket_no, opened_at, opened_by, location_id, updated_at,
  ticket_type, source, inbound_transfer_id, is_pm_visit, pm_visit_id, pm_coverage_released, client_id, client_branch_id, service_agreement_assignment_id, service_agreement_tier_id,
  service_location_name, service_room, service_address_line1, service_address_line2, service_city, service_state,
  service_postal_code, service_country, service_contact_name, service_contact_email, service_contact_phone,
  bill_to_name, bill_to_address_line1, bill_to_address_line2, bill_to_city, bill_to_state,
  bill_to_postal_code, bill_to_country, bill_to_attn, bill_to_email, bill_to_phone, payment_terms,
  service_technician_profile_id, requested_service_date, requested_service_time_preference,
  requested_service_start_time, scheduled_start_at, invoice_date, completed_date, service_performed,
  completion_photo_waiver_reason,
  sa_discount_percent, tax_percent, tax_region_code, tax_sourcing_basis, order_received_location_id,
  tax_calculation_status, tax_calculated_at, tax_context,
  subtotal_amount, tax_amount, total_amount, client_visible, updated_by,
  priority, status, summary, description,
  location:locations!fk_st_loc(
    name,address_line1,address_line2,city,state,postal_code,country,
    location_venues!left(
      is_default,
      venues!inner(id,venue_name,address_line1,address_line2,city,state,postal_code,country)
    )
  ),
  service_technician:user_profiles!service_tickets_service_technician_profile_id_fkey(full_name, email),
  client:clients!service_tickets_client_id_fkey(
    id, business_name, address_line1, address_line2, city, state, postal_code, country,
    set_discount_percent, tax_exempt, tax_exempt_expires_on
  ),
  client_branch:client_branches!service_tickets_client_branch_id_fkey(
    id, branch_name, billing_address_line1, billing_address_line2, billing_city, billing_state,
    billing_postal_code, billing_country, shipping_address_line1, shipping_address_line2,
    shipping_city, shipping_state, shipping_postal_code, shipping_country, set_discount_percent
  ),
  service_agreement_tier:service_agreement_tiers!service_tickets_service_agreement_tier_id_fkey(
    tier_id, code, name, description, price, term_months, pm_visit_count, pm_frequency_months,
    parts_labor_discount_percent, response_level, onsite_support, manufacturer_warranty,
    loaner_equipment, effective_start_date, effective_end_date, active, sort_order
  ),
  service_ticket_line_items(
    line_item_id, service_ticket_id, line_type, item_id, charge_id, description, quantity,
    unit_cost, margin_percent, unit_price, discount_percent, taxable, tax_category, sort_order${SERVICE_LINE_SECTION_COLUMNS}, created_at, updated_at, pm_managed,
    charge:service_charge_catalog!service_ticket_line_items_charge_id_fkey(
      charge_id, code, label, category, charge_type_id, taxable, tax_category, amount, unit, radius_miles, after_hours,
      included_labor_hours, effective_start_date, effective_end_date, active, sort_order
    ),
    item:items!service_ticket_line_items_item_id_fkey(item_code, name_internal)
  ),
  service_ticket_labor_lines(
    labor_line_id, service_ticket_id, labor_id, technician_profile_id, role_name, description,
    service_date, uom, hours, hourly_rate, ot_units, ot_rate, dt_units, notes, crew_quantity, billable, taxable, tax_category, sort_order${SERVICE_LINE_SECTION_COLUMNS}, created_at, updated_at,
    labor:service_labor_catalog!service_ticket_labor_lines_labor_id_fkey(
      labor_id, code, name, charge_type_id, taxable, tax_category, uom, hourly_rate, after_hours_rate, effective_start_date, effective_end_date, active, sort_order
    ),
    technician:user_profiles!service_ticket_labor_lines_technician_profile_id_fkey(full_name, email)
  ),
  ${TICKET_SECTIONS_EMBED},
  ${TICKET_TASK_DETAIL_EMBED},
  service_ticket_logistics(
    logistics_id, service_ticket_id, origin_location_id, destination_client_id, destination_client_branch_id,
    origin_snapshot, destination_snapshot, total_miles, travel_hours, notes, collapsed
  ),
  service_ticket_assets(
    service_ticket_asset_id, service_ticket_id, asset_id, product_id, issue_notes, status,
    original_location_id, current_location_id_at_intake, service_location_id,
    inbound_transfer_id, return_transfer_id, replacement_transfer_id,
    replacement_asset_id, vendor_po_id, replacement_po_id,
    outcome, return_destination, diagnosis_notes, findings_notes, work_performed_notes,
    recommendation_notes, repair_cost_estimate, repair_cost_actual, replacement_cost_snapshot,
    asset_age_months, recommendation_score, recommendation_label, vendor_id, vendor_tracking_ref,
    sent_to_vendor_at, returned_from_vendor_at, disposal_notes, retired_at, completed_at,
    transfer_line_id, item_id, quantity, source_line_key,
    item:items!service_ticket_assets_item_fkey(item_code, name_internal),
    transfer_line:transfer_lines!service_ticket_assets_transfer_line_fkey(
      line_no, status, qty, ordered_qty, shipped_qty, received_qty, shipped_at, received_at
    ),
    asset:assets!fk_sta_asset(
      asset_id, barcode, serial_number, status, service_status, item_id,
      home_location_id, current_location_id, date_in_service, received_at, purchase_cost,
      home_location:locations!assets_home_location_id_fkey(name),
      current_location:locations!assets_current_location_id_fkey(name),
      items!left(item_code, name_internal, replacement_cost, purchase_cost_avg, purchase_cost_last)
    ),
    replacement_asset:assets!service_ticket_assets_replacement_asset_id_fkey(
      asset_id, barcode, serial_number, status, service_status, item_id,
      home_location_id, current_location_id,
      home_location:locations!assets_home_location_id_fkey(name),
      current_location:locations!assets_current_location_id_fkey(name),
      items!left(item_code, name_internal, replacement_cost, purchase_cost_avg, purchase_cost_last)
    )
  )
`.replace(/\s+/g, ' ').trim();

// â”€â”€â”€ Fetch Functions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const SERVICE_ASSET_SELECT = `
  asset_id, item_id, barcode, serial_number, status, service_status,
  home_location_id, current_location_id, date_in_service, received_at, purchase_cost,
  home_location:locations!assets_home_location_id_fkey(name),
  current_location:locations!assets_current_location_id_fkey(name),
  items!left(item_code, name_internal, replacement_cost, purchase_cost_avg, purchase_cost_last)
`.replace(/\s+/g, ' ').trim();

const SERVICE_TIER_SELECT = `
  tier_id, code, name, description, price, term_months, pm_visit_count, pm_frequency_months,
  parts_labor_discount_percent, response_level, onsite_support, manufacturer_warranty,
  loaner_equipment, effective_start_date, effective_end_date, active, sort_order
`.replace(/\s+/g, ' ').trim();

const SERVICE_CHARGE_SELECT = `
  charge_id, code, label, category, charge_type_id, taxable, tax_category, amount, unit, radius_miles, after_hours,
  included_labor_hours, effective_start_date, effective_end_date, active, sort_order
`.replace(/\s+/g, ' ').trim();

const SERVICE_CHARGE_SELECT_LEGACY = `
  charge_id, code, label, category, amount, unit, radius_miles, after_hours,
  included_labor_hours, effective_start_date, effective_end_date, active, sort_order
`.replace(/\s+/g, ' ').trim();

const SERVICE_LABOR_SELECT = `
  labor_id, code, name, charge_type_id, taxable, tax_category, uom, hourly_rate, after_hours_rate, effective_start_date, effective_end_date, active, sort_order
`.replace(/\s+/g, ' ').trim();

const SERVICE_LABOR_SELECT_LEGACY = `
  labor_id, code, name, uom, hourly_rate, after_hours_rate, effective_start_date, effective_end_date, active, sort_order
`.replace(/\s+/g, ' ').trim();

const SERVICE_TASK_TEMPLATE_SELECT = `
  task_template_id, code, title, description, applies_to, add_by_default, active, sort_order, created_at, updated_at
`.replace(/\s+/g, ' ').trim();

const TICKET_DETAIL_SELECT_LEGACY = TICKET_DETAIL_SELECT
  .replace(' completion_photo_waiver_reason,', '')
  .replace(
    'scheduled_start_at, invoice_date, completed_date, service_performed',
    'scheduled_start_at, invoice_date, service_performed',
  )
  .replace(
    'sa_discount_percent, tax_percent, tax_region_code, tax_sourcing_basis, order_received_location_id, tax_calculation_status, tax_calculated_at, tax_context, subtotal_amount',
    'sa_discount_percent, tax_percent, subtotal_amount',
  )
  .replace(
    'unit_cost, margin_percent, unit_price, discount_percent, taxable, tax_category, sort_order',
    'unit_cost, margin_percent, unit_price, discount_percent, taxable, sort_order',
  )
  .replace(
    'charge_id, code, label, category, charge_type_id, taxable, tax_category, amount',
    'charge_id, code, label, category, amount',
  )
  .replace(
    'description, service_date, uom, hours, hourly_rate, ot_units, ot_rate, dt_units, notes, crew_quantity, billable, taxable, tax_category, sort_order',
    'description, uom, hours, hourly_rate, billable, sort_order',
  )
  .replace(
    'labor_id, code, name, charge_type_id, taxable, tax_category, uom',
    'labor_id, code, name, uom',
  )
  .split(SERVICE_LINE_SECTION_COLUMNS).join('')
  .replace(`, ${TICKET_SECTIONS_EMBED}`, '');

const SERVICE_TICKET_LINE_ITEM_SELECT = `
  line_item_id, service_ticket_id, line_type, item_id, charge_id, description, quantity,
  unit_cost, margin_percent, unit_price, discount_percent, taxable, tax_category, sort_order${SERVICE_LINE_SECTION_COLUMNS}, created_at, updated_at, pm_managed,
  charge:service_charge_catalog!service_ticket_line_items_charge_id_fkey(${SERVICE_CHARGE_SELECT}),
  item:items!service_ticket_line_items_item_id_fkey(item_code, name_internal)
`.replace(/\s+/g, ' ').trim();

const SERVICE_TICKET_LINE_ITEM_SELECT_LEGACY = `
  line_item_id, service_ticket_id, line_type, item_id, charge_id, description, quantity,
  unit_cost, margin_percent, unit_price, discount_percent, taxable, sort_order, created_at, updated_at,
  charge:service_charge_catalog!service_ticket_line_items_charge_id_fkey(${SERVICE_CHARGE_SELECT_LEGACY}),
  item:items!service_ticket_line_items_item_id_fkey(item_code, name_internal)
`.replace(/\s+/g, ' ').trim();

const SERVICE_TICKET_LABOR_LINE_SELECT = `
  labor_line_id, service_ticket_id, labor_id, technician_profile_id, role_name, description,
  service_date, uom, hours, hourly_rate, ot_units, ot_rate, dt_units, notes, crew_quantity, billable, taxable, tax_category, sort_order${SERVICE_LINE_SECTION_COLUMNS}, created_at, updated_at,
  labor:service_labor_catalog!service_ticket_labor_lines_labor_id_fkey(${SERVICE_LABOR_SELECT}),
  technician:user_profiles!service_ticket_labor_lines_technician_profile_id_fkey(full_name, email)
`.replace(/\s+/g, ' ').trim();

const SERVICE_TICKET_LABOR_LINE_SELECT_LEGACY = `
  labor_line_id, service_ticket_id, labor_id, technician_profile_id, role_name, description,
  uom, hours, hourly_rate, billable, sort_order, created_at, updated_at,
  labor:service_labor_catalog!service_ticket_labor_lines_labor_id_fkey(${SERVICE_LABOR_SELECT_LEGACY}),
  technician:user_profiles!service_ticket_labor_lines_technician_profile_id_fkey(full_name, email)
`.replace(/\s+/g, ' ').trim();

const SERVICE_TICKET_TASK_SELECT = `
  task_id, service_ticket_id, task_template_id, title, description, status, assigned_profile_id,
  priority, due_at, completed_at, completed_by_profile_id, sort_order, created_at, updated_at,
  assignee:user_profiles!service_ticket_tasks_assigned_profile_id_fkey(full_name, email),
  completed_by:user_profiles!service_ticket_tasks_completed_by_profile_id_fkey(full_name, email)
`.replace(/\s+/g, ' ').trim();

function normalizeText(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function firstRelated<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function displayAssetLabel(asset: Partial<ServiceAssetRow> | null | undefined): string {
  const item = firstRelated(asset?.items);
  return (
    item?.name_internal ||
    item?.item_code ||
    asset?.barcode ||
    asset?.serial_number ||
    (asset?.asset_id ? String(asset.asset_id).slice(0, 8) : 'Asset')
  );
}

export function formatServiceTicketReference(ticket: Pick<ActiveAssetServiceTicket, 'serviceTicketId' | 'ticketNumber'> | null | undefined): string {
  if (!ticket) return 'unknown';
  return ticket.ticketNumber || ticket.serviceTicketId.slice(0, 8);
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.map((value) => normalizeText(value)).filter(Boolean)));
}

function numericValue(value: unknown): number {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

function sameAssetLocation(asset: ServiceAssetRow, locationId: string | null | undefined): boolean {
  if (!locationId) return true;
  return asset.current_location_id === locationId || (!asset.current_location_id && asset.home_location_id === locationId);
}

export function calculateServiceRecommendation(params: {
  repairCostEstimate?: number | null;
  repairCostActual?: number | null;
  replacementCost?: number | null;
  assetAgeMonths?: number | null;
}): { score: number | null; label: string; notes: string } {
  const repairCost = Number(params.repairCostActual ?? params.repairCostEstimate ?? 0);
  const replacementCost = Number(params.replacementCost ?? 0);
  const ageMonths = Number(params.assetAgeMonths ?? 0);
  if (!Number.isFinite(repairCost) || repairCost <= 0 || !Number.isFinite(replacementCost) || replacementCost <= 0) {
    return {
      score: null,
      label: 'Needs review',
      notes: 'Add repair and replacement cost to generate a recommendation.',
    };
  }

  const ratio = repairCost / replacementCost;
  const ageYears = ageMonths > 0 ? ageMonths / 12 : 0;
  let label = 'Repair recommended';
  if (ratio >= 0.75 || ageYears >= 7) label = 'Replace recommended';
  else if (ratio >= 0.5 || ageYears >= 5) label = 'Manager review';

  const score = Math.round(ratio * 100);
  const ageText = ageMonths > 0 ? ` Asset age is ${ageYears.toFixed(1)} years.` : '';
  return {
    score,
    label,
    notes: `Repair is ${score}% of replacement cost.${ageText}`,
  };
}

function rowArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

const SERVICE_OPTIONAL_SCHEMA_COLUMNS = [
  'charge_type_id',
  'service_date',
  'taxable',
  'tax_category',
  'tax_region_code',
  'tax_sourcing_basis',
  'order_received_location_id',
  'tax_calculation_status',
  'tax_calculated_at',
  'tax_context',
  'completed_date',
  'completion_photo_waiver_reason',
  // 20260930224500: labor OT units and rate, DT units and notes.
  'ot_units',
  // 20261001003526: sections and Non-Printable lines.
  'section_id',
  'non_printable',
  'service_ticket_sections',
] as const;

function isMissingOptionalServiceColumn(error: PostgrestError | null | undefined): boolean {
  if (!error) return false;
  const text = [error.code, error.message, error.details, error.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const looksLikeSchemaGap = error.code === '42703'
    || error.code === 'PGRST204'
    || text.includes('does not exist')
    || text.includes('schema cache')
    || text.includes('could not find');
  return looksLikeSchemaGap && SERVICE_OPTIONAL_SCHEMA_COLUMNS.some(column => text.includes(column));
}

function withoutPayloadKeys<T extends Record<string, unknown>>(
  payload: T,
  keys: readonly string[],
): Partial<T> {
  const next: Record<string, unknown> = { ...payload };
  for (const key of keys) delete next[key];
  return next as Partial<T>;
}

const SERVICE_CATALOG_OPTIONAL_KEYS = ['charge_type_id', 'taxable', 'tax_category'] as const;
const SERVICE_LINE_SECTION_OPTIONAL_KEYS = ['section_id', 'non_printable'] as const;
const SERVICE_LINE_TAX_OPTIONAL_KEYS = ['tax_category', ...SERVICE_LINE_SECTION_OPTIONAL_KEYS] as const;
const SERVICE_LABOR_TAX_OPTIONAL_KEYS = ['taxable', 'tax_category'] as const;
const SERVICE_LABOR_LINE_OPTIONAL_KEYS = [
  ...SERVICE_LABOR_TAX_OPTIONAL_KEYS, 'ot_units', 'ot_rate', 'dt_units', 'notes', ...SERVICE_LINE_SECTION_OPTIONAL_KEYS,
] as const;
// Ticket columns added by migrations that may not have landed yet. A write that trips over one
// retries without them instead of failing the whole save.
const SERVICE_TICKET_OPTIONAL_WRITE_KEYS = [
  'tax_region_code',
  'tax_sourcing_basis',
  'order_received_location_id',
  'tax_calculation_status',
  'tax_calculated_at',
  'tax_context',
  'completed_date',
] as const;

function normalizeServiceChargeCatalogRow(row: ServiceChargeCatalogRow): ServiceChargeCatalogRow {
  return {
    ...row,
    charge_type_id: row.charge_type_id ?? null,
    taxable: row.taxable ?? true,
    tax_category: row.tax_category ?? (row.category === 'travel' ? 'freight' : row.category === 'diagnostic' ? 'labor' : 'service'),
  };
}

function normalizeServiceLaborCatalogRow(row: ServiceLaborCatalogRow): ServiceLaborCatalogRow {
  return {
    ...row,
    charge_type_id: row.charge_type_id ?? null,
    taxable: row.taxable ?? true,
    tax_category: row.tax_category ?? 'labor',
  };
}

function normalizeServiceTicketForSchema(row: ServiceTicketRow): ServiceTicketRow {
  return {
    ...row,
    tax_context: row.tax_context ?? {},
    completed_date: row.completed_date ?? null,
    service_ticket_line_items: rowArray(row.service_ticket_line_items).map(line => ({
      ...line,
      taxable: line.taxable ?? true,
      tax_category: line.tax_category ?? (line.line_type === 'charge' && line.charge?.category === 'travel' ? 'freight' : 'service'),
      section_id: line.section_id ?? null,
      non_printable: line.non_printable ?? false,
      charge: line.charge ? normalizeServiceChargeCatalogRow(line.charge) : line.charge,
    })),
    service_ticket_sections: rowArray(row.service_ticket_sections),
    service_ticket_labor_lines: rowArray(row.service_ticket_labor_lines).map(line => ({
      ...line,
      service_date: line.service_date ?? null,
      section_id: line.section_id ?? null,
      non_printable: line.non_printable ?? false,
      ot_units: line.ot_units ?? 0,
      ot_rate: line.ot_rate ?? 0,
      dt_units: line.dt_units ?? 0,
      notes: line.notes ?? null,
      crew_quantity: serviceLaborCrew(line),
      taxable: line.taxable ?? true,
      tax_category: line.tax_category ?? 'labor',
      labor: line.labor ? normalizeServiceLaborCatalogRow(line.labor) : line.labor,
    })),
  };
}

export const SERVICE_TICKET_SIGNATURE_TARGET_TYPE = 'service_ticket';
export const SERVICE_TICKET_ACCEPTANCE_PURPOSE = 'external_service_ticket_acceptance';
export const SERVICE_TICKET_ACCEPTANCE_STATEMENT =
  'I am authorized to approve this service work order. I agree to the applicable ITA Terms & Conditions and understand that my electronic signature will be stored with the work order details, charges, labor, totals, and service notes shown at the time of signing.';
export const SERVICE_TICKET_ESTIMATE_ACCEPTANCE_STATEMENT =
  'I am authorized to approve this service work order estimate. I accept the estimate shown at the time of signing, agree to the applicable ITA Terms & Conditions, and understand that my electronic signature will be stored with the work order details, charges, labor, estimated total, and service notes shown at the time of signing.';

export function getServiceTicketAcceptanceStatement(ticket: Pick<ServiceTicketRow, 'status'>): string {
  return ticket.status === 'open'
    ? SERVICE_TICKET_ESTIMATE_ACCEPTANCE_STATEMENT
    : SERVICE_TICKET_ACCEPTANCE_STATEMENT;
}

function serviceTicketDisplayLabel(ticket: Pick<ServiceTicketRow, 'service_ticket_id' | 'doc_no'>): string {
  return ticket.doc_no || ticket.service_ticket_id.slice(0, 8);
}

function serviceLineExtendedAmount(row: ServiceTicketLineItemRow): number {
  const qty = Number(row.quantity || 0);
  const unit = Number(row.unit_price || 0);
  const discount = Math.max(0, Math.min(100, Number(row.discount_percent || 0)));
  return qty * unit * (1 - discount / 100);
}

/** People a labor row prices (at least one). Its twin is laborCrew in server/src/routes/serviceTickets.js. */
export function serviceLaborCrew(row: Pick<ServiceTicketLaborLineRow, 'crew_quantity'>): number {
  return Math.max(1, Math.round(Number(row.crew_quantity ?? 1)) || 1);
}

const SERVICE_LABOR_UOM_LABELS = { hourly: 'Hourly', half_day: 'Half day', full_day: 'Full day' } as const;

/** The display name of a labor unit of measure. */
export function serviceLaborUomLabel(uom: string | null | undefined): string {
  return uom === 'half_day' || uom === 'full_day' ? SERVICE_LABOR_UOM_LABELS[uom] : SERVICE_LABOR_UOM_LABELS.hourly;
}

/** A labor row's regular-time (RT), overtime (OT) and double-time (DT) units per person. */
export function serviceLaborUnits(row: Pick<ServiceTicketLaborLineRow, 'hours' | 'ot_units' | 'dt_units'>): { rt: number; ot: number; dt: number } {
  return { rt: Number(row.hours || 0), ot: Number(row.ot_units || 0), dt: Number(row.dt_units || 0) };
}

/** Double time is always twice the regular-time rate (Alex, 2026-09-30); it is never stored. */
export function serviceLaborDtRate(row: Pick<ServiceTicketLaborLineRow, 'hourly_rate'>): number {
  return 2 * Number(row.hourly_rate || 0);
}

/**
 * A labor row's amount, as service_ticket_priced_lines bills it: crew x (RT units x RT rate +
 * OT units x OT rate + DT units x twice the RT rate), or 0 when it is not billable. Its twin is
 * laborExtendedAmount in server/src/routes/serviceTickets.js.
 */
export function serviceLaborExtendedAmount(
  row: Pick<ServiceTicketLaborLineRow, 'billable' | 'hours' | 'hourly_rate' | 'ot_units' | 'ot_rate' | 'dt_units' | 'crew_quantity'>,
): number {
  if (!row.billable) return 0;
  const units = serviceLaborUnits(row);
  return serviceLaborCrew(row) * (
    units.rt * Number(row.hourly_rate || 0)
    + units.ot * Number(row.ot_rate || 0)
    + units.dt * serviceLaborDtRate(row)
  );
}

function projectServiceTicketRows(ticket: ServiceTicketRow) {
  return projectServiceWorkOrder(
    {
      lines: rowArray(ticket.service_ticket_line_items),
      labor: rowArray(ticket.service_ticket_labor_lines),
      sections: rowArray(ticket.service_ticket_sections),
    },
    {
      lineAmount: serviceLineExtendedAmount,
      laborAmount: (row: ServiceTicketLaborLineRow) => row.extended_amount ?? serviceLaborExtendedAmount(row),
    },
  );
}

/**
 * The work order as the client sees it (shared/serviceWorkOrderProjection.js): Non-Printable lines
 * left out but counted in every subtotal, lines grouped by section. The client's /w/:token JSON
 * arrives with it built by the server; the staff preview builds it here from the full rows.
 */
export function serviceTicketWorkOrder(
  ticket: ServiceTicketRow,
): ServiceTicketWorkOrder {
  return ticket.work_order ?? projectServiceTicketRows(ticket);
}

export function buildServiceTicketSignedPayload(ticket: ServiceTicketRow): Record<string, unknown> {
  // The client signs what they see: Non-Printable lines stay out of the lists, while the totals
  // below still include them.
  const workOrder = projectServiceTicketRows(ticket);
  return {
    ticket: {
      service_ticket_id: ticket.service_ticket_id,
      doc_no: ticket.doc_no,
      ticket_type: ticket.ticket_type ?? null,
      status: ticket.status,
      priority: ticket.priority,
      summary: ticket.summary,
      problem_reported: ticket.description,
      service_performed: ticket.service_performed ?? null,
      requested_service_date: ticket.requested_service_date ?? null,
    scheduled_start_at: ticket.scheduled_start_at ?? null,
    invoice_date: ticket.invoice_date ?? null,
    completed_date: ticket.completed_date ?? null,
      client_visible: ticket.client_visible ?? null,
    },
    customer: {
      client_id: ticket.client_id ?? null,
      client_name: ticket.client?.business_name ?? ticket.bill_to_name ?? null,
      client_branch_id: ticket.client_branch_id ?? null,
      service_agreement_tier_id: ticket.service_agreement_tier_id ?? null,
      service_agreement_tier_name: ticket.service_agreement_tier?.name ?? null,
      service_contact_name: ticket.service_contact_name ?? null,
      service_contact_email: ticket.service_contact_email ?? null,
      service_contact_phone: ticket.service_contact_phone ?? null,
    },
    service_location: {
      name: ticket.service_location_name ?? null,
      room: ticket.service_room ?? null,
      address_line1: ticket.service_address_line1 ?? null,
      address_line2: ticket.service_address_line2 ?? null,
      city: ticket.service_city ?? null,
      state: ticket.service_state ?? null,
      postal_code: ticket.service_postal_code ?? null,
      country: ticket.service_country ?? null,
    },
    billing: {
      name: ticket.bill_to_name ?? null,
      attention: ticket.bill_to_attn ?? null,
      email: ticket.bill_to_email ?? null,
      phone: ticket.bill_to_phone ?? null,
      payment_terms: ticket.payment_terms ?? null,
      address_line1: ticket.bill_to_address_line1 ?? null,
      address_line2: ticket.bill_to_address_line2 ?? null,
      city: ticket.bill_to_city ?? null,
      state: ticket.bill_to_state ?? null,
      postal_code: ticket.bill_to_postal_code ?? null,
      country: ticket.bill_to_country ?? null,
    },
    charges: serviceWorkOrderLines(workOrder.charges).map(({ row, section }) => ({
      line_item_id: row.line_item_id,
      line_type: row.line_type,
      charge_id: row.charge_id,
      item_id: row.item_id,
      description: row.description || row.charge?.label || row.item?.name_internal || null,
      section,
      quantity: Number(row.quantity || 0),
      unit_price: Number(row.unit_price || 0),
      discount_percent: Number(row.discount_percent || 0),
      taxable: row.taxable,
      extended_amount: serviceLineExtendedAmount(row),
    })),
    labor: serviceWorkOrderLines(workOrder.labor).map(({ row, section }) => ({
      labor_line_id: row.labor_line_id,
      labor_id: row.labor_id,
      role_name: row.role_name || row.labor?.name || null,
      description: row.description,
      section,
      uom: row.uom ?? row.labor?.uom ?? 'hourly',
      hours: Number(row.hours || 0),
      ot_units: Number(row.ot_units || 0),
      dt_units: Number(row.dt_units || 0),
      crew_quantity: serviceLaborCrew(row),
      hourly_rate: Number(row.hourly_rate || 0),
      ot_rate: Number(row.ot_rate || 0),
      dt_rate: serviceLaborDtRate(row),
      billable: row.billable,
      extended_amount: serviceLaborExtendedAmount(row),
    })),
    sections: serviceWorkOrderSections(workOrder),
    totals: {
      amount_label: ticket.status === 'open' ? 'estimate' : 'total_due',
      subtotal_amount: Number(ticket.subtotal_amount || 0),
      sa_discount_percent: Number(ticket.sa_discount_percent || 0),
      tax_percent: Number(ticket.tax_percent || 0),
      tax_amount: Number(ticket.tax_amount || 0),
      total_amount: Number(ticket.total_amount || 0),
    },
  };
}

export function getServiceTicketSignatureLabel(record: SignatureRecord | null | undefined): string | null {
  if (!record) return null;
  const typedName = record.signature_data?.typed_name;
  if (typeof typedName === 'string' && typedName.trim()) {
    return `Typed signature: ${typedName.trim()}`;
  }
  if (record.signature_method === 'e_signature' || typeof record.signature_data?.drawn_signature === 'string') {
    return 'Drawn signature captured';
  }
  return null;
}

export function getServiceTicketSignatureImageDataUrl(record: SignatureRecord | null | undefined): string | null {
  if (!record) return null;
  const imageDataUrl = record.signature_image_data_url ?? record.signature_data?.drawn_signature;
  return typeof imageDataUrl === 'string' && imageDataUrl.startsWith('data:image/') ? imageDataUrl : null;
}

export async function fetchServiceTicketSignatureRecords(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: SignatureRecord[]; error: PostgrestError | null }> {
  const result = await listSignatureRecordsForTarget({
    type: SERVICE_TICKET_SIGNATURE_TARGET_TYPE,
    id: ticketId,
  }, options);
  // Client acceptance only, as the server's already-signed check reads it. Any other signature
  // on the ticket must not show as the client's or hide the hand-off actions.
  return {
    data: result.data.filter((record) => record.status === 'active' && !record.deleted_at
      && record.signature_purpose === SERVICE_TICKET_ACCEPTANCE_PURPOSE),
    error: result.error,
  };
}

/**
 * Extra audit facts merged into the signature record's metadata â€” used to
 * distinguish an in-person (kiosk) signature from a remote one, and to record
 * which staff account presented the device. Declared here rather than in a
 * `.tsx` because esbuild mis-parses `Record<...>` generics in JSX files.
 */
export type ServiceSignatureMetadata = Record<string, unknown>;

export async function createExternalServiceTicketSignatureRecord(
  params: {
    ticket: ServiceTicketRow;
    signature: SignatureModalValue;
    signerProfileId?: string | null;
    signerEmail?: string | null;
    signerName?: string | null;
    actorUserId?: string | null;
    source?: string;
    extraMetadata?: ServiceSignatureMetadata | null;
    /** True for a customer signing in person; keeps signer_profile_id null. */
    signerIsExternal?: boolean;
    termsAndConditions?: Pick<TermsAndConditions, 'tac_id' | 'name' | 'content'> | null;
  } & ClientOption,
): Promise<{ data: string | null; error: PostgrestError | Error | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  try {
    const ticket = params.ticket;
    const label = serviceTicketDisplayLabel(ticket);
    const signerName = params.signerName ?? params.signature.signerName ?? ticket.service_contact_name ?? ticket.bill_to_attn ?? null;
    const terms = params.termsAndConditions ?? null;
    const baseAcceptanceStatement = getServiceTicketAcceptanceStatement(ticket);
    const acceptanceStatement = terms
      ? `${baseAcceptanceStatement} Terms accepted: ${terms.name}.`
      : baseAcceptanceStatement;
    const signatureData = {
      ...params.signature.signatureData,
      reviewed_snapshot_hash: params.signature.signatureData.reviewed_snapshot_hash ?? ticket.customer_view_source_hash,
      accepted_terms: Boolean(terms),
      terms_and_conditions_id: terms?.tac_id ?? null,
      terms_and_conditions_name: terms?.name ?? null,
    };
    const signature = {
      ...params.signature,
      signatureData,
    };
    const recordId = await createSignatureRecord({
      target: {
        type: SERVICE_TICKET_SIGNATURE_TARGET_TYPE,
        id: ticket.service_ticket_id,
        label,
      },
      purpose: SERVICE_TICKET_ACCEPTANCE_PURPOSE,
      acceptanceStatement,
      signature,
      signedPayload: {
        ...buildServiceTicketSignedPayload(ticket),
        terms_and_conditions: terms
          ? {
              tac_id: terms.tac_id,
              name: terms.name,
              content: terms.content,
            }
          : null,
      },
      signerProfileId: params.signerProfileId ?? null,
      signerIsExternal: params.signerIsExternal === true,
      signerEmail: params.signerEmail ?? ticket.service_contact_email ?? ticket.bill_to_email ?? null,
      signerName,
      source: params.source ?? 'service_ticket',
      related: {
        table: 'service_tickets',
        id: ticket.service_ticket_id,
      },
      metadata: {
        doc_no: ticket.doc_no ?? null,
        ticket_status: ticket.status,
        client_id: ticket.client_id ?? null,
        client_branch_id: ticket.client_branch_id ?? null,
        service_agreement_tier_id: ticket.service_agreement_tier_id ?? null,
        terms_and_conditions_id: terms?.tac_id ?? null,
        terms_and_conditions_name: terms?.name ?? null,
        ...(params.extraMetadata ?? {}),
      },
    }, { supabase });

    const actionResult = await addServiceAction({
      ticketId: ticket.service_ticket_id,
      action: `Service work order signed${signerName ? ` by ${signerName}` : ''}`,
      actionBy: params.actorUserId ?? null,
      details: {
        signature_record_id: recordId,
        signature_method: signature.method,
        signature_purpose: SERVICE_TICKET_ACCEPTANCE_PURPOSE,
        terms_and_conditions_id: terms?.tac_id ?? null,
        terms_and_conditions_name: terms?.name ?? null,
      },
      supabase,
    });
    if (actionResult.error) {
      console.warn('Service signature action log failed:', actionResult.error.message);
    }

    return { data: recordId, error: null };
  } catch (error) {
    // Acceptance completes the ticket in the same transaction, so the completion guard can refuse it.
    return { data: null, error: withServiceTicketCompletionCopy(error as PostgrestError | Error) };
  }
}

function maybeNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function monthsSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  return Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
}

function replacementCostForAsset(asset: ServiceAssetRow | null | undefined): number | null {
  const item = firstRelated(asset?.items);
  const candidates = [
    item?.replacement_cost,
    asset?.purchase_cost,
    item?.purchase_cost_last,
    item?.purchase_cost_avg,
  ];
  for (const value of candidates) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return num;
  }
  return null;
}

type ServiceTicketOpenerProfileRow = {
  id?: string | null;
  auth_user_id?: string | null;
  full_name?: string | null;
  email?: string | null;
};

function isUuidText(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

async function hydrateServiceTicketOpeners(
  supabase: SupabaseClient,
  rows: ServiceTicketRow[],
): Promise<ServiceTicketRow[]> {
  const tickets = rows.map((ticket) => (ticket.source === 'workflow_engine' && !ticket.opened_by_profile
    ? { ...ticket, opened_by_profile: { full_name: WORKFLOW_ENGINE_LABEL, email: null } }
    : ticket));
  const openerIds = Array.from(new Set(
    tickets
      .map((ticket) => ticket.opened_by)
      .filter((id): id is string => typeof id === 'string' && Boolean(id) && isUuidText(id)),
  ));
  if (openerIds.length === 0) return tickets;

  const profilesByOpenerId = new Map<string, ServiceTicketOpenerProfileRow>();
  const addProfiles = (rows: unknown) => {
    if (!Array.isArray(rows)) return;
    for (const row of rows as ServiceTicketOpenerProfileRow[]) {
      if (row.auth_user_id) profilesByOpenerId.set(row.auth_user_id, row);
      if (row.id) profilesByOpenerId.set(row.id, row);
    }
  };

  const authRows = await runSupabaseQuery(
    () => supabase
      .from('user_profiles')
      .select('id,auth_user_id,full_name,email')
      .in('auth_user_id', openerIds),
    'Unable to load service ticket opener details.',
    'service.ticketOpeners.byAuthUser',
  );
  addProfiles(authRows);

  const missingProfileIds = openerIds.filter((id) => !profilesByOpenerId.has(id));
  if (missingProfileIds.length > 0) {
    const profileRows = await runSupabaseQuery(
      () => supabase
        .from('user_profiles')
        .select('id,auth_user_id,full_name,email')
        .in('id', missingProfileIds),
      'Unable to load service ticket opener details.',
      'service.ticketOpeners.byProfile',
    );
    addProfiles(profileRows);
  }

  return tickets.map((ticket) => {
    if (ticket.opened_by_profile || !ticket.opened_by) return ticket;
    const profile = profilesByOpenerId.get(ticket.opened_by);
    return profile
      ? { ...ticket, opened_by_profile: { full_name: profile.full_name ?? null, email: profile.email ?? null } }
      : ticket;
  });
}

export async function fetchServiceTickets(
  params: {
    locationId?: string | null;
    status?: ServiceTicketStatus | ServiceTicketStatus[] | null;
    priority?: ServiceTicketPriority | null;
    ticketType?: ServiceTicketType | 'all' | null;
    clientId?: string | null;
    search?: string | null;
    limit?: number;
    requireComplete?: boolean;
    signal?: AbortSignal;
  } & ClientOption = {},
): Promise<{ data: ServiceTicketRow[]; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const limit = Math.max(1, Math.min(params.limit ?? 1000, 10000));
  const rows: ServiceTicketRow[] = [];
  const seen = new Set<string>();
  let after: ServiceTicketRow | undefined;
  const pageArgs = (pageSize: number) => ({
      p_location_id: params.locationId ?? null,
      p_statuses: Array.isArray(params.status) ? params.status : params.status ? [params.status] : null,
      p_priority: params.priority ?? null,
      p_ticket_type: params.ticketType && params.ticketType !== 'all' ? params.ticketType : null,
      p_client_id: params.clientId ?? null,
      p_search: params.search?.replace(/[%(),]/g, ' ') ?? null,
      p_after_opened_at: after?.opened_at ?? null,
      p_after_id: after?.service_ticket_id ?? null,
      p_limit: pageSize,
  });
  while (rows.length < limit) {
    const pageSize = Math.min(250, limit - rows.length);
    let query = supabase.rpc('get_service_ticket_page' as any, pageArgs(pageSize)).select(TICKET_LIST_SELECT);
    if (params.signal) query = query.abortSignal(params.signal);
    const { data, error } = await query;
    if (error) return { data: [], error };
    const page = (Array.isArray(data) ? data : []) as unknown as ServiceTicketRow[];
    if (page.length) {
      // Authorize the bounded parent set once, instead of repeating ticket RLS
      // for every embedded child. Keep the list's existing ID-array contract.
      let childrenQuery = supabase.rpc('get_service_ticket_children' as any, {
        p_ticket_ids: page.map(row => row.service_ticket_id),
      });
      if (params.signal) childrenQuery = childrenQuery.abortSignal(params.signal);
      const children = await childrenQuery;
      if (children.error) return { data: [], error: children.error };
      const byId = new Map((Array.isArray(children.data) ? children.data : []).map(child => [child.service_ticket_id, child]));
      for (const row of page) {
        const child = byId.get(row.service_ticket_id);
        row.service_ticket_line_items = child?.service_ticket_line_items ?? [];
        row.service_ticket_labor_lines = child?.service_ticket_labor_lines ?? [];
        row.service_ticket_assets = child?.service_ticket_assets ?? [];
      }
    }
    for (const row of page) {
      if (!row.service_ticket_id || seen.has(row.service_ticket_id)) throw new Error('Service pagination did not advance.');
      seen.add(row.service_ticket_id);
      rows.push(row);
    }
    if (page.length < pageSize) break;
    after = page[page.length - 1];
  }
  if (params.requireComplete && rows.length === limit && after) {
    let probe = supabase.rpc('get_service_ticket_page' as any, pageArgs(1)).select('service_ticket_id');
    if (params.signal) probe = probe.abortSignal(params.signal);
    const { data, error } = await probe;
    if (error) return { data: [], error };
    if (Array.isArray(data) && data.length) return { data: [], error: {
      code: 'service_scope_too_large', message: 'There are too many service tickets in this view. Narrow the location, status, or search and try again.', details: '', hint: '',
    } as PostgrestError };
  }
  return { data: await hydrateServiceTicketOpeners(supabase, rows), error: null };
}

export async function fetchServiceTicketById(
  ticketId: string,
  options?: ClientOption & { signal?: AbortSignal },
): Promise<{ data: ServiceTicketRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  let query = supabase
    .from('service_tickets')
    .select(TICKET_DETAIL_SELECT)
    .eq('service_ticket_id', ticketId);
  if (options?.signal) query = query.abortSignal(options.signal);
  let { data, error } = await query.maybeSingle();
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await supabase
      .from('service_tickets')
      .select(TICKET_DETAIL_SELECT_LEGACY)
      .eq('service_ticket_id', ticketId)
      .maybeSingle();
    data = fallback.data;
    error = fallback.error;
  }
  if (error || !data) return { data: null, error: error ?? null };
  const [ticket] = await hydrateServiceTicketOpeners(supabase, [normalizeServiceTicketForSchema(data as unknown as ServiceTicketRow)]);
  return { data: ticket ?? null, error: null };
}

/** A ticket's type ('asset_repair' or 'external') and stage, for screens that know only its id. */
export async function fetchServiceTicketTypeAndStatus(ticketId: string): Promise<{ ticket_type: string | null; status: string | null } | null> {
  const { data, error } = await getSupabaseClient()
    .from('service_tickets')
    .select('ticket_type, status')
    .eq('service_ticket_id', ticketId)
    .maybeSingle();
  if (error) throw error;
  return (data as { ticket_type: string | null; status: string | null } | null) ?? null;
}

export async function fetchServiceTicketPortalById(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: ServiceTicketRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_service_customer_view', { p_ticket_id: ticketId });
  if (error || !data) return { data: null, error };
  const saved = data as { snapshot?: { ticket?: Record<string, unknown>; terms_and_conditions?: ServiceTicketRow['customer_view_terms'] }; signed_payload?: Record<string, unknown>; source_hash?: string; published?: boolean; signed?: boolean; unpublished_changes?: boolean };
  const ticket = saved.snapshot?.ticket ?? (saved.signed_payload ? serviceTicketFromSignedPayload(saved.signed_payload) : null);
  return { data: ticket ? { ...ticket, customer_view_source_hash: saved.source_hash, customer_view_terms: saved.snapshot?.terms_and_conditions ?? saved.signed_payload?.terms_and_conditions ?? null, customer_view_published: saved.published || saved.signed, customer_view_unpublished: saved.unpublished_changes } as ServiceTicketRow : null, error: null };
}

export async function fetchServiceTicketCounts(
  options?: { ticketType?: ServiceTicketType | 'all' | null } & ClientOption,
): Promise<{ data: Record<ServiceTicketStatus, number>; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const statuses: ServiceTicketStatus[] = ['open', 'in_progress', 'waiting_parts', 'awaiting_return', 'closed', 'cancelled'];
  const { data, error } = await supabase.rpc('get_service_ticket_counts' as any, {
    p_ticket_type: options?.ticketType && options.ticketType !== 'all' ? options.ticketType : null,
  });
  const counts = Object.fromEntries(statuses.map(status => [status, 0])) as Record<ServiceTicketStatus, number>;
  if (error) return { data: counts, error };
  const invalid = { code: 'invalid_service_counts', message: 'Unable to load service ticket counts.', details: '', hint: '' } as PostgrestError;
  if (!Array.isArray(data)) return { data: counts, error: invalid };
  const seen = new Set<ServiceTicketStatus>();
  for (const row of data as Array<{ status: ServiceTicketStatus; ticket_count: number }>) {
    const count = Number(row.ticket_count);
    if (!statuses.includes(row.status) || seen.has(row.status) || row.ticket_count == null || !Number.isSafeInteger(count) || count < 0) {
      return { data: counts, error: invalid };
    }
    seen.add(row.status);
    counts[row.status] = count;
  }
  return { data: counts, error: null };
}

export async function fetchServiceActions(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: ServiceActionRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_actions')
    .select('service_action_id, service_ticket_id, action_at, action_by, action, details')
    .eq('service_ticket_id', ticketId)
    .order('action_at', { ascending: false });
  if (error) return { data: [], error };

  const actions = Array.isArray(data) ? (data as unknown as ServiceActionRow[]) : [];
  const actorIds = Array.from(new Set(actions.map((row) => row.action_by).filter((id): id is string => Boolean(id))));
  if (!actorIds.length) return { data: actions, error: null };

  const profilesByProfileId = await runSupabaseQuery(
    () => supabase
      .from('user_profiles')
      .select('id, auth_user_id, full_name, email')
      .in('id', actorIds),
    'Unable to load service ticket activity authors.',
    'service.ticketActions.actorsByProfile',
  );
  const profileRows = Array.isArray(profilesByProfileId)
    ? profilesByProfileId as Array<{ id?: unknown; auth_user_id?: unknown; full_name?: string | null; email?: string | null }>
    : [];
  const foundActorIds = new Set<string>();
  profileRows.forEach((row) => {
    if (row.id) foundActorIds.add(String(row.id));
    if (row.auth_user_id) foundActorIds.add(String(row.auth_user_id));
  });
  const missingActorIds = actorIds.filter((id) => !foundActorIds.has(id));
  if (missingActorIds.length) {
    const profilesByAuthId = await runSupabaseQuery(
      () => supabase
        .from('user_profiles')
        .select('id, auth_user_id, full_name, email')
        .in('auth_user_id', missingActorIds),
      'Unable to load service ticket activity authors.',
      'service.ticketActions.actorsByAuthUser',
    );
    if (Array.isArray(profilesByAuthId)) {
      profileRows.push(...profilesByAuthId as Array<{ id?: unknown; auth_user_id?: unknown; full_name?: string | null; email?: string | null }>);
    }
  }

  const profilesById = new Map<string, { full_name: string | null; email: string | null }>();
  profileRows.forEach((row) => {
    const profile = { full_name: row.full_name ?? null, email: row.email ?? null };
    if (row.id) profilesById.set(String(row.id), profile);
    if (row.auth_user_id) profilesById.set(String(row.auth_user_id), profile);
  });

  return {
    data: actions.map((row) => ({
      ...row,
      action_by_profile: row.action_by ? profilesById.get(row.action_by) ?? null : null,
    })),
    error: null,
  };
}

export async function fetchServiceParts(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: ServicePartRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_parts')
    .select('service_part_id, service_ticket_id, description, vendor_id, qty, unit_cost, po_line_id, status, vendor:vendors!left(name)')
    .eq('service_ticket_id', ticketId)
    .order('created_at', { ascending: false });
  return { data: Array.isArray(data) ? (data as unknown as ServicePartRow[]) : [], error: error ?? null };
}

export async function fetchServiceSettings(options?: ClientOption): Promise<{ data: ServiceSettings; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('app_settings')
    .select(`
      service_location_id,
      default_replacement_source_location_id,
      service_location:locations!app_settings_service_location_id_fkey(name),
      replacement_source_location:locations!app_settings_default_replacement_source_location_id_fkey(name)
    `)
    .eq('id', 1)
    .maybeSingle();

  if (error) {
    return {
      data: {
        serviceLocationId: null,
        serviceLocationName: null,
        replacementSourceLocationId: null,
        replacementSourceLocationName: null,
      },
      error,
    };
  }

  const row = (data ?? {}) as any;
  return {
    data: {
      serviceLocationId: row.service_location_id ?? null,
      serviceLocationName: firstRelated(row.service_location)?.name ?? null,
      replacementSourceLocationId: row.default_replacement_source_location_id ?? null,
      replacementSourceLocationName: firstRelated(row.replacement_source_location)?.name ?? null,
    },
    error: null,
  };
}

type ServiceSettingsQueryData =
  | { data: ServiceSettings; error: PostgrestError | null }
  | ServiceSettings
  | null
  | undefined;

export function serviceSettingsFromQueryData(value: ServiceSettingsQueryData): ServiceSettings | null {
  if (!value) return null;
  if ('serviceLocationId' in value) return value;
  return value.data ?? null;
}

export async function syncDueServicePmDashboardTasks(options?: ClientOption): Promise<{ data: number; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('sync_due_service_pm_dashboard_tasks');
  return { data: typeof data === 'number' ? data : 0, error: error ?? null };
}

export async function fetchServiceAgreementTiers(
  options?: ClientOption,
): Promise<{ data: ServiceAgreementTierRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_agreement_tiers')
    .select(SERVICE_TIER_SELECT)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  return { data: rowArray(data as unknown as ServiceAgreementTierRow[]), error: error ?? null };
}

export async function saveServiceAgreementTier(
  values: Partial<ServiceAgreementTierRow> & {
    tier_id?: string | null;
    code: string;
    name: string;
  },
  options?: ClientOption,
): Promise<{ data: ServiceAgreementTierRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    code: normalizeText(values.code),
    name: normalizeText(values.name),
    description: values.description ?? null,
    price: Number(values.price ?? 0),
    term_months: Math.max(1, Number(values.term_months ?? 12)),
    pm_visit_count: Math.max(0, Number(values.pm_visit_count ?? 0)),
    pm_frequency_months: maybeNumber(values.pm_frequency_months),
    parts_labor_discount_percent: Math.max(0, Math.min(100, Number(values.parts_labor_discount_percent ?? 0))),
    response_level: values.response_level ?? null,
    onsite_support: values.onsite_support ?? null,
    manufacturer_warranty: values.manufacturer_warranty ?? null,
    loaner_equipment: values.loaner_equipment ?? null,
    effective_start_date: values.effective_start_date ?? new Date().toISOString().slice(0, 10),
    effective_end_date: values.effective_end_date ?? null,
    active: values.active ?? true,
    sort_order: Number(values.sort_order ?? 0),
    updated_at: new Date().toISOString(),
  };
  const query = values.tier_id
    ? supabase.from('service_agreement_tiers').update(payload).eq('tier_id', values.tier_id)
    : supabase.from('service_agreement_tiers').insert(payload);
  const { data, error } = await query.select(SERVICE_TIER_SELECT).maybeSingle();
  return { data: (data as unknown as ServiceAgreementTierRow) ?? null, error: error ?? null };
}

export async function fetchServiceChargeCatalog(
  options?: ClientOption,
): Promise<{ data: ServiceChargeCatalogRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  let { data, error } = await supabase
    .from('service_charge_catalog')
    .select(SERVICE_CHARGE_SELECT)
    .order('sort_order', { ascending: true })
    .order('label', { ascending: true });
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await supabase
      .from('service_charge_catalog')
      .select(SERVICE_CHARGE_SELECT_LEGACY)
      .order('sort_order', { ascending: true })
      .order('label', { ascending: true });
    data = fallback.data;
    error = fallback.error;
  }
  return {
    data: rowArray(data as unknown as ServiceChargeCatalogRow[]).map(normalizeServiceChargeCatalogRow),
    error: error ?? null,
  };
}

export async function saveServiceCharge(
  values: Partial<ServiceChargeCatalogRow> & {
    charge_id?: string | null;
    code: string;
    label: string;
  },
  options?: ClientOption,
): Promise<{ data: ServiceChargeCatalogRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    code: normalizeText(values.code),
    label: normalizeText(values.label),
    category: values.category ?? 'additional',
    charge_type_id: values.charge_type_id ?? null,
    taxable: values.taxable ?? true,
    tax_category: values.tax_category ?? (values.category === 'travel' ? 'freight' : values.category === 'diagnostic' ? 'labor' : 'service'),
    amount: Number(values.amount ?? 0),
    unit: values.unit ?? 'flat',
    radius_miles: maybeNumber(values.radius_miles),
    after_hours: Boolean(values.after_hours),
    included_labor_hours: Number(values.included_labor_hours ?? 0),
    effective_start_date: values.effective_start_date ?? new Date().toISOString().slice(0, 10),
    effective_end_date: values.effective_end_date ?? null,
    active: values.active ?? true,
    sort_order: Number(values.sort_order ?? 0),
    updated_at: new Date().toISOString(),
  };
  const run = (nextPayload: Partial<typeof payload>, select: string) => {
    const query = values.charge_id
      ? supabase.from('service_charge_catalog').update(nextPayload).eq('charge_id', values.charge_id)
      : supabase.from('service_charge_catalog').insert(nextPayload);
    return query.select(select).maybeSingle();
  };
  let { data, error } = await run(payload, SERVICE_CHARGE_SELECT);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await run(withoutPayloadKeys(payload, SERVICE_CATALOG_OPTIONAL_KEYS), SERVICE_CHARGE_SELECT_LEGACY);
    data = fallback.data;
    error = fallback.error;
  }
  return {
    data: data ? normalizeServiceChargeCatalogRow(data as unknown as ServiceChargeCatalogRow) : null,
    error: error ?? null,
  };
}

export async function fetchServiceLaborCatalog(
  options?: ClientOption,
): Promise<{ data: ServiceLaborCatalogRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  let { data, error } = await supabase
    .from('service_labor_catalog')
    .select(SERVICE_LABOR_SELECT)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await supabase
      .from('service_labor_catalog')
      .select(SERVICE_LABOR_SELECT_LEGACY)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    data = fallback.data;
    error = fallback.error;
  }
  return {
    data: rowArray(data as unknown as ServiceLaborCatalogRow[]).map(normalizeServiceLaborCatalogRow),
    error: error ?? null,
  };
}

export async function saveServiceLabor(
  values: Partial<ServiceLaborCatalogRow> & {
    labor_id?: string | null;
    code: string;
    name: string;
  },
  options?: ClientOption,
): Promise<{ data: ServiceLaborCatalogRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    code: normalizeText(values.code),
    name: normalizeText(values.name),
    charge_type_id: values.charge_type_id ?? null,
    taxable: values.taxable ?? true,
    tax_category: values.tax_category ?? 'labor',
    hourly_rate: Number(values.hourly_rate ?? 0),
    uom: values.uom ?? 'hourly',
    after_hours_rate: maybeNumber(values.after_hours_rate),
    effective_start_date: values.effective_start_date ?? new Date().toISOString().slice(0, 10),
    effective_end_date: values.effective_end_date ?? null,
    active: values.active ?? true,
    sort_order: Number(values.sort_order ?? 0),
    updated_at: new Date().toISOString(),
  };
  const run = (nextPayload: Partial<typeof payload>, select: string) => {
    const query = values.labor_id
      ? supabase.from('service_labor_catalog').update(nextPayload).eq('labor_id', values.labor_id)
      : supabase.from('service_labor_catalog').insert(nextPayload);
    return query.select(select).maybeSingle();
  };
  let { data, error } = await run(payload, SERVICE_LABOR_SELECT);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await run(withoutPayloadKeys(payload, SERVICE_CATALOG_OPTIONAL_KEYS), SERVICE_LABOR_SELECT_LEGACY);
    data = fallback.data;
    error = fallback.error;
  }
  return {
    data: data ? normalizeServiceLaborCatalogRow(data as unknown as ServiceLaborCatalogRow) : null,
    error: error ?? null,
  };
}

export async function fetchServiceTaskTemplates(
  options?: ClientOption,
): Promise<{ data: ServiceTaskTemplateRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_task_templates')
    .select(SERVICE_TASK_TEMPLATE_SELECT)
    .order('sort_order', { ascending: true })
    .order('title', { ascending: true });
  return { data: rowArray(data as unknown as ServiceTaskTemplateRow[]), error: error ?? null };
}

export async function saveServiceTaskTemplate(
  values: Partial<ServiceTaskTemplateRow> & {
    task_template_id?: string | null;
    title: string;
    actorUserId?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: ServiceTaskTemplateRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    code: normalizeText(values.code) || null,
    title: normalizeText(values.title),
    description: normalizeText(values.description) || null,
    applies_to: values.applies_to ?? 'both',
    add_by_default: Boolean(values.add_by_default),
    active: values.active ?? true,
    sort_order: Number(values.sort_order ?? 0),
    updated_by: values.actorUserId ?? null,
    updated_at: new Date().toISOString(),
  };
  const query = values.task_template_id
    ? supabase.from('service_task_templates').update(payload).eq('task_template_id', values.task_template_id)
    : supabase.from('service_task_templates').insert({ ...payload, created_by: values.actorUserId ?? null });
  const { data, error } = await query.select(SERVICE_TASK_TEMPLATE_SELECT).maybeSingle();
  return { data: (data as unknown as ServiceTaskTemplateRow) ?? null, error: error ?? null };
}

export async function seedServiceTicketDefaultTasks(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: number; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('seed_service_ticket_default_tasks', { p_service_ticket_id: ticketId });
  return { data: typeof data === 'number' ? data : 0, error: error ?? null };
}

export async function getServiceTicketChatConversation(
  ticketId: string,
  create: boolean,
  options?: ClientOption,
): Promise<{ data: ServiceTicketChatConversationRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_service_ticket_chat_conversation', {
    p_service_ticket_id: ticketId,
    p_create: create,
  });
  return {
    data: data ? (data as unknown as ServiceTicketChatConversationRow) : null,
    error: error ?? null,
  };
}

export async function saveServiceTicketTask(
  values: Partial<ServiceTicketTaskRow> & {
    task_id?: string | null;
    service_ticket_id: string;
    title: string;
    actorUserId?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: ServiceTicketTaskRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const status = values.status ?? 'open';
  const payload = {
    service_ticket_id: values.service_ticket_id,
    task_template_id: values.task_template_id ?? null,
    title: normalizeText(values.title),
    description: normalizeText(values.description) || null,
    status,
    assigned_profile_id: values.assigned_profile_id ?? null,
    priority: values.priority ?? 'normal',
    due_at: values.due_at ?? null,
    completed_at: status === 'completed' ? (values.completed_at ?? new Date().toISOString()) : null,
    completed_by_profile_id: status === 'completed' ? (values.completed_by_profile_id ?? null) : null,
    sort_order: Number(values.sort_order ?? 0),
    updated_by: values.actorUserId ?? null,
  };
  const query = values.task_id
    ? supabase.from('service_ticket_tasks').update(payload).eq('task_id', values.task_id)
    : supabase.from('service_ticket_tasks').insert({ ...payload, created_by: values.actorUserId ?? null });
  const { data, error } = await query.select(SERVICE_TICKET_TASK_SELECT).maybeSingle();
  return { data: (data as unknown as ServiceTicketTaskRow) ?? null, error: error ?? null };
}

export async function completeServiceTicketTask(
  taskId: string,
  values: {
    completed: boolean;
    completedByProfileId?: string | null;
    actorUserId?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: ServiceTicketTaskRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = values.completed
    ? {
        status: 'completed' as ServiceTicketTaskStatus,
        completed_at: new Date().toISOString(),
        completed_by_profile_id: values.completedByProfileId ?? null,
        updated_by: values.actorUserId ?? null,
      }
    : {
        status: 'open' as ServiceTicketTaskStatus,
        completed_at: null,
        completed_by_profile_id: null,
        updated_by: values.actorUserId ?? null,
      };
  const { data, error } = await supabase
    .from('service_ticket_tasks')
    .update(payload)
    .eq('task_id', taskId)
    .select(SERVICE_TICKET_TASK_SELECT)
    .maybeSingle();
  return { data: (data as unknown as ServiceTicketTaskRow) ?? null, error: error ?? null };
}

export async function cancelServiceTicketTask(
  taskId: string,
  values: { actorUserId?: string | null } = {},
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('service_ticket_tasks')
    .update({
      status: 'cancelled' as ServiceTicketTaskStatus,
      completed_at: null,
      completed_by_profile_id: null,
      updated_by: values.actorUserId ?? null,
    })
    .eq('task_id', taskId);
  return { error: error ?? null };
}

export async function fetchClientServiceAgreements(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ClientServiceAgreementRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_service_agreements')
    .select(`
      assignment_id, client_id, client_branch_id, tier_id, start_date, term_years, end_date,
      status, notes, created_at, updated_at, client_service_location_id, source_agreement_option_id, coverage_snapshot, accepted_signature_record_id, source_installation_job_id, warranty_start_date, warranty_end_date,
      tier:service_agreement_tiers!client_service_agreements_tier_id_fkey(${SERVICE_TIER_SELECT}),
      client_branch:client_branches!client_service_agreements_client_branch_id_fkey(
        id, branch_name, billing_address_line1, billing_address_line2, billing_city, billing_state,
        billing_postal_code, billing_country, shipping_address_line1, shipping_address_line2,
        shipping_city, shipping_state, shipping_postal_code, shipping_country, set_discount_percent
      )
    `)
    .eq('client_id', clientId)
    .order('start_date', { ascending: false });
  return { data: rowArray(data as unknown as ClientServiceAgreementRow[]), error: error ?? null };
}

function normalizeServiceDate(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}

export function resolveServiceTicketAgreementDate(values: {
  requestedServiceDate?: string | null;
  scheduledStartAt?: string | null;
  openedAt?: string | null;
  fallbackDate?: string | null;
}): string {
  return (
    normalizeServiceDate(values.requestedServiceDate)
    ?? normalizeServiceDate(values.scheduledStartAt)
    ?? normalizeServiceDate(values.openedAt)
    ?? normalizeServiceDate(values.fallbackDate)
    ?? new Date().toISOString().slice(0, 10)
  );
}

export function serviceAgreementCoversDate(
  agreement: Pick<ClientServiceAgreementRow, 'status' | 'start_date' | 'end_date'> | null | undefined,
  serviceDate: string,
): boolean {
  if (!agreement || agreement.status !== 'active') return false;
  return agreement.start_date <= serviceDate && agreement.end_date >= serviceDate;
}

function branchIdMatches(left: string | number | null | undefined, right: string | number | null | undefined): boolean {
  if (left == null || right == null) return left == null && right == null;
  return String(left) === String(right);
}

export function selectClientServiceAgreementForTicket(
  agreements: ClientServiceAgreementRow[],
  values: {
    serviceDate: string;
    clientBranchId?: string | number | null;
    clientServiceLocationId?: string | null;
    serviceAgreementAssignmentId?: string | null;
  },
): ClientServiceAgreementRow | null {
  const eligible = agreements.filter((agreement) => serviceAgreementCoversDate(agreement, values.serviceDate));
  if (values.serviceAgreementAssignmentId) {
    return eligible.find((agreement) => agreement.assignment_id === values.serviceAgreementAssignmentId) ?? null;
  }

  const branchScoped = eligible.filter((agreement) => {
    if (agreement.client_service_location_id) {
      if (values.clientServiceLocationId) return agreement.client_service_location_id === values.clientServiceLocationId;
      return agreement.client_branch_id != null && values.clientBranchId != null && branchIdMatches(agreement.client_branch_id, values.clientBranchId);
    }
    if (agreement.client_branch_id == null) return true;
    if (values.clientBranchId == null) return false;
    return branchIdMatches(agreement.client_branch_id, values.clientBranchId);
  });
  if (values.clientBranchId != null) {
    return (
      branchScoped.find((agreement) => branchIdMatches(agreement.client_branch_id, values.clientBranchId))
      ?? branchScoped.find((agreement) => agreement.client_branch_id == null)
      ?? null
    );
  }
  return branchScoped.find((agreement) => agreement.client_branch_id == null) ?? branchScoped[0] ?? null;
}

export const SERVICE_AGREEMENT_MIN_TERM_YEARS = 1;
export const SERVICE_AGREEMENT_MAX_TERM_YEARS = 25;

/**
 * Service agreements are sold in whole years. Anything fractional (legacy rows, hand-typed
 * values) is rounded to the nearest whole year and clamped to the supported range so PM visit
 * scheduling always works from a clean number of years.
 */
export function normalizeServiceAgreementTermYears(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return SERVICE_AGREEMENT_MIN_TERM_YEARS;
  const whole = Math.round(parsed);
  if (whole < SERVICE_AGREEMENT_MIN_TERM_YEARS) return SERVICE_AGREEMENT_MIN_TERM_YEARS;
  return Math.min(SERVICE_AGREEMENT_MAX_TERM_YEARS, whole);
}

export async function saveClientServiceAgreement(
  values: {
    assignmentId?: string | null;
    clientId: string;
    clientBranchId?: string | number | null;
    tierId: string;
    startDate: string;
    termYears: number;
    status?: ClientServiceAgreementStatus;
    notes?: string | null;
    actorUserId?: string | null;
  } & ClientOption,
): Promise<{ data: ClientServiceAgreementRow | null; error: PostgrestError | null }> {
  const supabase = values.supabase ?? getSupabaseClient();
  const payload = {
    client_id: values.clientId,
    client_branch_id: values.clientBranchId ? Number(values.clientBranchId) : null,
    tier_id: values.tierId,
    start_date: values.startDate,
    term_years: normalizeServiceAgreementTermYears(values.termYears),
    status: values.status ?? 'active',
    notes: values.notes ?? null,
    updated_by: values.actorUserId ?? null,
  };
  const query = values.assignmentId
    ? supabase.from('client_service_agreements').update(payload).eq('assignment_id', values.assignmentId)
    : supabase.from('client_service_agreements').insert({ ...payload, created_by: values.actorUserId ?? null });
  const { data, error } = await query
    .select(`
      assignment_id, client_id, client_branch_id, tier_id, start_date, term_years, end_date,
      status, notes, created_at, updated_at,
      tier:service_agreement_tiers!client_service_agreements_tier_id_fkey(${SERVICE_TIER_SELECT})
    `)
    .maybeSingle();
  return { data: (data as unknown as ClientServiceAgreementRow) ?? null, error: error ?? null };
}

export async function fetchClientServiceTickets(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ServiceTicketRow[]; error: PostgrestError | null }> {
  return fetchServiceTickets({ clientId, ticketType: 'external', limit: 50, supabase: options?.supabase });
}

export async function fetchClientServicePmVisits(
  clientId: string,
  options?: ClientOption,
): Promise<{ data: ServicePmVisitRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_pm_visits')
    .select(`
      pm_visit_id, agreement_assignment_id, client_id, client_branch_id, tier_id, sequence_no,
      due_date, reminder_date, dashboard_task_id, status, generated_ticket_id,
      completed_at, completed_by,
      tier:service_agreement_tiers!service_pm_visits_tier_id_fkey(${SERVICE_TIER_SELECT})
    `)
    .eq('client_id', clientId)
    .order('due_date', { ascending: true });
  return { data: rowArray(data as unknown as ServicePmVisitRow[]), error: error ?? null };
}

export async function updateServicePmVisitStatus(
  values: {
    pmVisitId: string;
    status: ServicePmVisitStatus;
    actorUserId?: string | null;
  } & ClientOption,
): Promise<{ data: ServicePmVisitRow | null; error: PostgrestError | null }> {
  const supabase = values.supabase ?? getSupabaseClient();
  const completing = values.status === 'completed';
  const { data, error } = await supabase
    .from('service_pm_visits')
    .update({
      status: values.status,
      completed_at: completing ? new Date().toISOString() : null,
      completed_by: completing ? values.actorUserId ?? null : null,
      updated_at: new Date().toISOString(),
    })
    .eq('pm_visit_id', values.pmVisitId)
    .select(`
      pm_visit_id, agreement_assignment_id, client_id, client_branch_id, tier_id, sequence_no,
      due_date, reminder_date, dashboard_task_id, status, generated_ticket_id,
      completed_at, completed_by,
      tier:service_agreement_tiers!service_pm_visits_tier_id_fkey(${SERVICE_TIER_SELECT})
    `)
    .maybeSingle();
  return { data: (data as unknown as ServicePmVisitRow) ?? null, error: error ?? null };
}

export async function fetchServiceAssetByIdentifier(
  identifier: string,
  options?: ClientOption,
): Promise<{ data: ServiceAssetRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const trimmed = normalizeText(identifier);
  if (!trimmed) return { data: null, error: null };
  let query = supabase.from('assets').select(SERVICE_ASSET_SELECT).limit(1);
  query = isUuidLike(trimmed) ? query.eq('asset_id', trimmed) : query.eq('barcode', trimmed);
  const { data, error } = await query.maybeSingle();
  return { data: (data as unknown as ServiceAssetRow) ?? null, error: error ?? null };
}

export async function fetchServicePurchaseOrders(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: Array<ServicePurchaseOrderRow & { lines: ServicePurchaseOrderLineRow[] }>; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('purchase_orders')
    .select(`
      po_id, doc_no, service_ticket_id, vendor_id, location_id, status,
      ordered_at, expected_delivery_at, notes, total_amount,
      vendor:vendors!left(name),
      lines:purchase_order_lines(po_line_id, po_id, description, item_id, qty, unit_cost, received_qty, status)
    `)
    .eq('service_ticket_id', ticketId)
    .order('created_at', { ascending: false });
  return {
    data: Array.isArray(data) ? (data as unknown as Array<ServicePurchaseOrderRow & { lines: ServicePurchaseOrderLineRow[] }>) : [],
    error: error ?? null,
  };
}

// The `closed` stage is labelled "Completed" in the UI, so status-change actions have been
// logged as "Status changed to Closed" historically and "Status changed to Completed" since.
// Service history reads both, otherwise Resolution/Date returned go blank on newer tickets.
const SERVICE_TICKET_COMPLETION_ACTION_TEXT = ['status changed to closed', 'status changed to completed'];

function findServiceTicketCompletionAction(actions: any[]): any | undefined {
  return actions.find((action) => {
    const text = String(action?.action || '').toLowerCase();
    return SERVICE_TICKET_COMPLETION_ACTION_TEXT.some((phrase) => text.includes(phrase));
  });
}

export async function fetchServiceHistoryByAsset(
  assetId: string,
  options?: ClientOption,
): Promise<{ data: AssetServiceHistoryRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data: ticketAssets, error: ticketAssetsError } = await supabase
    .from('service_ticket_assets')
    .select(
      `
      service_ticket_asset_id,
      service_ticket_id,
      issue_notes,
      status,
      outcome,
      findings_notes,
      work_performed_notes,
      recommendation_notes,
      completed_at,
      retired_at,
      ticket:service_tickets!inner(
        service_ticket_id,
        doc_no,
        opened_at,
        status,
        summary,
        description,
        location:locations!fk_st_loc(name)
      )
    `,
    )
    .eq('asset_id', assetId);

  const typedTicketAssets = Array.isArray(ticketAssets) ? (ticketAssets as any[]) : [];
  const ticketIds = Array.from(
    new Set(
      typedTicketAssets
        .map((row) => String(row?.ticket?.service_ticket_id || row?.service_ticket_id || ''))
        .filter(Boolean),
    ),
  );

  let actionsError: PostgrestError | null = null;
  let ticketActions: any[] = [];
  if (ticketIds.length > 0) {
    const { data, error } = await supabase
      .from('service_actions')
      .select('service_ticket_id, action_at, action')
      .in('service_ticket_id', ticketIds)
      .order('action_at', { ascending: false });
    ticketActions = Array.isArray(data) ? data : [];
    actionsError = error ?? null;
  }

  const actionsByTicket = new Map<string, any[]>();
  ticketActions.forEach((action) => {
    const key = String(action?.service_ticket_id || '');
    if (!key) return;
    const existing = actionsByTicket.get(key) ?? [];
    existing.push(action);
    actionsByTicket.set(key, existing);
  });

  const rows: AssetServiceHistoryRow[] = typedTicketAssets.map((row) => {
    const ticket = row?.ticket ?? null;
    const ticketId = String(ticket?.service_ticket_id || row?.service_ticket_id || '');
    const ticketStatus = (ticket?.status || 'open') as ServiceTicketStatus;
    const actions = actionsByTicket.get(ticketId) ?? [];
    const closeAction = findServiceTicketCompletionAction(actions);
    const issue = row?.issue_notes || ticket?.summary || ticket?.description || null;
    const structuredResolution =
      row?.work_performed_notes ||
      row?.findings_notes ||
      row?.recommendation_notes ||
      (row?.outcome ? String(row.outcome).replace(/_/g, ' ') : null);
    const resolution = structuredResolution || closeAction?.action || (ticketStatus === 'closed' ? 'Completed' : null);
    const dateReturned = row?.completed_at || row?.retired_at || closeAction?.action_at || null;
    const serviceTech = null;

    return {
      service_ticket_asset_id: String(row?.service_ticket_asset_id || ''),
      service_ticket_id: ticketId,
      ticket_doc_no: ticket?.doc_no ?? null,
      ticket_status: ticketStatus,
      ticket_summary: ticket?.summary ?? null,
      ticket_description: ticket?.description ?? null,
      ticket_asset_status: (row?.status || 'diagnose') as TicketAssetStatus,
      issue,
      resolution,
      date_sent: ticket?.opened_at ?? null,
      date_returned: dateReturned,
      service_tech: serviceTech,
      owner_location: ticket?.location?.name ?? null,
    };
  });

  rows.sort((a, b) => {
    const aTs = a.date_sent ? Date.parse(a.date_sent) || 0 : 0;
    const bTs = b.date_sent ? Date.parse(b.date_sent) || 0 : 0;
    return bTs - aTs;
  });

  return { data: rows, error: ticketAssetsError ?? actionsError ?? null };
}

export async function fetchActiveServiceTicketForAsset(
  assetId: string | null | undefined,
  options?: ClientOption,
): Promise<{ data: ActiveAssetServiceTicket | null; error: PostgrestError | null }> {
  if (!assetId) return { data: null, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_ticket_assets')
    .select(`
      service_ticket_asset_id,
      service_ticket_id,
      asset_id,
      issue_notes,
      status,
      created_at,
      ticket:service_tickets!inner(
        service_ticket_id,
        doc_no,
        opened_at,
        status,
        summary
      )
    `)
    .eq('asset_id', assetId)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) return { data: null, error };

  const rows = Array.isArray(data) ? (data as any[]) : [];
  for (const row of rows) {
    const ticket = firstRelated(row?.ticket);
    const ticketId = ticket?.service_ticket_id
      ? String(ticket.service_ticket_id)
      : row?.service_ticket_id
        ? String(row.service_ticket_id)
        : '';
    if (!ticketId) continue;
    const status = String(ticket?.status || '').toLowerCase();
    if (status === 'closed' || status === 'cancelled') continue;
    return {
      data: {
        serviceTicketAssetId: row?.service_ticket_asset_id ? String(row.service_ticket_asset_id) : '',
        serviceTicketId: ticketId,
        ticketNumber: ticket?.doc_no ? String(ticket.doc_no) : null,
        ticketStatus: ticket?.status ? String(ticket.status) : 'open',
        ticketAssetStatus: row?.status ? String(row.status) : null,
        issueNotes: row?.issue_notes ? String(row.issue_notes) : null,
        summary: ticket?.summary ? String(ticket.summary) : null,
        openedAt: ticket?.opened_at ? String(ticket.opened_at) : null,
      },
      error: null,
    };
  }

  return { data: null, error: null };
}

function parseQtyFromActions(actions: any[]): number | null {
  for (const action of actions) {
    const details = action?.details;
    if (!details || typeof details !== 'object') continue;
    const directQty = (details as Record<string, unknown>).qty;
    if (typeof directQty === 'number' && Number.isFinite(directQty) && directQty > 0) {
      return directQty;
    }
    const altQty = (details as Record<string, unknown>).quantity;
    if (typeof altQty === 'number' && Number.isFinite(altQty) && altQty > 0) {
      return altQty;
    }
  }
  return null;
}

export async function fetchServiceHistoryByItem(
  itemId: string,
  options?: ClientOption,
): Promise<{ data: ItemServiceHistoryRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data: ticketAssets, error: ticketAssetsError } = await supabase
    .from('service_ticket_assets')
    .select(
      `
      service_ticket_asset_id,
      service_ticket_id,
      asset_id,
      product_id,
      issue_notes,
      status,
      outcome,
      findings_notes,
      work_performed_notes,
      recommendation_notes,
      completed_at,
      retired_at,
      asset:assets!fk_sta_asset(asset_id, item_id, barcode, serial_number),
      product:products!left(
        product_id,
        name_public,
        default_item_id,
        product_item_link(item_id)
      ),
      ticket:service_tickets!inner(
        service_ticket_id,
        doc_no,
        opened_at,
        status,
        summary,
        description,
        location:locations!fk_st_loc(name)
      )
    `,
    );

  const typedRows = Array.isArray(ticketAssets) ? (ticketAssets as any[]) : [];
  const filteredRows = typedRows.filter((row) => {
    const assetItemId = String(row?.asset?.item_id || '');
    if (assetItemId && assetItemId === itemId) return true;

    const defaultItemId = String(row?.product?.default_item_id || '');
    if (defaultItemId && defaultItemId === itemId) return true;

    const linkedItems = Array.isArray(row?.product?.product_item_link)
      ? (row.product.product_item_link as Array<{ item_id?: string | null }>)
      : [];
    return linkedItems.some((entry) => String(entry?.item_id || '') === itemId);
  });

  const ticketIds = Array.from(
    new Set(
      filteredRows
        .map((row) => String(row?.ticket?.service_ticket_id || row?.service_ticket_id || ''))
        .filter(Boolean),
    ),
  );

  let actionsError: PostgrestError | null = null;
  let ticketActions: any[] = [];
  if (ticketIds.length > 0) {
    const { data, error } = await supabase
      .from('service_actions')
      .select('service_ticket_id, action_at, action, details')
      .in('service_ticket_id', ticketIds)
      .order('action_at', { ascending: false });
    ticketActions = Array.isArray(data) ? data : [];
    actionsError = error ?? null;
  }

  const actionsByTicket = new Map<string, any[]>();
  ticketActions.forEach((action) => {
    const key = String(action?.service_ticket_id || '');
    if (!key) return;
    const existing = actionsByTicket.get(key) ?? [];
    existing.push(action);
    actionsByTicket.set(key, existing);
  });

  const historyRows: ItemServiceHistoryRow[] = filteredRows.map((row) => {
    const ticket = row?.ticket ?? null;
    const ticketId = String(ticket?.service_ticket_id || row?.service_ticket_id || '');
    const ticketStatus = (ticket?.status || 'open') as ServiceTicketStatus;
    const actions = actionsByTicket.get(ticketId) ?? [];
    const closeAction = findServiceTicketCompletionAction(actions);
    const issue = row?.issue_notes || ticket?.summary || ticket?.description || null;
    const structuredResolution =
      row?.work_performed_notes ||
      row?.findings_notes ||
      row?.recommendation_notes ||
      (row?.outcome ? String(row.outcome).replace(/_/g, ' ') : null);
    const resolution = structuredResolution || closeAction?.action || (ticketStatus === 'closed' ? 'Completed' : null);
    const dateReturned = row?.completed_at || row?.retired_at || closeAction?.action_at || null;
    const serviceTech = null;
    const assetBarcode = row?.asset?.barcode ?? null;
    const assetSerial = row?.asset?.serial_number ?? null;
    const assetId = row?.asset?.asset_id ?? row?.asset_id ?? null;
    const qty = parseQtyFromActions(actions) ?? 1;

    return {
      service_ticket_asset_id: String(row?.service_ticket_asset_id || ''),
      service_ticket_id: ticketId,
      ticket_doc_no: ticket?.doc_no ?? null,
      ticket_status: ticketStatus,
      ticket_summary: ticket?.summary ?? null,
      ticket_description: ticket?.description ?? null,
      ticket_asset_status: (row?.status || 'diagnose') as TicketAssetStatus,
      issue,
      resolution,
      date_sent: ticket?.opened_at ?? null,
      date_returned: dateReturned,
      service_tech: serviceTech,
      owner_location: ticket?.location?.name ?? null,
      asset_id: assetId,
      asset_barcode: assetBarcode,
      asset_serial: assetSerial,
      asset_label: assetBarcode || assetSerial || (assetId ? String(assetId).slice(0, 8) : 'Item (non-serialized)'),
      quantity: qty,
      is_asset_linked: Boolean(assetId),
    };
  });

  historyRows.sort((a, b) => {
    const aTs = a.date_sent ? Date.parse(a.date_sent) || 0 : 0;
    const bTs = b.date_sent ? Date.parse(b.date_sent) || 0 : 0;
    return bTs - aTs;
  });

  return { data: historyRows, error: ticketAssetsError ?? actionsError ?? null };
}

// â”€â”€â”€ Mutations â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function createLinkedAssetTransfer(
  params: {
    fromLocationId: string;
    toLocationId: string;
    ticketId: string;
    asset: Pick<ServiceAssetRow, 'asset_id' | 'item_id'>;
    isPermanent?: boolean;
    completeNow?: boolean;
    actorUserId?: string | null;
    completedAt?: string | null;
    completionNotes?: string | null;
  } & ClientOption,
): Promise<{ transferId: string | null; error: PostgrestError | Error | null }> {
  if (!params.asset.item_id) return { transferId: null, error: new Error('Asset item_id is required for transfer lines') };
  const supabase = params.supabase ?? getSupabaseClient();
  const now = params.completedAt ?? new Date().toISOString();
  const completeNow = Boolean(params.completeNow);
  const { data: transfer, error: transferError } = await supabase
    .from('transfer_orders')
    .insert({
      from_location_id: params.fromLocationId,
      to_location_id: params.toLocationId,
      service_ticket_id: params.ticketId,
      status: completeNow ? 'complete' : 'draft',
      need_by_at: now,
      expected_ship_at: completeNow ? now : null,
      expected_receive_at: completeNow ? now : null,
      actual_ship_at: completeNow ? now : null,
      actual_receive_at: completeNow ? now : null,
    })
    .select('transfer_id')
    .maybeSingle();
  if (transferError || !transfer?.transfer_id) {
    return { transferId: null, error: transferError ?? new Error('Failed to create transfer order') };
  }

  const { error: lineError } = await supabase.from('transfer_lines').insert({
    transfer_id: transfer.transfer_id,
    asset_id: params.asset.asset_id,
    item_id: params.asset.item_id,
    qty: 1,
    status: completeNow ? (params.isPermanent ? 'kept' : 'received') : 'pending',
    shipped_qty: completeNow ? 1 : 0,
    received_qty: completeNow ? 1 : 0,
    kept_qty: completeNow && params.isPermanent ? 1 : 0,
    is_permanent: params.isPermanent ?? false,
    picked_at: completeNow ? now : null,
    shipped_at: completeNow ? now : null,
    received_at: completeNow ? now : null,
    picked_by: completeNow ? params.actorUserId ?? null : null,
    shipped_by: completeNow ? params.actorUserId ?? null : null,
    received_by: completeNow ? params.actorUserId ?? null : null,
  });
  if (lineError) return { transferId: String(transfer.transfer_id), error: lineError };
  if (completeNow) {
    const eventRows = [
      { event_type: 'created', notes: params.completionNotes ?? 'Service replacement transfer created.' },
      { event_type: 'shipped', notes: params.completionNotes ?? 'Service replacement transfer shipped automatically.' },
      { event_type: 'received', notes: params.completionNotes ?? 'Service replacement transfer received automatically.' },
      { event_type: 'complete', notes: params.completionNotes ?? 'Service replacement transfer completed automatically.' },
    ].map((event) => ({
      transfer_id: transfer.transfer_id,
      event_type: event.event_type,
      notes: event.notes,
      created_by: params.actorUserId ?? null,
    }));
    const { error: eventError } = await supabase.from('transfer_order_events').insert(eventRows);
    if (eventError) return { transferId: String(transfer.transfer_id), error: eventError };
  }
  return { transferId: String(transfer.transfer_id), error: null };
}

/**
 * Adds a note to an asset's record. An audit errand: every caller has already saved the change
 * itself, so a failure here must not fail it. It is logged with the asset instead. (An asset's status
 * history is written by the database writer that changes the status, in the same transaction.)
 */
async function createAssetNote(
  params: {
    assetId: string;
    noteType: 'general' | 'service' | 'replacement' | 'retirement' | 'vendor_repair';
    title: string;
    body?: string | null;
    relatedAssetId?: string | null;
    ticketId?: string | null;
    createdByProfileId?: string | null;
  } & ClientOption,
): Promise<void> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('asset_notes').insert({
    asset_id: params.assetId,
    note_type: params.noteType,
    title: params.title,
    body: params.body ?? null,
    related_asset_id: params.relatedAssetId ?? null,
    service_ticket_id: params.ticketId ?? null,
    created_by: params.createdByProfileId ?? null,
  });
  if (error) console.warn('[service] Asset note was not recorded', { assetId: params.assetId, title: params.title, error });
}

async function fetchExternalTicketClientSnapshot(
  params: {
    clientId: string;
    clientBranchId?: string | number | null;
    serviceLocationId?: string | null;
  } & ClientOption,
): Promise<{
  client: ClientReferenceRow | null;
  branch: ClientBranchReferenceRow | null;
  originLocation: { location_id: string; name: string | null; address_line1?: string | null; address_line2?: string | null; city?: string | null; state?: string | null; postal_code?: string | null; country?: string | null } | null;
  error: PostgrestError | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const branchId = params.clientBranchId ? Number(params.clientBranchId) : null;
  const [clientResult, branchResult, originResult] = await Promise.all([
    supabase
      .from('clients')
      .select('id, business_name, address_line1, address_line2, city, state, postal_code, country, set_discount_percent, tax_exempt, tax_exempt_expires_on, payment_terms, payment_term:payment_terms!clients_payment_terms_id_fkey(payment_terms_id, payment_terms_code, description)')
      .eq('id', params.clientId)
      .maybeSingle(),
    branchId
      ? supabase
          .from('client_branches')
          .select('id, branch_name, billing_address_line1, billing_address_line2, billing_city, billing_state, billing_postal_code, billing_country, shipping_address_line1, shipping_address_line2, shipping_city, shipping_state, shipping_postal_code, shipping_country, set_discount_percent, payment_terms, payment_term:payment_terms!client_branches_payment_terms_id_fkey(payment_terms_id, payment_terms_code, description)')
          .eq('id', branchId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    params.serviceLocationId
      ? supabase
          .from('locations')
          .select('location_id, name, address_line1, address_line2, city, state, postal_code, country')
          .eq('location_id', params.serviceLocationId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
  ]);

  return {
    client: (clientResult.data as ClientReferenceRow | null) ?? null,
    branch: (branchResult.data as ClientBranchReferenceRow | null) ?? null,
    originLocation: (originResult.data as any) ?? null,
    error: clientResult.error ?? branchResult.error ?? originResult.error ?? null,
  };
}

function addressSnapshotFromClient(client: ClientReferenceRow | null): Record<string, string | null> {
  return {
    name: client?.business_name ?? null,
    address_line1: client?.address_line1 ?? null,
    address_line2: client?.address_line2 ?? null,
    city: client?.city ?? null,
    state: client?.state ?? null,
    postal_code: client?.postal_code ?? null,
    country: client?.country ?? null,
  };
}

function billingSnapshot(client: ClientReferenceRow | null, branch: ClientBranchReferenceRow | null): Record<string, string | null> {
  if (branch) {
    return {
      name: branch.branch_name || client?.business_name || null,
      address_line1: branch.billing_address_line1 || client?.address_line1 || null,
      address_line2: branch.billing_address_line2 || client?.address_line2 || null,
      city: branch.billing_city || client?.city || null,
      state: branch.billing_state || client?.state || null,
      postal_code: branch.billing_postal_code || client?.postal_code || null,
      country: branch.billing_country || client?.country || null,
    };
  }
  return addressSnapshotFromClient(client);
}

function paymentTermsLabelFromReference(row: ClientReferenceRow | ClientBranchReferenceRow | null): string | null {
  const related = firstRelated(row?.payment_term);
  const relatedDescription = normalizeText(related?.description);
  if (relatedDescription) return relatedDescription;
  const rawValue = normalizeText(row?.payment_terms);
  return rawValue && !isUuidLike(rawValue) ? rawValue : null;
}

function paymentTermsSnapshot(client: ClientReferenceRow | null, branch: ClientBranchReferenceRow | null): string | null {
  return paymentTermsLabelFromReference(branch) ?? paymentTermsLabelFromReference(client);
}

function serviceLocationSnapshot(client: ClientReferenceRow | null, branch: ClientBranchReferenceRow | null): Record<string, string | null> {
  if (branch) {
    return {
      name: branch.branch_name || client?.business_name || null,
      address_line1: branch.shipping_address_line1 || branch.billing_address_line1 || client?.address_line1 || null,
      address_line2: branch.shipping_address_line2 || branch.billing_address_line2 || client?.address_line2 || null,
      city: branch.shipping_city || branch.billing_city || client?.city || null,
      state: branch.shipping_state || branch.billing_state || client?.state || null,
      postal_code: branch.shipping_postal_code || branch.billing_postal_code || client?.postal_code || null,
      country: branch.shipping_country || branch.billing_country || client?.country || null,
    };
  }
  return addressSnapshotFromClient(client);
}

/** Reuse intake snapshots when staff deliberately choose another customer/location. */
export async function fetchExternalServiceTicketCustomerDefaults(clientId: string, branchId: string | null, options?: ClientOption) {
  const snapshot = await fetchExternalTicketClientSnapshot({ clientId, clientBranchId: branchId, ...options });
  if (snapshot.error) throw snapshot.error;
  if (!snapshot.client || (branchId && !snapshot.branch)) throw new Error('The selected customer or location is no longer available.');
  const service = serviceLocationSnapshot(snapshot.client, snapshot.branch);
  const billing = billingSnapshot(snapshot.client, snapshot.branch);
  return {
    customer_branch_name: snapshot.branch?.branch_name || '',
    service_location_name: service.name || '', service_address_line1: service.address_line1 || '',
    service_address_line2: service.address_line2 || '', service_city: service.city || '',
    service_state: service.state || '', service_postal_code: service.postal_code || '', service_country: service.country || '',
    service_room: '',
    bill_to_name: billing.name || '', bill_to_address_line1: billing.address_line1 || '',
    bill_to_address_line2: billing.address_line2 || '', bill_to_city: billing.city || '',
    bill_to_state: billing.state || '', bill_to_postal_code: billing.postal_code || '', bill_to_country: billing.country || '',
    payment_terms: paymentTermsSnapshot(snapshot.client, snapshot.branch) || '',
  };
}

/** One version-checked write owns ticket, customer agreement and route changes. */
export async function saveExternalServiceTicketDetails(ticketId: string, expectedUpdatedAt: string | null, updates: Record<string, unknown>, options?: ClientOption): Promise<void> {
  if (!Object.keys(updates).length) return;
  if (!expectedUpdatedAt) throw new Error('Reload this ticket before saving so its current version can be checked.');
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('update_external_service_ticket_details', {
    p_ticket_id: ticketId, p_expected_updated_at: expectedUpdatedAt, p_patch: updates,
  });
  if (error) throw withServiceTicketCompletionCopy(error);
}

/**
 * Last resort for a new ticket's trip zone, when neither Google nor the ZIP codes give driving miles: the address
 * names Columbus or Dayton, or neither. The ticket then asks for its trip charge to be checked.
 */
export function selectDefaultExternalServiceTripRadius(serviceLocation: Record<string, string | null | undefined>): number {
  const locationText = [
    serviceLocation.name,
    serviceLocation.address_line1,
    serviceLocation.address_line2,
    serviceLocation.city,
    serviceLocation.state,
    serviceLocation.postal_code,
  ].map((part) => normalizeText(part)).filter(Boolean).join(' ').toLowerCase();

  if (/\bcolumbus\b/.test(locationText)) {
    return 120;
  }
  if (/\bdayton\b/.test(locationText)) {
    return 60;
  }
  return 30;
}

/** The flat diagnostic charge every new external ticket starts with. */
export function selectDefaultExternalServiceDiagnosticCharge(
  charges: ServiceChargeCatalogRow[],
): ServiceChargeCatalogRow | null {
  const diagnosticCharges = charges
    .filter((charge) => (
      charge.category === 'diagnostic'
      && charge.unit === 'flat'
      && charge.active
      && Number(charge.included_labor_hours ?? 0) <= 0
    ))
    .sort((a, b) => {
      if (a.code === DEFAULT_EXTERNAL_SERVICE_DIAGNOSTIC_CHARGE_CODE) return -1;
      if (b.code === DEFAULT_EXTERNAL_SERVICE_DIAGNOSTIC_CHARGE_CODE) return 1;
      return Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
    });
  return diagnosticCharges[0] || null;
}

function selectDefaultExternalServiceTravelCharge(
  charges: ServiceChargeCatalogRow[],
  radius: number,
): ServiceChargeCatalogRow | null {
  const targetCode = DEFAULT_EXTERNAL_SERVICE_TRIP_CHARGE_CODES_BY_RADIUS[radius] || null;
  const flatCharges = charges
    .filter((charge) => charge.category === 'travel' && charge.unit === 'flat' && charge.radius_miles != null)
    .sort((a, b) => {
      const radiusDelta = Number(a.radius_miles ?? 0) - Number(b.radius_miles ?? 0);
      if (radiusDelta !== 0) return radiusDelta;
      return Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
    });
  return flatCharges.find((charge) => targetCode && charge.code === targetCode)
    || flatCharges.find((charge) => Number(charge.radius_miles ?? 0) === radius)
    || flatCharges.find((charge) => Number(charge.radius_miles ?? 0) >= radius)
    || flatCharges[flatCharges.length - 1]
    || null;
}

function selectDefaultDiagnosticLabor(laborRows: ServiceLaborCatalogRow[]): ServiceLaborCatalogRow | null {
  const hourlyRows = laborRows.filter((row) => row.uom === 'hourly');
  return (
    hourlyRows.find((row) => /\bdiagnostic\b/i.test(`${row.code} ${row.name}`))
    || hourlyRows.find((row) => /service\s*technician/i.test(`${row.code} ${row.name}`))
    || hourlyRows[0]
    || laborRows[0]
    || null
  );
}

function minimalCreatedExternalServiceTicketRow(params: {
  serviceTicketId: string;
  locationId: string | null;
  openedBy?: string | null;
  actorUserId?: string | null;
  summary: string;
  problemReported: string;
  priority?: ServiceTicketPriority;
}): ServiceTicketRow {
  return {
    service_ticket_id: params.serviceTicketId,
    doc_no: null,
    opened_at: new Date().toISOString(),
    opened_by: params.openedBy ?? params.actorUserId ?? null,
    location_id: params.locationId,
    ticket_type: 'external',
    priority: params.priority ?? 'normal',
    status: 'open',
    summary: normalizeText(params.summary),
    description: normalizeText(params.problemReported),
  };
}

async function insertServiceTicketLineItemPayload(
  supabase: SupabaseClient,
  payload: Record<string, unknown>,
): Promise<PostgrestError | null> {
  let { error } = await supabase.from('service_ticket_line_items').insert([payload]);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await supabase
      .from('service_ticket_line_items')
      .insert([withoutPayloadKeys(payload, SERVICE_LINE_TAX_OPTIONAL_KEYS)]);
    error = fallback.error;
  }
  return error ?? null;
}

async function insertServiceTicketLaborLinePayload(
  supabase: SupabaseClient,
  payload: Record<string, unknown>,
): Promise<PostgrestError | null> {
  let { error } = await supabase.from('service_ticket_labor_lines').insert([payload]);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await supabase
      .from('service_ticket_labor_lines')
      .insert([withoutPayloadKeys(payload, SERVICE_LABOR_TAX_OPTIONAL_KEYS)]);
    error = fallback.error;
  }
  return error ?? null;
}

async function insertDefaultExternalServiceLines(params: {
  serviceTicketId: string;
  serviceLocation: Record<string, string | null | undefined>;
  /** Driving miles from the service location. Without them the trip charge falls back to the address rule. */
  drivingMiles: number | null;
  actorUserId?: string | null;
  supabase: SupabaseClient;
}): Promise<{ error: PostgrestError | null; beyondTripZones: boolean }> {
  const { data: chargeData, error: chargeError } = await fetchServiceChargeCatalog({ supabase: params.supabase });
  if (chargeError) return { error: chargeError, beyondTripZones: false };

  const { data: laborData, error: laborError } = await fetchServiceLaborCatalog({ supabase: params.supabase });
  if (laborError) return { error: laborError, beyondTripZones: false };

  const activeCharges = chargeData.filter((charge) => charge.active);
  const diagnosticCharge = selectDefaultExternalServiceDiagnosticCharge(activeCharges);
  const tripZone = tripZoneForMiles(tripZonesFromCatalog(activeCharges), params.drivingMiles);
  const travelCharge = tripZone?.zone.charge ?? selectDefaultExternalServiceTravelCharge(
    activeCharges.filter((charge) => charge.category === 'travel' && charge.unit === 'flat'),
    selectDefaultExternalServiceTripRadius(params.serviceLocation),
  );
  const diagnosticLabor = selectDefaultDiagnosticLabor(laborData.filter((row) => row.active));

  if (diagnosticCharge) {
    const insertDiagnosticError = await insertServiceTicketLineItemPayload(params.supabase, {
      service_ticket_id: params.serviceTicketId,
      line_type: 'charge' as ServiceLineItemType,
      charge_id: diagnosticCharge.charge_id,
      description: diagnosticCharge.label,
      quantity: 1,
      unit_price: Number(diagnosticCharge.amount ?? 0),
      discount_percent: 0,
      taxable: diagnosticCharge.taxable ?? true,
      tax_category: diagnosticCharge.tax_category ?? 'labor',
      sort_order: 10,
      created_by: params.actorUserId ?? null,
      updated_by: params.actorUserId ?? null,
    });
    if (insertDiagnosticError) return { error: insertDiagnosticError, beyondTripZones: false };
  }

  if (travelCharge) {
    const insertTravelError = await insertServiceTicketLineItemPayload(params.supabase, {
      service_ticket_id: params.serviceTicketId,
      line_type: 'charge' as ServiceLineItemType,
      charge_id: travelCharge.charge_id,
      description: travelCharge.label,
      quantity: 1,
      unit_price: Number(travelCharge.amount ?? 0),
      discount_percent: 0,
      taxable: travelCharge.taxable ?? true,
      tax_category: travelCharge.tax_category ?? 'freight',
      sort_order: 20,
      created_by: params.actorUserId ?? null,
      updated_by: params.actorUserId ?? null,
    });
    if (insertTravelError) return { error: insertTravelError, beyondTripZones: false };
  }

  // Every external ticket starts with a non-billable Diagnostic Labor row. It is the quote only, so
  // it needs no date: visits are planned on /schedule.
  const isDiagnosticCatalogRow = /\bdiagnostic\b/i.test(`${diagnosticLabor?.code ?? ''} ${diagnosticLabor?.name ?? ''}`);
  const insertLaborError = await insertServiceTicketLaborLinePayload(params.supabase, {
    service_ticket_id: params.serviceTicketId,
    labor_id: isDiagnosticCatalogRow ? diagnosticLabor?.labor_id ?? null : null,
    role_name: 'Diagnostic Labor',
    description: 'Diagnostic Labor',
    uom: diagnosticLabor?.uom ?? 'hourly',
    hours: 1,
    hourly_rate: 0,
    billable: false,
    taxable: diagnosticLabor?.taxable ?? true,
    tax_category: diagnosticLabor?.tax_category ?? 'labor',
    sort_order: 10,
    created_by: params.actorUserId ?? null,
    updated_by: params.actorUserId ?? null,
  });
  return { error: insertLaborError ?? null, beyondTripZones: Boolean(tripZone?.beyond) };
}

/**
 * Why a new ticket's trip charge needs a look: Google couldn't measure the drive (`estimated`), or the drive is past
 * the last trip zone, so extra travel must be added by hand (`beyond_zones`).
 */
export type ExternalTicketTripChargeCheck = {
  reason: 'estimated' | 'beyond_zones';
  drivingMiles: number | null;
};

/**
 * Driving miles for a new ticket's trip charge. Google's route also saves the miles on the ticket's Logistics
 * row; when Google can't answer, the ZIP codes give an estimate, and null leaves the address rule.
 */
async function measureNewExternalTicketDrive(params: {
  serviceTicketId: string;
  originPostalCode: string | null | undefined;
  servicePostalCode: string | null | undefined;
}): Promise<{ miles: number | null; estimated: boolean }> {
  try {
    const route = await refreshServiceTicketLogisticsDistance(params.serviceTicketId, { force: false });
    if (route.distanceMiles != null && Number.isFinite(route.distanceMiles)) {
      return { miles: route.distanceMiles, estimated: false };
    }
  } catch (routeError) {
    console.warn('[service-ticket-create] route lookup failed; estimating the trip from ZIP codes', routeError);
  }
  try {
    return { miles: await estimateRoadMilesBetweenZips(params.originPostalCode, params.servicePostalCode), estimated: true };
  } catch (zipError) {
    console.warn('[service-ticket-create] ZIP estimate failed; the trip charge follows the address', zipError);
    return { miles: null, estimated: true };
  }
}

export async function generateExternalServiceTicketTitle(
  params: ExternalServiceTicketTitleRequest,
): Promise<{ data: string | null; error: Error | null }> {
  const problemReported = normalizeText(params.problemReported);
  if (!problemReported) return { data: null, error: new Error('Problem reported is required.') };

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timeoutMs = Math.max(0, params.timeoutMs ?? EXTERNAL_SERVICE_TICKET_TITLE_TIMEOUT_MS);
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  if (controller && timeoutMs > 0) {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    const response = await apiFetch('/ai/service-ticket-title', {
      method: 'POST',
      signal: controller?.signal,
      body: JSON.stringify({
        customerName: normalizeText(params.customerName),
        serviceLocationName: normalizeText(params.serviceLocationName),
        serviceRoom: normalizeText(params.serviceRoom),
        serviceAddress: normalizeText(params.serviceAddress),
        contactName: normalizeText(params.contactName),
        priority: params.priority ?? null,
        requestedServiceDate: normalizeText(params.requestedServiceDate),
        problemReported,
      }),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const message = typeof payload?.error === 'string' ? payload.error : 'Failed to generate service ticket title';
      throw new Error(message);
    }

    const title = normalizeText(typeof payload?.title === 'string' ? payload.title : null);
    return { data: title || null, error: null };
  } catch (err: unknown) {
    const errorName = err && typeof err === 'object' && 'name' in err
      ? (err as { name?: unknown }).name
      : null;
    if (errorName === 'AbortError') {
      return { data: null, error: new Error('Title generation timed out; using a fallback title.') };
    }
    return { data: null, error: err instanceof Error ? err : new Error(String(err)) };
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function createExternalServiceTicket(
  params: {
    clientId: string;
    clientBranchId?: string | number | null;
    clientServiceLocationId?: string | null;
    serviceRoom?: string | null;
    serviceContactName?: string | null;
    serviceContactEmail?: string | null;
    serviceContactPhone?: string | null;
    serviceAgreementAssignmentId?: string | null;
    serviceAgreementTierId?: string | null;
    pmVisitId?: string | null;
    isPmVisit?: boolean;
    creationId?: string;
    summary: string;
    problemReported: string;
    requestedServiceDate?: string | null;
    requestedServiceTimePreference?: ServiceTicketTimePreference | null;
    requestedServiceStartTime?: string | null;
    priority?: ServiceTicketPriority;
    serviceLocationId?: string | null;
    openedBy?: string | null;
    actorUserId?: string | null;
  } & ClientOption,
): Promise<{
  data: ServiceTicketRow | null;
  error: PostgrestError | Error | null;
  defaultLinesIncomplete?: boolean;
  tripChargeCheck?: ExternalTicketTripChargeCheck;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const settings = await fetchServiceSettings({ supabase });
  const locationId = params.serviceLocationId ?? settings.data.serviceLocationId;
  const serviceDate = resolveServiceTicketAgreementDate({ requestedServiceDate: params.requestedServiceDate });

  const agreements = await fetchClientServiceAgreements(params.clientId, { supabase });
  if (agreements.error) return { data: null, error: agreements.error };
  const agreement = selectClientServiceAgreementForTicket(agreements.data, {
    serviceDate,
    clientBranchId: params.clientBranchId,
    clientServiceLocationId: params.clientServiceLocationId,
    serviceAgreementAssignmentId: params.serviceAgreementAssignmentId,
  });

  const snapshot = await fetchExternalTicketClientSnapshot({
    clientId: params.clientId,
    clientBranchId: params.clientBranchId ?? agreement?.client_branch_id ?? null,
    serviceLocationId: locationId,
    supabase,
  });
  if (snapshot.error) return { data: null, error: snapshot.error };

  const billing = billingSnapshot(snapshot.client, snapshot.branch);
  const serviceAddress = serviceLocationSnapshot(snapshot.client, snapshot.branch);
  const coveredLocation = agreement?.coverage_snapshot?.location as { name?: string; address?: { line1?: string; line2?: string; city?: string; state?: string; postal_code?: string; country?: string } } | undefined;
  if (agreement?.client_service_location_id && coveredLocation?.address) {
    Object.assign(serviceAddress, { name: coveredLocation.name ?? serviceAddress.name, address_line1: coveredLocation.address.line1 ?? null,
      address_line2: coveredLocation.address.line2 ?? null, city: coveredLocation.address.city ?? null, state: coveredLocation.address.state ?? null,
      postal_code: coveredLocation.address.postal_code ?? null, country: coveredLocation.address.country ?? 'US' });
  }
  const paymentTerms = paymentTermsSnapshot(snapshot.client, snapshot.branch);
  const tierId = agreement?.tier_id ?? null;
  const discount = agreement?.tier?.parts_labor_discount_percent ?? snapshot.branch?.set_discount_percent ?? snapshot.client?.set_discount_percent ?? 0;

  const ticketPayload = {
      service_ticket_id: params.creationId ?? crypto.randomUUID(),
      is_pm_visit: params.isPmVisit ?? !!params.pmVisitId,
      pm_visit_id: params.pmVisitId || null,
      ticket_type: 'external',
      client_id: params.clientId,
      client_branch_id: params.clientBranchId ?? agreement?.client_branch_id ?? null,
      service_agreement_assignment_id: agreement?.assignment_id ?? null,
      client_service_location_id: agreement?.client_service_location_id ?? params.clientServiceLocationId ?? null,
      service_agreement_tier_id: tierId,
      location_id: locationId,
      summary: normalizeText(params.summary),
      description: normalizeText(params.problemReported),
      priority: params.priority ?? 'normal',
      opened_by: params.openedBy ?? params.actorUserId ?? null,
      updated_by: params.actorUserId ?? params.openedBy ?? null,
      requested_service_date: params.requestedServiceDate || null,
      requested_service_time_preference: normalizeServiceTicketTimePreference(params.requestedServiceTimePreference),
      requested_service_start_time: serviceTicketStartTimeForPreference(
        normalizeServiceTicketTimePreference(params.requestedServiceTimePreference),
        params.requestedServiceStartTime,
      ),
      service_location_name: serviceAddress.name,
      service_room: normalizeText(params.serviceRoom) || null,
      service_address_line1: serviceAddress.address_line1,
      service_address_line2: serviceAddress.address_line2,
      service_city: serviceAddress.city,
      service_state: serviceAddress.state,
      service_postal_code: serviceAddress.postal_code,
      service_country: serviceAddress.country,
      service_contact_name: normalizeText(params.serviceContactName) || null,
      service_contact_email: normalizeText(params.serviceContactEmail) || null,
      service_contact_phone: normalizeText(params.serviceContactPhone) || null,
      bill_to_name: billing.name,
      bill_to_address_line1: billing.address_line1,
      bill_to_address_line2: billing.address_line2,
      bill_to_city: billing.city,
      bill_to_state: billing.state,
      bill_to_postal_code: billing.postal_code,
      bill_to_country: billing.country,
      payment_terms: paymentTerms,
      sa_discount_percent: Number(discount || 0),
      client_visible: false,
    };
  const insertQuery = params.creationId
    ? supabase.from('service_tickets').upsert(ticketPayload, { onConflict: 'service_ticket_id', ignoreDuplicates: true })
    : supabase.from('service_tickets').insert(ticketPayload);
  const { data: inserted, error } = await insertQuery.select('service_ticket_id').maybeSingle();

  if (!error && !inserted && params.creationId) return fetchServiceTicketById(params.creationId, { supabase });
  if (error || !inserted?.service_ticket_id) return { data: null, error: error ?? new Error('Failed to create external service ticket') };

  const originSnapshot = snapshot.originLocation
    ? {
        location_id: snapshot.originLocation.location_id,
        name: snapshot.originLocation.name,
        address_line1: snapshot.originLocation.address_line1 ?? null,
        address_line2: snapshot.originLocation.address_line2 ?? null,
        city: snapshot.originLocation.city ?? null,
        state: snapshot.originLocation.state ?? null,
        postal_code: snapshot.originLocation.postal_code ?? null,
        country: snapshot.originLocation.country ?? null,
    }
    : {};

  try {
    const { error: logisticsError } = await supabase.from('service_ticket_logistics').insert({
      service_ticket_id: inserted.service_ticket_id,
      origin_location_id: locationId,
      destination_client_id: params.clientId,
      destination_client_branch_id: params.clientBranchId ?? agreement?.client_branch_id ?? null,
      origin_snapshot: originSnapshot,
      destination_snapshot: {
        ...serviceAddress,
        room: normalizeText(params.serviceRoom) || null,
      },
      collapsed: true,
      created_by: params.actorUserId ?? params.openedBy ?? null,
    });
    if (logisticsError) {
      console.warn('[service-ticket-create] logistics setup failed after ticket creation', logisticsError);
    }
  } catch (setupError) {
    console.warn('[service-ticket-create] logistics setup failed after ticket creation', setupError);
  }

  let defaultLinesIncomplete = false;
  let tripChargeCheck: ExternalTicketTripChargeCheck | null = null;
  if (!params.isPmVisit && !params.pmVisitId) {
    try {
      // The trip charge follows the driving miles, so the drive is measured (and saved on the Logistics row
      // above) before the default lines go in.
      const drive = await measureNewExternalTicketDrive({
        serviceTicketId: inserted.service_ticket_id,
        originPostalCode: snapshot.originLocation?.postal_code,
        servicePostalCode: serviceAddress.postal_code,
      });
      const { error: baseChargeError, beyondTripZones } = await insertDefaultExternalServiceLines({
        serviceTicketId: inserted.service_ticket_id,
        serviceLocation: serviceAddress,
        drivingMiles: drive.miles,
        actorUserId: params.actorUserId ?? params.openedBy ?? null,
        supabase,
      });
      if (baseChargeError) {
        defaultLinesIncomplete = true;
        console.warn('[service-ticket-create] default line setup failed after ticket creation', baseChargeError);
      } else if (drive.estimated) {
        tripChargeCheck = { reason: 'estimated', drivingMiles: drive.miles };
      } else if (beyondTripZones) {
        tripChargeCheck = { reason: 'beyond_zones', drivingMiles: drive.miles };
      }
    } catch (setupError) {
      defaultLinesIncomplete = true;
      console.warn('[service-ticket-create] default line setup failed after ticket creation', setupError);
    }
  }
  const setupNotes = {
    ...(defaultLinesIncomplete ? { defaultLinesIncomplete: true } : {}),
    ...(tripChargeCheck ? { tripChargeCheck } : {}),
  };

  const createdTicket = await fetchServiceTicketById(inserted.service_ticket_id, { supabase });
  if (createdTicket.data) return { ...createdTicket, ...setupNotes };
  if (createdTicket.error) {
    console.warn('[service-ticket-create] detail refetch failed after ticket creation', createdTicket.error);
  }
  return {
    data: minimalCreatedExternalServiceTicketRow({
      serviceTicketId: inserted.service_ticket_id,
      locationId,
      openedBy: params.openedBy,
      actorUserId: params.actorUserId,
      summary: params.summary,
      problemReported: params.problemReported,
      priority: params.priority,
    }),
    ...setupNotes,
    error: null,
  };
}

export async function updateServiceTicket(
  ticketId: string,
  updates: Partial<{
    summary: string;
    description: string | null;
    priority: ServiceTicketPriority;
    status: ServiceTicketStatus;
    location_id: string | null;
    requested_service_date: string | null;
    requested_service_time_preference: ServiceTicketTimePreference | null;
    requested_service_start_time: string | null;
    scheduled_start_at: string | null;
    invoice_date: string | null;
    completed_date: string | null;
    service_location_name: string | null;
    service_address_line1: string | null;
    service_address_line2: string | null;
    service_city: string | null;
    service_state: string | null;
    service_postal_code: string | null;
    service_country: string | null;
    service_room: string | null;
    service_contact_name: string | null;
    service_contact_email: string | null;
    service_contact_phone: string | null;
    bill_to_name: string | null;
    bill_to_address_line1: string | null;
    bill_to_address_line2: string | null;
    bill_to_city: string | null;
    bill_to_state: string | null;
    bill_to_postal_code: string | null;
    bill_to_country: string | null;
    bill_to_attn: string | null;
    bill_to_email: string | null;
    bill_to_phone: string | null;
    payment_terms: string | null;
    service_performed: string | null;
    completion_photo_waiver_reason: string | null;
    service_technician_profile_id: string | null;
    sa_discount_percent: number | null;
    tax_percent: number | null;
    tax_region_code: string | null;
    tax_sourcing_basis: string | null;
    order_received_location_id: string | null;
    client_visible: boolean | null;
    updated_by: string | null;
  }>,
  options?: ClientOption,
): Promise<{ error: PostgrestError | AppError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = { ...updates, updated_at: new Date().toISOString() };
  let { error } = await supabase
    .from('service_tickets')
    .update(payload)
    .eq('service_ticket_id', ticketId)
    .select('service_ticket_id')
    .single();
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await supabase
      .from('service_tickets')
      .update(withoutPayloadKeys(payload, SERVICE_TICKET_OPTIONAL_WRITE_KEYS))
      .eq('service_ticket_id', ticketId)
    .select('service_ticket_id')
    .single();
    error = fallback.error;
  }
  return { error: isServiceTicketUpdateDenied(error)
    ? new AppError('access_denied', 'Your changes were not saved because this ticket is unavailable or your account cannot process it. Your draft is preserved. Refresh the ticket and check your Service access before trying again.', { code: 'PGRST116', cause: error })
    : withServiceTicketCompletionCopy(error ?? null) };
}

// HINT codes raised by private.guard_external_service_ticket_completion and the PM coverage rules.
// The app never shows raw database text (see utils/appErrors), so each rule gets its own copy here.
const SERVICE_TICKET_COMPLETION_GUARD_COPY: Record<string, string> = {
  service_ticket_completion_fields_required:
    'Fill in Problem reported and Service performed before this ticket is completed or signed.',
  service_ticket_completed_fields_required:
    "Problem reported and Service performed can't be blank on a completed ticket.",
  service_ticket_completion_photo_reason_required:
    'Add a Completed / After photo, or enter why there is no photo of the completed work.',
  // PM Visit coverage (service_pm_ticket_guard and update_external_service_ticket_details, 20261006231151).
  service_pm_invoiced:
    "This ticket's invoice is posted or paid. Use the invoice correction process to change its PM coverage.",
  service_pm_completed_allocation:
    "This ticket's PM visit is completed. Only a manager or admin can change its coverage.",
  service_pm_reopen_needs_coverage:
    'Choose a PM visit or Paid PM visit before reopening this cancelled PM ticket.',
  service_pm_external_only: 'Only external tickets marked as PM visits can use an agreement visit.',
  service_pm_visit_missing: 'That PM visit no longer exists. Choose another option.',
  service_pm_visit_taken: 'That PM visit is already used by another ticket. Choose another option.',
  service_pm_manager_required: 'Only a manager or admin can change PM coverage on a completed ticket.',
  service_pm_visit_unavailable: 'That PM visit is completed or cancelled. Choose another option.',
  service_pm_wrong_customer: 'That PM visit belongs to a different customer or location.',
  service_pm_date_outside_coverage:
    "The Requested date must fall within the agreement's coverage for that PM visit.",
  service_pm_completed_cancel: "A ticket whose PM visit is completed can't be cancelled.",
  service_pm_included_labor: "Included labor must be this ticket's own labor, on a PM visit.",
};

/**
 * Turns a refusal from the completion guard or the PM coverage rules into an AppError with app
 * copy; any other error is returned as is.
 */
export function withServiceTicketCompletionCopy<T>(error: T): T | AppError {
  if (!error || typeof error !== 'object') return error;
  const { hint, code } = error as { hint?: unknown; code?: unknown };
  const copy = typeof hint === 'string' ? SERVICE_TICKET_COMPLETION_GUARD_COPY[hint] : undefined;
  if (!copy) return error;
  return new AppError('validation', copy, { code: typeof code === 'string' ? code : undefined, cause: error });
}

export type ExternalServiceTicketCompletionReadiness = {
  status: ServiceTicketStatus;
  problemReportedBlank: boolean;
  servicePerformedBlank: boolean;
  /** A live photo in the Photos tab's "Completed / After" section. */
  hasCompletionPhoto: boolean;
  photoWaiverReason: string | null;
};

/** The stored facts the completion rules check (public.external_service_ticket_completion_readiness). */
export async function fetchExternalServiceTicketCompletionReadiness(
  ticketId: string,
  options?: ClientOption,
): Promise<ExternalServiceTicketCompletionReadiness> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('external_service_ticket_completion_readiness', { p_ticket_id: ticketId });
  if (error) throw error;
  if (!data || typeof data !== 'object') throw new Error('This external service ticket is unavailable.');
  const row = data as { [key: string]: unknown };
  return {
    status: row.status as ServiceTicketStatus,
    problemReportedBlank: row.problem_reported_blank === true,
    servicePerformedBlank: row.service_performed_blank === true,
    hasCompletionPhoto: row.has_completion_photo === true,
    photoWaiverReason: typeof row.photo_waiver_reason === 'string' ? row.photo_waiver_reason : null,
  };
}

/**
 * `updateServiceTicket` reads the row back with `.single()`, so an update that matched no row answers
 * PGRST116 ("Cannot coerce the result to a single JSON object"). For a ticket the user can see, that means
 * `service_tickets_update_scope` hid it: they may not process this ticket.
 */
export function isServiceTicketUpdateDenied(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as { code?: unknown }).code === 'PGRST116');
}

/** The rule behind `service_tickets_update_scope`, for gating actions that save through a ticket. */
export async function fetchServiceTicketProcessPermission(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: boolean | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('current_user_can_process_service_ticket', { p_ticket_id: ticketId });
  return { data: error ? null : data === true, error: error ?? null };
}

export async function updateServiceTicketStatus(
  ticketId: string,
  newStatus: ServiceTicketStatus,
  options?: ClientOption & {
    /** Saved in the same statement, so the completion guard sees it (external tickets). */
    completionPhotoWaiverReason?: string | null;
    actorUserId?: string | null;
    expectedStatus?: ServiceTicketStatus;
  },
): Promise<{ error: PostgrestError | AppError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const reason = options?.completionPhotoWaiverReason?.trim();
  let query = supabase
    .from('service_tickets')
    .update({
      status: newStatus,
      ...(newStatus === 'open' && options?.expectedStatus === 'closed' ? { completed_date: null } : {}),
      updated_at: new Date().toISOString(),
      ...(reason ? { completion_photo_waiver_reason: reason } : {}),
      ...(options?.actorUserId ? { updated_by: options.actorUserId } : {}),
    })
    .eq('service_ticket_id', ticketId);
  if (options?.expectedStatus) query = query.eq('status', options.expectedStatus);
  const { error } = await query
    .select('service_ticket_id')
    .single();
  return { error: withServiceTicketCompletionCopy(error ?? null) };
}

export interface ServiceTicketInvoiceGenerationRow {
  invoice_id: string;
  generation_key: string | null;
  line_count: number | null;
  subtotal: number | null;
  tax_total: number | null;
  total: number | null;
}

export async function generateServiceTicketInvoice(
  ticketId: string,
  options?: ClientOption & { actorProfileId?: string | null; post?: boolean },
): Promise<{ data: ServiceTicketInvoiceGenerationRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('generate_service_ticket_invoice', {
    p_service_ticket_id: ticketId,
    p_actor_profile_id: options?.actorProfileId ?? null,
    p_post: options?.post ?? false,
  });
  return {
    data: Array.isArray(data) ? (data as ServiceTicketInvoiceGenerationRow[]) : [],
    error: error ?? null,
  };
}

/** A line's section and Non-Printable flag are written only when the caller sends them, so a writer
 * that knows nothing about sections (the Schedule drawer, the default lines) never moves or shows a
 * line. */
function serviceLinePlacement(values: { section_id?: string | null; non_printable?: boolean | null }) {
  const placement: { section_id?: string | null; non_printable?: boolean } = {};
  if (values.section_id !== undefined) placement.section_id = values.section_id ?? null;
  if (values.non_printable != null) placement.non_printable = Boolean(values.non_printable);
  return placement;
}

export async function saveServiceTicketLineItem(
  values: Partial<ServiceTicketLineItemRow> & {
    line_item_id?: string | null;
    service_ticket_id: string;
    actorUserId?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: ServiceTicketLineItemRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    service_ticket_id: values.service_ticket_id,
    line_type: values.line_type ?? 'charge',
    item_id: values.item_id ?? null,
    charge_id: values.charge_id ?? null,
    description: normalizeText(values.description),
    quantity: Number(values.quantity ?? 1),
    unit_cost: values.unit_cost == null ? null : Number(values.unit_cost),
    // margin_percent is NOT NULL with a default: without a value, a new line takes the default and an edit keeps its margin.
    ...(values.margin_percent == null ? {} : { margin_percent: Number(values.margin_percent) }),
    unit_price: Number(values.unit_price ?? 0),
    discount_percent: Number(values.discount_percent ?? 0),
    taxable: values.taxable ?? true,
    tax_category: values.tax_category ?? null,
    sort_order: Number(values.sort_order ?? 0),
    ...serviceLinePlacement(values),
    updated_by: values.actorUserId ?? null,
  };
  const run = (nextPayload: Partial<typeof payload>, select: string) => {
    const query = values.line_item_id
      ? supabase.from('service_ticket_line_items').update(nextPayload).eq('line_item_id', values.line_item_id)
      : supabase.from('service_ticket_line_items').insert({ ...nextPayload, created_by: values.actorUserId ?? null });
    return query.select(select).maybeSingle();
  };
  let { data, error } = await run(payload, SERVICE_TICKET_LINE_ITEM_SELECT);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await run(withoutPayloadKeys(payload, SERVICE_LINE_TAX_OPTIONAL_KEYS), SERVICE_TICKET_LINE_ITEM_SELECT_LEGACY);
    data = fallback.data;
    error = fallback.error;
  }
  const row = data as unknown as ServiceTicketLineItemRow | null;
  return {
    data: row ? normalizeServiceTicketForSchema({
      service_ticket_id: row.service_ticket_id,
      doc_no: null,
      opened_at: '',
      opened_by: null,
      location_id: null,
      priority: 'normal',
      status: 'open',
      summary: null,
      description: null,
      service_ticket_line_items: [row],
      service_ticket_labor_lines: [],
    }).service_ticket_line_items?.[0] ?? null : null,
    error: error ?? null,
  };
}

export async function deleteServiceTicketLineItem(
  lineItemId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('service_ticket_line_items').delete().eq('line_item_id', lineItemId);
  return { error: error ?? null };
}

type ServiceTicketLaborLineValues = Partial<ServiceTicketLaborLineRow> & {
  labor_line_id?: string | null;
  service_ticket_id: string;
  actorUserId?: string | null;
};

/** A labor row is the quote: who works and when are planned on /schedule, so writes never carry
 * technician_profile_id or service_date (an update leaves a legacy value alone). OT units and rate,
 * DT units and notes are written only when the caller sends them, so a writer that carries only the
 * regular-time pair (a picker retry, the Schedule drawer) never zeroes them. */
function serviceTicketLaborLinePayload(values: ServiceTicketLaborLineValues) {
  const tiers: { ot_units?: number; ot_rate?: number; dt_units?: number; notes?: string | null } = {};
  if (values.ot_units !== undefined) tiers.ot_units = Number(values.ot_units ?? 0);
  if (values.ot_rate !== undefined) tiers.ot_rate = Number(values.ot_rate ?? 0);
  if (values.dt_units !== undefined) tiers.dt_units = Number(values.dt_units ?? 0);
  if (values.notes !== undefined) tiers.notes = normalizeText(values.notes);
  return {
    service_ticket_id: values.service_ticket_id,
    labor_id: values.labor_id ?? null,
    role_name: values.role_name ?? null,
    description: normalizeText(values.description),
    uom: values.uom ?? 'hourly',
    hours: Number(values.hours ?? 1),
    hourly_rate: Number(values.hourly_rate ?? 0),
    ...tiers,
    crew_quantity: serviceLaborCrew(values),
    billable: values.billable ?? true,
    taxable: values.taxable ?? true,
    tax_category: values.tax_category ?? 'labor',
    sort_order: Number(values.sort_order ?? 0),
    ...serviceLinePlacement(values),
    updated_by: values.actorUserId ?? null,
  };
}

function normalizeServiceTicketLaborLines(rows: ServiceTicketLaborLineRow[]): ServiceTicketLaborLineRow[] {
  if (!rows.length) return [];
  return normalizeServiceTicketForSchema({
    service_ticket_id: rows[0].service_ticket_id,
    doc_no: null,
    opened_at: '',
    opened_by: null,
    location_id: null,
    priority: 'normal',
    status: 'open',
    summary: null,
    description: null,
    service_ticket_line_items: [],
    service_ticket_labor_lines: rows,
  }).service_ticket_labor_lines ?? [];
}

export async function saveServiceTicketLaborLine(
  values: ServiceTicketLaborLineValues,
  options?: ClientOption,
): Promise<{ data: ServiceTicketLaborLineRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = serviceTicketLaborLinePayload(values);
  const run = (nextPayload: Partial<typeof payload>, select: string) => {
    const query = values.labor_line_id
      ? supabase.from('service_ticket_labor_lines').update(nextPayload).eq('labor_line_id', values.labor_line_id)
      : supabase.from('service_ticket_labor_lines').insert({ ...nextPayload, created_by: values.actorUserId ?? null });
    return query.select(select).maybeSingle();
  };
  let { data, error } = await run(payload, SERVICE_TICKET_LABOR_LINE_SELECT);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await run(withoutPayloadKeys(payload, SERVICE_LABOR_LINE_OPTIONAL_KEYS), SERVICE_TICKET_LABOR_LINE_SELECT_LEGACY);
    data = fallback.data;
    error = fallback.error;
  }
  const row = data as unknown as ServiceTicketLaborLineRow | null;
  return { data: row ? normalizeServiceTicketLaborLines([row])[0] ?? null : null, error: error ?? null };
}

/** Inserts several labor rows in one request, so they are saved together or not at all. */
export async function addServiceTicketLaborLines(
  valuesList: ServiceTicketLaborLineValues[],
  options?: ClientOption,
): Promise<{ data: ServiceTicketLaborLineRow[]; error: PostgrestError | null }> {
  if (!valuesList.length) return { data: [], error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const payloads = valuesList.map(values => ({ ...serviceTicketLaborLinePayload(values), created_by: values.actorUserId ?? null }));
  const run = (rows: Array<Partial<(typeof payloads)[number]>>, select: string) => (
    supabase.from('service_ticket_labor_lines').insert(rows).select(select)
  );
  let { data, error } = await run(payloads, SERVICE_TICKET_LABOR_LINE_SELECT);
  if (isMissingOptionalServiceColumn(error)) {
    const fallback = await run(payloads.map(payload => withoutPayloadKeys(payload, SERVICE_LABOR_LINE_OPTIONAL_KEYS)), SERVICE_TICKET_LABOR_LINE_SELECT_LEGACY);
    data = fallback.data;
    error = fallback.error;
  }
  return {
    data: error ? [] : normalizeServiceTicketLaborLines((data ?? []) as unknown as ServiceTicketLaborLineRow[]),
    error: error ?? null,
  };
}

export async function deleteServiceTicketLaborLine(
  laborLineId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('service_ticket_labor_lines').delete().eq('labor_line_id', laborLineId);
  return { error: error ?? null };
}

const SERVICE_TICKET_SECTION_SELECT =
  'section_id, service_ticket_id, grid, label, sort_order, show_subtotal, created_at, updated_at';

/**
 * Saves a heading over a ticket's charges or labor. A new section takes the ticket and grid; an
 * edit writes only the fields it is given. Sections group lines and never price.
 */
export async function saveServiceTicketSection(
  values: Partial<Pick<ServiceTicketSectionRow, 'section_id' | 'label' | 'sort_order' | 'show_subtotal'>> & {
    service_ticket_id: string;
    grid: ServiceTicketSectionGrid;
    actorUserId?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: ServiceTicketSectionRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const patch: { label?: string; sort_order?: number; show_subtotal?: boolean; updated_by: string | null; updated_at: string } = {
    updated_by: values.actorUserId ?? null,
    updated_at: new Date().toISOString(),
  };
  if (values.label !== undefined) patch.label = String(values.label ?? '').trim().slice(0, 200);
  if (values.sort_order !== undefined) patch.sort_order = Number(values.sort_order ?? 0);
  if (values.show_subtotal !== undefined) patch.show_subtotal = Boolean(values.show_subtotal);
  const query = values.section_id
    ? supabase.from('service_ticket_sections').update(patch).eq('section_id', values.section_id)
    : supabase.from('service_ticket_sections').insert({
      ...patch,
      service_ticket_id: values.service_ticket_id,
      grid: values.grid,
      created_by: values.actorUserId ?? null,
    });
  const { data, error } = await query.select(SERVICE_TICKET_SECTION_SELECT).maybeSingle();
  return { data: (data as ServiceTicketSectionRow | null) ?? null, error: error ?? null };
}

/** Deletes a heading. Its lines stay on the ticket, unsectioned (ON DELETE SET NULL). */
export async function deleteServiceTicketSection(
  sectionId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('service_ticket_sections').delete().eq('section_id', sectionId);
  return { error: error ?? null };
}

export async function saveServiceTicketLogistics(
  values: Partial<ServiceTicketLogisticsRow> & {
    logistics_id?: string | null;
    service_ticket_id: string;
    actorUserId?: string | null;
  },
  options?: ClientOption,
): Promise<{ data: ServiceTicketLogisticsRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const payload = {
    service_ticket_id: values.service_ticket_id,
    origin_location_id: values.origin_location_id ?? null,
    destination_client_id: values.destination_client_id ?? null,
    destination_client_branch_id: values.destination_client_branch_id ? Number(values.destination_client_branch_id) : null,
    origin_snapshot: values.origin_snapshot ?? {},
    destination_snapshot: values.destination_snapshot ?? {},
    total_miles: maybeNumber(values.total_miles),
    travel_hours: maybeNumber(values.travel_hours),
    notes: values.notes ?? null,
    collapsed: values.collapsed ?? true,
    updated_by: values.actorUserId ?? null,
  };
  const query = values.logistics_id
    ? supabase.from('service_ticket_logistics').update(payload).eq('logistics_id', values.logistics_id)
    : supabase.from('service_ticket_logistics').insert({ ...payload, created_by: values.actorUserId ?? null });
  const { data, error } = await query.select(`
    logistics_id, service_ticket_id, origin_location_id, destination_client_id, destination_client_branch_id,
    origin_snapshot, destination_snapshot, total_miles, travel_hours, notes, collapsed
  `).maybeSingle();
  return { data: (data as unknown as ServiceTicketLogisticsRow) ?? null, error: error ?? null };
}

export type ServiceTicketScheduleListRow = {
  shift_id: string;
  starts_at: string;
  ends_at: string | null;
  source: string;
  schedule_status: string;
  publish_status: string;
  required_people: number;
  /** The people `people` names, counted. */
  assigned_count: number;
  shift_label: string | null;
  people: Array<{ name: string | null; kind: 'employee' | 'freelancer' }>;
  calendar_color: string | null;
  site_location_id: string | null;
  /** The visit holds recorded time: its times are the actual clock-in and clock-out. */
  recorded: boolean;
  /** The plan that recorded time replaced, when it replaced one. */
  planned_starts_at: string | null;
  planned_ends_at: string | null;
};

/**
 * Every active visit on one external ticket: its own visit, its independent visits and its
 * recorded time, with who is on each. Planned visits are readable by anyone who can open the
 * ticket, unlike the shift table itself, which hides other people's shifts from service staff.
 * Recorded-only visits (time with no plan) follow `job_time_history`: your own and your reports',
 * or everyone's for admins, accounting and schedule managers.
 */
export async function fetchServiceTicketScheduleShifts(
  ticketId: string,
  options?: ClientOption,
): Promise<{ data: ServiceTicketScheduleListRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('service_ticket_schedule_shifts', { p_ticket_id: ticketId });
  const rows = (Array.isArray(data) ? data : []) as ServiceTicketScheduleListRow[];
  return { data: rows.map(row => ({ ...row, people: Array.isArray(row.people) ? row.people : [] })), error };
}

/** One plan of visits for an external ticket: a window per day, sharing everything else. */
export type ServiceTicketVisitPlan = {
  calendar?: import('./scheduleCalendar').CalendarReview;
  visits: Array<{ startsAt: string; endsAt: string; visitDate: string }>;
  requiredPeople: number;
  /** 0 for no lunch. */
  unpaidBreakMinutes: number;
  notes: string;
  profileIds: string[];
  freelancerIds: string[];
};

/**
 * Adds one published, staffed visit per planned day to an external ticket, all or nothing
 * (`service_ticket_save_visits`). Sending the same request id again returns the same visits, so a
 * retry after a lost response never doubles them. The people on them get the schedule email, as
 * when /schedule publishes.
 */
export async function saveServiceTicketVisits(
  ticketId: string,
  requestId: string,
  plan: ServiceTicketVisitPlan,
  options?: ClientOption,
): Promise<{ data: string[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('service_ticket_save_visits', {
    p_service_ticket_id: ticketId,
    p_request_id: requestId,
    p_input: plan,
  });
  if (error) return { data: [], error };
  const shiftIds = (Array.isArray(data) ? data : []).map(id => String(id)).filter(Boolean);
  if (shiftIds.length) void notifyLaborCalendar({ type: 'published', shiftIds });
  return { data: shiftIds, error: null };
}

/** The ticket's own visit ('header', the default: the one that follows the ticket's date), or its
 * earliest active visit of any kind ('primary': the one the ticket page shows and puts a vehicle
 * on, as service_ticket_assign_vehicle does). Recorded-only time is never a visit. */
export async function fetchServiceTicketScheduleShift(
  ticketId: string,
  options?: ClientOption & { visit?: 'header' | 'primary' },
): Promise<{ data: ServiceTicketScheduleShiftRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const baseQuery = supabase
    .from('labor_schedule_shifts')
    .select('shift_id, service_ticket_id, service_ticket_labor_line_id, site_location_id, starts_at, ends_at, schedule_status, updated_at')
    .eq('service_ticket_id', ticketId)
    .neq('schedule_status', 'cancelled');
  const shiftQuery = options?.visit === 'primary'
    ? baseQuery.neq('source', 'time_tracking')
    : baseQuery.eq('source', 'service_ticket');
  const { data: shift, error: shiftError } = await shiftQuery
    .order('starts_at', { ascending: true })
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (shiftError) return { data: null, error: shiftError };
  if (!shift?.shift_id) return { data: null, error: null };

  const { data: assignments, error: assignmentError } = await supabase
    .from('labor_schedule_shift_vehicle_assignments')
    .select(`
      assignment_id, shift_id, vehicle_id, status,
      vehicle:location_fleet_vehicles!labor_schedule_shift_vehicle_assignments_vehicle_id_fkey(
        name, vehicle_type, license_plate, capacity
      )
    `)
    .eq('shift_id', String(shift.shift_id))
    .eq('status', 'assigned')
    .order('assigned_at', { ascending: true });
  if (assignmentError) return { data: null, error: assignmentError };

  const vehicleAssignments: ServiceTicketScheduleVehicleAssignmentRow[] = rowArray(assignments as Array<Record<string, unknown>>).map((row) => {
    const vehicle = firstRelated(row.vehicle as Record<string, unknown> | Record<string, unknown>[] | null | undefined);
    return {
      assignment_id: String(row.assignment_id ?? ''),
      shift_id: String(row.shift_id ?? ''),
      vehicle_id: String(row.vehicle_id ?? ''),
      status: (row.status === 'cancelled' || row.status === 'removed' ? row.status : 'assigned') as ServiceTicketScheduleVehicleAssignmentRow['status'],
      display_name: normalizeText(vehicle?.name as string | null | undefined) || null,
      vehicle_type: normalizeText(vehicle?.vehicle_type as string | null | undefined) || null,
      license_plate: normalizeText(vehicle?.license_plate as string | null | undefined) || null,
      capacity: normalizeText(vehicle?.capacity as string | null | undefined) || null,
    };
  }).filter(row => row.assignment_id && row.vehicle_id);

  return {
    data: {
      shift_id: String(shift.shift_id),
      service_ticket_id: String(shift.service_ticket_id),
      service_ticket_labor_line_id: shift.service_ticket_labor_line_id ? String(shift.service_ticket_labor_line_id) : null,
      site_location_id: shift.site_location_id ? String(shift.site_location_id) : null,
      starts_at: String(shift.starts_at),
      ends_at: shift.ends_at ? String(shift.ends_at) : null,
      schedule_status: String(shift.schedule_status ?? 'planned'),
      updated_at: shift.updated_at ? String(shift.updated_at) : null,
      vehicle_assignments: vehicleAssignments,
    },
    error: null,
  };
}

export async function assignServiceTicketVehicle(
  ticketId: string,
  vehicleId: string | null,
  options?: ClientOption,
): Promise<{ data: string | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('service_ticket_assign_vehicle', {
    p_service_ticket_id: ticketId,
    p_vehicle_id: vehicleId || null,
  });
  return { data: data == null ? null : String(data), error: error ?? null };
}

export async function addAssetToTicket(
  params: {
    ticketId: string;
    assetId: string;
    issueNotes?: string | null;
    productId?: string | null;
    originalLocationId?: string | null;
    currentLocationIdAtIntake?: string | null;
    serviceLocationId?: string | null;
    inboundTransferId?: string | null;
    replacementCostSnapshot?: number | null;
    assetAgeMonths?: number | null;
  } & ClientOption,
): Promise<{ data: ServiceTicketAssetRow | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const issueNotes = normalizeText(params.issueNotes);
  if (!issueNotes) {
    return { data: null, error: { message: 'Issue notes are required for service assets' } as PostgrestError };
  }
  const { data, error } = await supabase
    .from('service_ticket_assets')
    .insert({
      service_ticket_id: params.ticketId,
      asset_id: params.assetId,
      issue_notes: issueNotes,
      product_id: params.productId ?? null,
      original_location_id: params.originalLocationId ?? null,
      current_location_id_at_intake: params.currentLocationIdAtIntake ?? null,
      service_location_id: params.serviceLocationId ?? null,
      inbound_transfer_id: params.inboundTransferId ?? null,
      replacement_cost_snapshot: params.replacementCostSnapshot ?? null,
      asset_age_months: params.assetAgeMonths ?? null,
    })
    .select('service_ticket_asset_id, service_ticket_id, asset_id, product_id, issue_notes, status, original_location_id, current_location_id_at_intake, service_location_id, inbound_transfer_id, replacement_cost_snapshot, asset_age_months')
    .maybeSingle();
  return { data: (data as unknown as ServiceTicketAssetRow) ?? null, error: error ?? null };
}

export type ServiceIntakeResult = {
  ticketId: string;
  /** The ticket's number: its inbound transfer's, or its own at the service site. */
  docNo: string | null;
  /** The inbound transfer to the service site; null for assets already there. */
  transferId: string | null;
  assetIds: string[];
  /** R2's nightly import may put an asset's status back (its open ticket still marks it in service). */
  r2ResetsNightly: boolean;
  /** The person can open the ticket; it lives at the service site, which staff of other sites do not see. */
  canOpen: boolean;
  /** The same request key had already made this intake (a retried submit). */
  replayed: boolean;
};

function newIntakeRequestKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

/**
 * Sends 1-50 assets at one site to service in one transaction (`service_intake_assets`): the inbound
 * transfer to the service site, whose trigger opens the Asset Ticket (or the ticket itself for assets
 * already there), the issue on each asset's line, each asset to maintenance with a status history row,
 * and one action. All or none; the database checks who and where. Reuse `requestKey` to retry the same
 * submit. Throws the database error, whose hint names a refusal (`service_intake_*`).
 */
export async function sendAssetsToService(
  params: {
    requestKey: string;
    assetIds: string[];
    issueNotes: string;
    priority?: ServiceTicketPriority;
    summary?: string | null;
    source?: string | null;
  } & ClientOption,
): Promise<ServiceIntakeResult> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('service_intake_assets', {
    p_request_key: params.requestKey,
    p_asset_ids: params.assetIds,
    p_issue: params.issueNotes,
    p_priority: params.priority ?? 'normal',
    p_summary: normalizeText(params.summary) || null,
    p_source: params.source ?? null,
  });
  if (error) throw error;
  const result = (data ?? {}) as Record<string, unknown>;
  const ticketId = result.ticket_id ? String(result.ticket_id) : '';
  if (!ticketId) {
    throw new AppError('unexpected', 'The service ticket may not have been created. Check Service before trying again.', {
      context: 'service.intake',
    });
  }
  return {
    ticketId,
    docNo: result.doc_no ? String(result.doc_no) : null,
    transferId: result.transfer_id ? String(result.transfer_id) : null,
    assetIds: Array.isArray(result.asset_ids) ? result.asset_ids.map(String) : [],
    r2ResetsNightly: result.r2_resets_nightly === true,
    canOpen: result.can_open === true,
    replayed: result.replayed === true,
  };
}

/** One asset to service (`sendAssetsToService`), as `{ data, error }` for the single-asset screens. */
export async function createServiceIntakeForAsset(
  params: {
    assetId: string;
    issueNotes: string;
    priority?: ServiceTicketPriority;
    summary?: string | null;
    source?: string | null;
    /** Reuse to retry the same submit; a new key per call otherwise. */
    requestKey?: string;
  } & ClientOption,
): Promise<{ data: ServiceIntakeResult | null; error: PostgrestError | Error | null }> {
  const issue = normalizeText(params.issueNotes);
  if (!issue) return { data: null, error: new Error('Issue notes are required') };
  try {
    const data = await sendAssetsToService({
      requestKey: params.requestKey ?? newIntakeRequestKey(),
      assetIds: [params.assetId],
      issueNotes: issue,
      priority: params.priority,
      summary: params.summary,
      source: params.source,
      supabase: params.supabase,
    });
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error as PostgrestError | Error };
  }
}

export async function addExistingAssetToServiceTicket(
  params: {
    ticketId: string;
    assetIdentifier: string;
    issueNotes: string;
    actorUserId?: string | null;
    actorProfileId?: string | null;
  } & ClientOption,
): Promise<{
  data: {
    ticketAsset: ServiceTicketAssetRow | null;
    inboundTransferId: string | null;
    settings: ServiceSettings;
  } | null;
  error: PostgrestError | Error | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const issue = normalizeText(params.issueNotes);
  if (!issue) return { data: null, error: new Error('Issue notes are required') };

  const assetResult = await fetchServiceAssetByIdentifier(params.assetIdentifier, { supabase });
  if (assetResult.error) return { data: null, error: assetResult.error };
  const asset = assetResult.data;
  if (!asset) return { data: null, error: new Error('Asset not found') };

  const settingsResult = await fetchServiceSettings({ supabase });
  if (settingsResult.error) return { data: null, error: settingsResult.error };
  const settings = settingsResult.data;
  if (!settings.serviceLocationId) return { data: null, error: new Error('Service location is not configured') };

  const originalLocationId = asset.current_location_id ?? asset.home_location_id ?? null;
  let inboundTransferId: string | null = null;
  if (originalLocationId && originalLocationId !== settings.serviceLocationId) {
    const transfer = await createLinkedAssetTransfer({
      fromLocationId: originalLocationId,
      toLocationId: settings.serviceLocationId,
      ticketId: params.ticketId,
      asset,
      supabase,
    });
    if (transfer.error) return { data: null, error: transfer.error };
    inboundTransferId = transfer.transferId;
  }

  const linked = await addAssetToTicket({
    ticketId: params.ticketId,
    assetId: asset.asset_id,
    issueNotes: issue,
    originalLocationId,
    currentLocationIdAtIntake: asset.current_location_id ?? null,
    serviceLocationId: settings.serviceLocationId,
    inboundTransferId,
    replacementCostSnapshot: replacementCostForAsset(asset),
    assetAgeMonths: monthsSince(asset.date_in_service ?? asset.received_at ?? null),
    supabase,
  });
  if (linked.error) return { data: null, error: linked.error };

  // The asset goes to maintenance with the line's issue, and its history row, in one call.
  if (linked.data?.service_ticket_asset_id) {
    const { error: inServiceError } = await applyServiceAssetStatus({
      serviceTicketAssetId: linked.data.service_ticket_asset_id,
      change: 'in_service',
      supabase,
    });
    if (inServiceError) {
      return { data: null, error: toPartialSaveError(inServiceError, 'The asset was added to the ticket, but it was not marked in service.', 'service.addAsset.inService') };
    }
  }

  await addServiceAction({
    ticketId: params.ticketId,
    action: `Asset added for service: ${displayAssetLabel(asset)}`,
    details: {
      asset_id: asset.asset_id,
      barcode: asset.barcode,
      original_location_id: originalLocationId,
      service_location_id: settings.serviceLocationId,
      inbound_transfer_id: inboundTransferId,
    },
    actionBy: params.actorUserId ?? null,
    supabase,
  });

  return {
    data: {
      ticketAsset: linked.data,
      inboundTransferId,
      settings,
    },
    error: null,
  };
}

export async function removeAssetFromTicket(
  serviceTicketAssetId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('service_ticket_assets')
    .delete()
    .eq('service_ticket_asset_id', serviceTicketAssetId);
  return { error: error ?? null };
}

export async function updateTicketAssetStatus(
  serviceTicketAssetId: string,
  newStatus: TicketAssetStatus,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('service_ticket_assets')
    .update({ status: newStatus })
    .eq('service_ticket_asset_id', serviceTicketAssetId);
  return { error: error ?? null };
}

export async function fetchReplacementCandidatesForTicketAsset(
  serviceTicketAssetId: string,
  options?: ClientOption,
): Promise<{ data: ServiceAssetRow[]; error: PostgrestError | Error | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data: sta, error: staError } = await supabase
    .from('service_ticket_assets')
    .select(`service_ticket_asset_id, asset_id, asset:assets!fk_sta_asset(${SERVICE_ASSET_SELECT})`)
    .eq('service_ticket_asset_id', serviceTicketAssetId)
    .maybeSingle();
  if (staError) return { data: [], error: staError };
  const sourceAsset = (sta as any)?.asset as ServiceAssetRow | null;
  if (!sourceAsset?.item_id) return { data: [], error: new Error('Service asset item is missing') };

  const settingsResult = await fetchServiceSettings({ supabase });
  if (settingsResult.error) return { data: [], error: settingsResult.error };
  const sourceLocationId = settingsResult.data.replacementSourceLocationId;
  if (!sourceLocationId) return { data: [], error: new Error('Replacement source location is not configured') };

  const { data, error } = await supabase
    .from('assets')
    .select(SERVICE_ASSET_SELECT)
    .eq('item_id', sourceAsset.item_id)
    .eq('status', 'available')
    .neq('asset_id', sourceAsset.asset_id)
    .or(`current_location_id.eq.${sourceLocationId},and(current_location_id.is.null,home_location_id.eq.${sourceLocationId})`)
    .order('barcode', { ascending: true })
    .limit(25);
  return { data: Array.isArray(data) ? (data as unknown as ServiceAssetRow[]) : [], error: error ?? null };
}

export async function fetchReceivedReplacementAssetsForTicketAsset(
  serviceTicketAssetId: string,
  options?: ClientOption,
): Promise<{ data: ReceivedReplacementAssetCandidateRow[]; error: PostgrestError | Error | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data: sta, error: staError } = await supabase
    .from('service_ticket_assets')
    .select(`service_ticket_asset_id, service_ticket_id, asset_id, replacement_asset_id, replacement_po_id, asset:assets!fk_sta_asset(${SERVICE_ASSET_SELECT})`)
    .eq('service_ticket_asset_id', serviceTicketAssetId)
    .maybeSingle();
  if (staError) return { data: [], error: staError };
  if (!sta) return { data: [], error: new Error('Service ticket asset not found') };

  const serviceAsset = sta as unknown as ServiceTicketAssetReplacementLookupRow;
  const originalAsset = firstRelated(serviceAsset.asset);
  const originalItemId = originalAsset?.item_id ?? null;
  const replacementPoId = serviceAsset.replacement_po_id as string | null;
  if (!replacementPoId) return { data: [], error: null };
  if (!originalItemId) return { data: [], error: new Error('Original service asset item is missing') };

  const { data: po, error: poError } = await supabase
    .from('purchase_orders')
    .select(`
      po_id, doc_no, location_id, status, created_at,
      location:locations!left(name),
      lines:purchase_order_lines(po_line_id, po_id, description, item_id, qty, received_qty, status, created_at, updated_at)
    `)
    .eq('po_id', replacementPoId)
    .maybeSingle();
  if (poError) return { data: [], error: poError };
  if (!po) return { data: [], error: new Error('Replacement purchase order not found') };

  const poRow = po as unknown as ReplacementPurchaseOrderLookupRow;
  const lines = Array.isArray(poRow.lines) ? poRow.lines : [];
  const receivedLines = lines.filter((line) =>
    line?.item_id === originalItemId &&
    String(line?.status || '').toLowerCase() !== 'cancelled' &&
    numericValue(line?.received_qty) > 0,
  );
  if (receivedLines.length === 0) return { data: [], error: null };

  const lineIds = uniqueStrings(receivedLines.map((line) => line.po_line_id));
  const lineById = new Map<string, ServicePurchaseOrderLineRow>(receivedLines.map((line) => [String(line.po_line_id), line]));

  let receiptLines: PurchaseOrderReceiptAssetLineRow[] = [];
  const { data: receipts } = await supabase
    .from('purchase_order_receipts')
    .select('po_receipt_id')
    .eq('po_id', replacementPoId)
    .eq('status', 'applied');
  const receiptIds = uniqueStrings(Array.isArray(receipts) ? (receipts as PurchaseOrderReceiptIdRow[]).map((row) => row.po_receipt_id) : []);
  if (receiptIds.length > 0 && lineIds.length > 0) {
    const { data } = await supabase
      .from('purchase_order_receipt_lines')
      .select('po_receipt_line_id, po_line_id, barcode, serial_number, applied_received_qty, decision, created_at')
      .in('po_receipt_id', receiptIds)
      .in('po_line_id', lineIds)
      .gt('applied_received_qty', 0)
      .eq('decision', 'applied');
    receiptLines = Array.isArray(data) ? (data as unknown as PurchaseOrderReceiptAssetLineRow[]) : [];
  }

  const barcodeKeys = uniqueStrings(receiptLines.map((line) => line?.barcode));
  const serialKeys = uniqueStrings(receiptLines.map((line) => line?.serial_number));
  const assetsById = new Map<string, ServiceAssetRow>();

  if (barcodeKeys.length > 0) {
    const { data } = await supabase
      .from('assets')
      .select(SERVICE_ASSET_SELECT)
      .in('barcode', barcodeKeys)
      .eq('status', 'available');
    (Array.isArray(data) ? (data as unknown as ServiceAssetRow[]) : []).forEach((asset) => assetsById.set(String(asset.asset_id), asset));
  }

  if (serialKeys.length > 0) {
    const { data } = await supabase
      .from('assets')
      .select(SERVICE_ASSET_SELECT)
      .in('serial_number', serialKeys)
      .eq('status', 'available');
    (Array.isArray(data) ? (data as unknown as ServiceAssetRow[]) : []).forEach((asset) => assetsById.set(String(asset.asset_id), asset));
  }

  let fallbackQuery = supabase
    .from('assets')
    .select(SERVICE_ASSET_SELECT)
    .eq('item_id', originalItemId)
    .eq('status', 'available')
    .neq('asset_id', serviceAsset.asset_id);
  if (poRow.location_id) {
    fallbackQuery = fallbackQuery.or(`current_location_id.eq.${poRow.location_id},and(current_location_id.is.null,home_location_id.eq.${poRow.location_id})`);
  }
  const { data: fallbackAssets, error: fallbackError } = await fallbackQuery
    .gte('received_at', poRow.created_at || '1970-01-01T00:00:00.000Z')
    .order('received_at', { ascending: false, nullsFirst: false })
    .limit(50);
  if (fallbackError) return { data: [], error: fallbackError };
  (Array.isArray(fallbackAssets) ? (fallbackAssets as unknown as ServiceAssetRow[]) : []).forEach((asset) => assetsById.set(String(asset.asset_id), asset));

  const candidateIds = Array.from(assetsById.keys());
  let linkedReplacementIds = new Set<string>();
  if (candidateIds.length > 0) {
    const { data: links } = await supabase
      .from('service_ticket_assets')
      .select('service_ticket_asset_id, replacement_asset_id')
      .in('replacement_asset_id', candidateIds);
    const linkRows = Array.isArray(links) ? (links as unknown as ServiceReplacementLinkRow[]) : [];
    linkedReplacementIds = new Set(
      linkRows
        .filter((link) => link.service_ticket_asset_id !== serviceTicketAssetId)
        .flatMap((link) => link.replacement_asset_id ? [String(link.replacement_asset_id)] : []),
    );
  }

  const candidates: ReceivedReplacementAssetCandidateRow[] = [];
  assetsById.forEach((asset) => {
    if (asset.asset_id === serviceAsset.asset_id) return;
    if (asset.item_id !== originalItemId) return;
    if (linkedReplacementIds.has(asset.asset_id)) return;
    if (!sameAssetLocation(asset, poRow.location_id)) return;

    const receiptLine = receiptLines.find((line) =>
      (line?.barcode && asset.barcode === line.barcode) ||
      (line?.serial_number && asset.serial_number === line.serial_number),
    );
    const poLine = (receiptLine?.po_line_id ? lineById.get(String(receiptLine.po_line_id)) : null) ?? receivedLines[0];
    candidates.push({
      ...asset,
      po_id: poRow.po_id,
      po_doc_no: poRow.doc_no ?? null,
      po_status: poRow.status ?? null,
      po_line_id: String(poLine.po_line_id),
      po_line_description: poLine.description ?? null,
      po_line_received_qty: numericValue(poLine.received_qty),
      po_location_id: poRow.location_id ?? null,
      po_location_name: firstRelated(poRow.location)?.name ?? null,
      receipt_source: receiptLine ? 'receipt_audit' : 'received_asset',
    });
  });

  candidates.sort((a, b) => {
    const aTime = a.received_at ? Date.parse(a.received_at) || 0 : 0;
    const bTime = b.received_at ? Date.parse(b.received_at) || 0 : 0;
    return bTime - aTime || displayAssetLabel(a).localeCompare(displayAssetLabel(b));
  });

  return { data: candidates, error: null };
}

export async function assignReplacementAssetToService(
  params: {
    serviceTicketAssetId: string;
    replacementAssetId: string;
    actorUserId?: string | null;
    actorProfileId?: string | null;
  } & ClientOption,
): Promise<{ error: PostgrestError | Error | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data: sta, error: staError } = await supabase
    .from('service_ticket_assets')
    .select(`service_ticket_asset_id, service_ticket_id, asset_id, original_location_id, service_location_id, asset:assets!fk_sta_asset(${SERVICE_ASSET_SELECT})`)
    .eq('service_ticket_asset_id', params.serviceTicketAssetId)
    .maybeSingle();
  if (staError) return { error: staError };
  if (!sta) return { error: new Error('Service ticket asset not found') };
  const serviceAsset = identifiedReplacementRow(sta as unknown as ServiceTicketAssetReplacementLookupRow);
  if (!serviceAsset) return { error: new Error('Identify the serialized asset before assigning a replacement') };
  const originalAsset = firstRelated(serviceAsset.asset);
  const originalLocationId = serviceAsset.original_location_id ?? originalAsset?.home_location_id ?? originalAsset?.current_location_id ?? null;
  if (!originalLocationId) return { error: new Error('Original location is required before assigning a replacement') };

  const replacementResult = await fetchServiceAssetByIdentifier(params.replacementAssetId, { supabase });
  if (replacementResult.error) return { error: replacementResult.error };
  const replacement = replacementResult.data;
  if (!replacement) return { error: new Error('Replacement asset not found') };
  if (replacement.status !== 'available') return { error: new Error('Replacement asset must be available') };

  const settingsResult = await fetchServiceSettings({ supabase });
  if (settingsResult.error) return { error: settingsResult.error };
  const replacementSourceLocationId = settingsResult.data.replacementSourceLocationId;
  if (!replacementSourceLocationId) return { error: new Error('Replacement source location is not configured') };

  const transfer = await createLinkedAssetTransfer({
    fromLocationId: replacementSourceLocationId,
    toLocationId: originalLocationId,
    ticketId: serviceAsset.service_ticket_id,
    asset: replacement,
    isPermanent: true,
    supabase,
  });
  if (transfer.error) return { error: transfer.error };

  const { error: updateError } = await supabase
    .from('service_ticket_assets')
    .update({
      replacement_asset_id: replacement.asset_id,
      replacement_transfer_id: transfer.transferId,
      return_destination: 'service_inventory',
      updated_at: new Date().toISOString(),
    })
    .eq('service_ticket_asset_id', params.serviceTicketAssetId);
  if (updateError) return { error: updateError };

  await createAssetNote({
    assetId: replacement.asset_id,
    noteType: 'replacement',
    title: 'Issued as service replacement',
    body: `Replacement for ${displayAssetLabel(originalAsset)} on service ticket ${String(serviceAsset.service_ticket_id).slice(0, 8)}.`,
    relatedAssetId: serviceAsset.asset_id,
    ticketId: serviceAsset.service_ticket_id,
    createdByProfileId: params.actorProfileId ?? null,
    supabase,
  });
  await createAssetNote({
    assetId: serviceAsset.asset_id,
    noteType: 'replacement',
    title: 'Replacement asset issued',
    body: `${displayAssetLabel(replacement)} was issued as this asset's replacement. Repaired original will stay in 032 service inventory.`,
    relatedAssetId: replacement.asset_id,
    ticketId: serviceAsset.service_ticket_id,
    createdByProfileId: params.actorProfileId ?? null,
    supabase,
  });
  await addServiceAction({
    ticketId: serviceAsset.service_ticket_id,
    action: `Replacement assigned: ${displayAssetLabel(replacement)}`,
    details: {
      original_asset_id: serviceAsset.asset_id,
      replacement_asset_id: replacement.asset_id,
      replacement_transfer_id: transfer.transferId,
    },
    actionBy: params.actorUserId ?? null,
    supabase,
  });
  return { error: null };
}

export async function completeReplacementByOrderForService(
  params: {
    serviceTicketAssetId: string;
    replacementAssetId: string;
    actorUserId?: string | null;
    actorProfileId?: string | null;
  } & ClientOption,
): Promise<{ data: { transferId: string | null } | null; error: PostgrestError | Error | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data: sta, error: staError } = await supabase
    .from('service_ticket_assets')
    .select(`service_ticket_asset_id, service_ticket_id, asset_id, original_location_id, replacement_asset_id, replacement_po_id, asset:assets!fk_sta_asset(${SERVICE_ASSET_SELECT})`)
    .eq('service_ticket_asset_id', params.serviceTicketAssetId)
    .maybeSingle();
  if (staError) return { data: null, error: staError };
  if (!sta) return { data: null, error: new Error('Service ticket asset not found') };

  const serviceAsset = identifiedReplacementRow(sta as unknown as ServiceTicketAssetReplacementLookupRow);
  if (!serviceAsset) return { data: null, error: new Error('Identify the serialized asset before linking a replacement') };
  if (serviceAsset.replacement_asset_id) {
    return { data: null, error: new Error('A replacement asset is already linked to this service asset') };
  }
  if (!serviceAsset.replacement_po_id) {
    return { data: null, error: new Error('Create and receive a replacement purchase order before linking a received asset') };
  }

  const originalAsset = firstRelated(serviceAsset.asset);
  const originalLocationId = serviceAsset.original_location_id ?? originalAsset?.home_location_id ?? originalAsset?.current_location_id ?? null;
  if (!originalLocationId) return { data: null, error: new Error('Original location is required before completing a replacement') };

  const candidates = await fetchReceivedReplacementAssetsForTicketAsset(params.serviceTicketAssetId, { supabase });
  if (candidates.error) return { data: null, error: candidates.error };
  const replacement = candidates.data.find((asset) => asset.asset_id === params.replacementAssetId);
  if (!replacement) {
    return { data: null, error: new Error('Select an available asset that was received against this replacement PO') };
  }

  const { data: duplicateLinks, error: duplicateError } = await supabase
    .from('service_ticket_assets')
    .select('service_ticket_asset_id')
    .eq('replacement_asset_id', replacement.asset_id)
    .neq('service_ticket_asset_id', params.serviceTicketAssetId)
    .limit(1);
  if (duplicateError) return { data: null, error: duplicateError };
  if (Array.isArray(duplicateLinks) && duplicateLinks.length > 0) {
    return { data: null, error: new Error('This asset is already linked as a service replacement') };
  }

  const now = new Date().toISOString();
  const sourceLocationId = replacement.current_location_id ?? replacement.home_location_id ?? replacement.po_location_id ?? originalLocationId;
  const transfer = await createLinkedAssetTransfer({
    fromLocationId: sourceLocationId,
    toLocationId: originalLocationId,
    ticketId: serviceAsset.service_ticket_id,
    asset: replacement,
    isPermanent: true,
    completeNow: true,
    actorUserId: params.actorUserId ?? null,
    completedAt: now,
    completionNotes: `Replacement PO ${replacement.po_doc_no || replacement.po_id.slice(0, 8)} asset linked to service ticket ${String(serviceAsset.service_ticket_id).slice(0, 8)}.`,
    supabase,
  });
  if (transfer.error) return { data: null, error: transfer.error };

  const { error: updateError } = await supabase
    .from('service_ticket_assets')
    .update({
      replacement_asset_id: replacement.asset_id,
      replacement_transfer_id: transfer.transferId,
      outcome: 'replace_order',
      status: 'complete',
      return_destination: 'service_inventory',
      completed_at: now,
      updated_at: now,
    })
    .eq('service_ticket_asset_id', params.serviceTicketAssetId);
  if (updateError) return { data: null, error: updateError };

  // The replacement becomes available at the original site, "Replacement issued from PO …", with its
  // history row (worked out from the line just saved).
  const { error: issuedError } = await applyServiceAssetStatus({
    serviceTicketAssetId: params.serviceTicketAssetId,
    change: 'replacement_issued',
    supabase,
  });
  if (issuedError) {
    return { data: null, error: toPartialSaveError(issuedError, 'The replacement was linked, but it was not made available at the original site.', 'service.replacement.available') };
  }

  await createAssetNote({
    assetId: replacement.asset_id,
    noteType: 'replacement',
    title: 'Received PO asset issued as service replacement',
    body: `Received asset from replacement PO ${replacement.po_doc_no || replacement.po_id.slice(0, 8)} and issued for ${displayAssetLabel(originalAsset)}. Transfer ${transfer.transferId ? transfer.transferId.slice(0, 8) : 'created'} was completed automatically.`,
    relatedAssetId: serviceAsset.asset_id,
    ticketId: serviceAsset.service_ticket_id,
    createdByProfileId: params.actorProfileId ?? null,
    supabase,
  });
  await createAssetNote({
    assetId: serviceAsset.asset_id,
    noteType: 'replacement',
    title: 'Replacement received and linked',
    body: `${displayAssetLabel(replacement)} was received on replacement PO ${replacement.po_doc_no || replacement.po_id.slice(0, 8)} and linked as this asset's replacement. The original remains assigned to service inventory after repair.`,
    relatedAssetId: replacement.asset_id,
    ticketId: serviceAsset.service_ticket_id,
    createdByProfileId: params.actorProfileId ?? null,
    supabase,
  });
  await addServiceAction({
    ticketId: serviceAsset.service_ticket_id,
    action: `Replacement PO asset linked: ${displayAssetLabel(replacement)}`,
    details: {
      service_ticket_asset_id: params.serviceTicketAssetId,
      original_asset_id: serviceAsset.asset_id,
      replacement_asset_id: replacement.asset_id,
      replacement_po_id: replacement.po_id,
      replacement_po_line_id: replacement.po_line_id,
      replacement_transfer_id: transfer.transferId,
      transfer_completed: true,
    },
    actionBy: params.actorUserId ?? null,
    supabase,
  });

  return { data: { transferId: transfer.transferId }, error: null };
}

export type ServiceAssetStatusChange = 'in_service' | 'repaired' | 'retired' | 'replacement_issued';

/**
 * Applies the asset change a saved service line calls for (service_ticket_apply_asset_status): the
 * database works out the status, sites and reason from the line and writes the status history row in
 * the same transaction. Anyone who can process the ticket; clients cannot update asset statuses
 * themselves (20261013190200).
 */
export async function applyServiceAssetStatus(
  params: { serviceTicketAssetId: string; change: ServiceAssetStatusChange } & ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('service_ticket_apply_asset_status', {
    p_request_key: crypto.randomUUID(),
    p_service_ticket_asset_id: params.serviceTicketAssetId,
    p_change: params.change,
  });
  return { error: error ?? null };
}

export async function updateServiceAssetOutcome(
  params: {
    serviceTicketAssetId: string;
    outcome: ServiceAssetOutcome;
    diagnosisNotes?: string | null;
    findingsNotes?: string | null;
    workPerformedNotes?: string | null;
    recommendationNotes?: string | null;
    repairCostEstimate?: number | null;
    repairCostActual?: number | null;
    vendorId?: string | null;
    vendorTrackingRef?: string | null;
    disposalNotes?: string | null;
    actorUserId?: string | null;
    actorProfileId?: string | null;
  } & ClientOption,
): Promise<{ error: PostgrestError | Error | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data: sta, error: staError } = await supabase
    .from('service_ticket_assets')
    .select(`*, ticket:service_tickets!fk_sta_ticket(source), asset:assets!fk_sta_asset(${SERVICE_ASSET_SELECT})`)
    .eq('service_ticket_asset_id', params.serviceTicketAssetId)
    .maybeSingle();
  if (staError) return { error: staError };
  if (!sta) return { error: new Error('Service ticket asset not found') };
  const row = sta as any as ServiceTicketAssetRow & { ticket?: { source?: ServiceTicketSource | null } | null };
  // The Workflow Engine's source system (R2) moves the item and sets its status; the Portal
  // records the outcome only, so it never creates a competing transfer or relocates the asset.
  const asset = row.asset ?? null;
  const portalMovesInventory = row.ticket?.source !== 'workflow_engine';
  const now = new Date().toISOString();
  const replacementCost = row.replacement_cost_snapshot ?? replacementCostForAsset(asset);
  const ageMonths = row.asset_age_months ?? monthsSince(asset?.date_in_service ?? asset?.received_at ?? null);
  const recommendation = calculateServiceRecommendation({
    repairCostEstimate: params.repairCostEstimate ?? null,
    repairCostActual: params.repairCostActual ?? null,
    replacementCost,
    assetAgeMonths: ageMonths,
  });
  const patch: Record<string, unknown> = {
    outcome: params.outcome,
    diagnosis_notes: normalizeText(params.diagnosisNotes) || null,
    findings_notes: normalizeText(params.findingsNotes) || null,
    work_performed_notes: normalizeText(params.workPerformedNotes) || null,
    recommendation_notes: normalizeText(params.recommendationNotes) || recommendation.notes,
    repair_cost_estimate: params.repairCostEstimate ?? null,
    repair_cost_actual: params.repairCostActual ?? null,
    replacement_cost_snapshot: replacementCost,
    asset_age_months: ageMonths,
    recommendation_score: recommendation.score,
    recommendation_label: recommendation.label,
    vendor_id: params.vendorId ?? null,
    vendor_tracking_ref: normalizeText(params.vendorTrackingRef) || null,
    disposal_notes: normalizeText(params.disposalNotes) || null,
    updated_at: now,
  };

  if (params.outcome === 'internal_repair') {
    if (!normalizeText(params.findingsNotes) || !normalizeText(params.workPerformedNotes)) {
      return { error: new AppError('validation', 'Enter findings and work performed before saving an internal repair. If no repair was needed, describe the checks you performed in Work performed.') };
    }
    patch.status = 'complete';
    patch.completed_at = now;
    if (row.sent_to_vendor_at && !row.returned_from_vendor_at) {
      patch.returned_from_vendor_at = now;
    }
    if (row.replacement_asset_id) {
      patch.return_destination = 'service_inventory';
    } else {
      patch.return_destination = 'original_location';
    }
  } else if (params.outcome === 'retire_dispose') {
    if (!normalizeText(params.findingsNotes) || !normalizeText(params.disposalNotes)) {
      return { error: new AppError('validation', 'Enter findings and retirement / disposal notes before saving a retired asset.') };
    }
    patch.status = 'complete';
    patch.retired_at = now;
    patch.completed_at = now;
    if (row.sent_to_vendor_at && !row.returned_from_vendor_at) {
      patch.returned_from_vendor_at = now;
    }
  } else if (params.outcome === 'vendor_repair') {
    if (!params.vendorId && !normalizeText(params.vendorTrackingRef)) {
      return { error: new AppError('validation', 'Choose a vendor or enter a tracking / RMA reference before saving a vendor repair.') };
    }
    patch.sent_to_vendor_at = row.sent_to_vendor_at ?? now;
  } else if (params.outcome === 'replace_order') {
    patch.return_destination = 'service_inventory';
  }

  const { data: savedOutcome, error: updateError } = await supabase
    .from('service_ticket_assets')
    .update(patch)
    .eq('service_ticket_asset_id', params.serviceTicketAssetId)
    .select('service_ticket_asset_id');
  if (updateError) return { error: updateError };
  // Recording an outcome needs current_user_can_process_service_ticket, which is narrower than who
  // can open the ticket, and row level security skips anyone else's update without an error. The
  // steps below report that the outcome saved, so confirm it first.
  if (!savedOutcome?.length) {
    return { error: new AppError('access_denied', 'The outcome was not saved: you do not have permission to process this ticket.', { context: 'service.assetOutcome' }) };
  }

  if (params.outcome === 'internal_repair' && asset?.asset_id && portalMovesInventory) {
    const serviceLocationId = row.service_location_id ?? null;
    const originalLocationId = row.original_location_id ?? null;
    let returnTransferId: string | null = null;
    if (!row.replacement_asset_id && serviceLocationId && originalLocationId && serviceLocationId !== originalLocationId) {
      const transfer = await createLinkedAssetTransfer({
        fromLocationId: serviceLocationId,
        toLocationId: originalLocationId,
        ticketId: row.service_ticket_id,
        asset,
        supabase,
      });
      if (transfer.error) return { error: transfer.error };
      returnTransferId = transfer.transferId;
      const { error: linkError } = await supabase
        .from('service_ticket_assets')
        .update({ return_transfer_id: returnTransferId, updated_at: now })
        .eq('service_ticket_asset_id', params.serviceTicketAssetId);
      if (linkError) {
        return { error: toPartialSaveError(linkError, 'The outcome was saved and a return transfer created, but the transfer could not be linked to this item.', 'service.assetOutcome.returnTransfer') };
      }
    }

    // Available again: at the service site when a replacement took its place, otherwise back at its
    // original site (worked out from the saved line, with its history row, in one call).
    const { error: availableError } = await applyServiceAssetStatus({
      serviceTicketAssetId: params.serviceTicketAssetId,
      change: 'repaired',
      supabase,
    });
    if (availableError) {
      return { error: toPartialSaveError(availableError, 'The outcome was saved, but the asset was not made available.', 'service.assetOutcome.available') };
    }
    await createAssetNote({
      assetId: asset.asset_id,
      noteType: 'service',
      title: 'Service repair completed',
      body: normalizeText(params.workPerformedNotes) || normalizeText(params.findingsNotes) || null,
      ticketId: row.service_ticket_id,
      createdByProfileId: params.actorProfileId ?? null,
      supabase,
    });
  }

  if (params.outcome === 'retire_dispose' && asset?.asset_id && portalMovesInventory) {
    const { error: retiredError } = await applyServiceAssetStatus({
      serviceTicketAssetId: params.serviceTicketAssetId,
      change: 'retired',
      supabase,
    });
    if (retiredError) {
      return { error: toPartialSaveError(retiredError, 'The outcome was saved, but the asset was not marked retired.', 'service.assetOutcome.retired') };
    }
    await createAssetNote({
      assetId: asset.asset_id,
      noteType: 'retirement',
      title: 'Marked for retirement and disposal',
      body: normalizeText(params.disposalNotes) || normalizeText(params.findingsNotes),
      ticketId: row.service_ticket_id,
      createdByProfileId: params.actorProfileId ?? null,
      supabase,
    });
  }

  await addServiceAction({
    ticketId: row.service_ticket_id,
    action: `Service outcome recorded: ${params.outcome}`,
    details: {
      service_ticket_asset_id: params.serviceTicketAssetId,
      recommendation_label: recommendation.label,
      recommendation_score: recommendation.score,
    },
    actionBy: params.actorUserId ?? null,
    supabase,
  });
  return { error: null };
}

export async function addServiceAction(
  params: {
    ticketId: string;
    action: string;
    details?: Record<string, unknown> | null;
    actionBy?: string | null;
  } & ClientOption,
): Promise<{ data: ServiceActionRow | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_actions')
    .insert({
      service_ticket_id: params.ticketId,
      action: params.action,
      details: params.details ?? {},
      action_by: params.actionBy ?? null,
    })
    .select('service_action_id, service_ticket_id, action_at, action_by, action, details')
    .maybeSingle();
  return { data: (data as unknown as ServiceActionRow) ?? null, error: error ?? null };
}

export async function addServicePart(
  params: {
    ticketId: string;
    description: string;
    vendorId?: string | null;
    qty?: number;
    unitCost?: number | null;
  } & ClientOption,
): Promise<{ data: ServicePartRow | null; error: PostgrestError | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('service_parts')
    .insert({
      service_ticket_id: params.ticketId,
      description: params.description,
      vendor_id: params.vendorId ?? null,
      qty: params.qty ?? 1,
      unit_cost: params.unitCost ?? null,
    })
    .select('service_part_id, service_ticket_id, description, vendor_id, qty, unit_cost, po_line_id, status')
    .maybeSingle();
  return { data: (data as unknown as ServicePartRow) ?? null, error: error ?? null };
}

export async function createServicePurchaseOrder(
  params: {
    ticketId: string;
    locationId?: string | null;
    vendorId?: string | null;
    notes?: string | null;
    lines: Array<{
      description: string;
      itemId?: string | null;
      qty?: number;
      unitCost?: number | null;
      servicePartId?: string | null;
    }>;
    actorUserId?: string | null;
    actorProfileId?: string | null;
    linkAs?: 'vendor_po' | 'replacement_po' | null;
    serviceTicketAssetId?: string | null;
  } & ClientOption,
): Promise<{ data: ServicePurchaseOrderRow | null; error: PostgrestError | Error | null }> {
  const supabase = params.supabase ?? getSupabaseClient();
  const normalizedLines = params.lines
    .map((line) => ({
      ...line,
      description: normalizeText(line.description),
      qty: Math.max(1, Number(line.qty ?? 1) || 1),
      unitCost: line.unitCost == null ? null : Number(line.unitCost),
    }))
    .filter((line) => line.description.length > 0);
  if (normalizedLines.length === 0) return { data: null, error: new Error('At least one PO line is required') };

  const totalAmount = normalizedLines.reduce((sum, line) => {
    const unitCost = Number(line.unitCost ?? 0);
    return sum + (Number.isFinite(unitCost) ? unitCost * line.qty : 0);
  }, 0);
  const { data: po, error: poError } = await supabase
    .from('purchase_orders')
    .insert({
      service_ticket_id: params.ticketId,
      location_id: params.locationId ?? null,
      vendor_id: params.vendorId ?? null,
      status: 'draft',
      ordered_by: params.actorProfileId ?? null,
      notes: params.notes ?? null,
      total_amount: totalAmount > 0 ? totalAmount : null,
    })
    .select('po_id, doc_no, service_ticket_id, vendor_id, location_id, status, ordered_at, expected_delivery_at, notes, total_amount')
    .maybeSingle();
  if (poError || !po) return { data: null, error: poError ?? new Error('Failed to create purchase order') };

  const linePayload = normalizedLines.map((line) => ({
    po_id: po.po_id,
    description: line.description,
    item_id: line.itemId ?? null,
    qty: line.qty,
    unit_cost: line.unitCost,
    status: 'pending',
  }));
  const { data: insertedLines, error: lineError } = await supabase
    .from('purchase_order_lines')
    .insert(linePayload)
    .select('po_line_id');
  if (lineError) return { data: po as ServicePurchaseOrderRow, error: lineError };

  const insertedLineRows = Array.isArray(insertedLines) ? insertedLines : [];
  const partResults = await Promise.all(
    normalizedLines.map((line, index) => {
      const poLineId = insertedLineRows[index]?.po_line_id ?? null;
      if (!line.servicePartId || !poLineId) return Promise.resolve(null);
      return supabase
        .from('service_parts')
        .update({ po_line_id: poLineId, status: 'ordered', updated_at: new Date().toISOString() })
        .eq('service_part_id', line.servicePartId);
    }),
  );
  const partError = partResults.find(result => result?.error)?.error;
  if (partError) {
    return {
      data: po as ServicePurchaseOrderRow,
      error: toPartialSaveError(partError, 'The purchase order was created, but its parts could not be marked as ordered.', 'service.purchaseOrder.parts'),
    };
  }

  if (params.linkAs && params.serviceTicketAssetId) {
    const field = params.linkAs === 'vendor_po' ? 'vendor_po_id' : 'replacement_po_id';
    const { error: linkError } = await supabase
      .from('service_ticket_assets')
      .update({ [field]: po.po_id, updated_at: new Date().toISOString() })
      .eq('service_ticket_asset_id', params.serviceTicketAssetId);
    if (linkError) {
      return {
        data: po as ServicePurchaseOrderRow,
        error: toPartialSaveError(linkError, 'The purchase order was created, but it could not be linked to this item.', 'service.purchaseOrder.link'),
      };
    }
  }

  await addServiceAction({
    ticketId: params.ticketId,
    action: `Purchase order created${po.doc_no ? `: ${po.doc_no}` : ''}`,
    details: {
      po_id: po.po_id,
      line_count: normalizedLines.length,
      link_as: params.linkAs ?? null,
    },
    actionBy: params.actorUserId ?? null,
    supabase,
  });

  return { data: po as ServicePurchaseOrderRow, error: null };
}

export async function updateServicePart(
  partId: string,
  updates: Partial<{
    description: string;
    vendor_id: string | null;
    qty: number;
    unit_cost: number | null;
    status: ServicePartStatus;
    po_line_id: string | null;
  }>,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('service_parts')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('service_part_id', partId);
  return { error: error ?? null };
}

/** Recorded intake quantities are operational rows, never customer charge lines. */
export type ServiceTicketTransferRow = {
  transfer_id: string;
  doc_no: string | null;
  r2_transfer_order_id: string | null;
  r2_transfer_order_iid: number | null;
  status: string | null;
  created_at: string | null;
  actual_ship_at: string | null;
  actual_receive_at: string | null;
  from_location: { name?: string | null } | null;
  to_location: { name?: string | null } | null;
  transfer_lines: Array<{ item_id: string | null; qty: number | null; ordered_qty: number | null; shipped_qty: number | null; received_qty: number | null }> | null;
};

/** An order out of the service location matched to one ticket item (`service_ticket_return_allocations`). */
export type ServiceTicketReturnMatch = {
  service_ticket_asset_id: string;
  transfer_id: string;
  transfer_line_id: string;
  transfer_number: string | null;
  transfer_status: string | null;
  actual_ship_at: string | null;
  actual_receive_at: string | null;
  match_basis: 'serial' | 'quantity';
  quantity: number;
  shipped_qty: number;
  received_qty: number;
};

export type ServiceTicketTransfers = {
  inbound: ServiceTicketTransferRow | null;
  /** One-way transfers sending items back from service; R2 links them as return legs of the inbound. */
  returns: ServiceTicketTransferRow[];
  /** Orders the service location sent on their own, matched to ticket items by serial or by quantity. */
  matchedReturns: ServiceTicketReturnMatch[];
  /** The matched orders could not be loaded; the inbound transfer and return legs still show. */
  matchedReturnsFailed: boolean;
};

const SERVICE_TICKET_TRANSFER_SELECT = `
  transfer_id, doc_no, r2_transfer_order_id, r2_transfer_order_iid, status, created_at, actual_ship_at, actual_receive_at,
  from_location:locations!fk_to_from(name),
  to_location:locations!fk_to_to(name),
  transfer_lines(item_id, qty, ordered_qty, shipped_qty, received_qty)
`.replace(/\s+/g, ' ').trim();

async function fetchServiceTicketReturnMatches(serviceTicketId: string): Promise<ServiceTicketReturnMatch[]> {
  const supabase = getSupabaseClient();
  const rows = await runSupabaseQuery(
    () => supabase.rpc('service_ticket_return_allocations', { p_service_ticket_id: serviceTicketId }),
    'Unable to load matched return orders.',
    'service.ticketTransfers.matches',
  ) as unknown as ServiceTicketReturnMatch[] | null;
  return (rows ?? []).map(row => ({
    ...row,
    quantity: Number(row.quantity) || 0,
    shipped_qty: Number(row.shipped_qty) || 0,
    received_qty: Number(row.received_qty) || 0,
  }));
}

export async function fetchServiceTicketTransfers(
  inboundTransferId: string,
  serviceTicketId?: string | null,
): Promise<ServiceTicketTransfers> {
  const supabase = getSupabaseClient();
  // Matches load beside the transfer reads; a failure there must not hide the inbound transfer.
  const matchedReturnsPromise: Promise<ServiceTicketReturnMatch[] | null> = serviceTicketId
    ? fetchServiceTicketReturnMatches(serviceTicketId).catch(() => null)
    : Promise.resolve([]);
  const inbound = await runSupabaseQuery(
    () => supabase.from('transfer_orders').select(SERVICE_TICKET_TRANSFER_SELECT).eq('transfer_id', inboundTransferId).maybeSingle(),
    'Unable to load the service transfer.',
    'service.ticketTransfers.inbound',
  ) as unknown as ServiceTicketTransferRow | null;
  if (!inbound) {
    const matched = await matchedReturnsPromise;
    return { inbound: null, returns: [], matchedReturns: matched ?? [], matchedReturnsFailed: matched === null };
  }
  const parentFilter = inbound.r2_transfer_order_iid != null
    ? `parent_transfer_id.eq.${inbound.transfer_id},r2_parent_transfer_iid.eq.${inbound.r2_transfer_order_iid}`
    : `parent_transfer_id.eq.${inbound.transfer_id}`;
  const [returns, matched] = await Promise.all([
    runSupabaseQuery(
      () => supabase.from('transfer_orders').select(SERVICE_TICKET_TRANSFER_SELECT).or(parentFilter).order('created_at', { ascending: true }),
      'Unable to load return transfers.',
      'service.ticketTransfers.returns',
    ) as unknown as Promise<ServiceTicketTransferRow[] | null>,
    matchedReturnsPromise,
  ]);
  return {
    inbound,
    returns: (returns ?? []).filter(row => row.status !== 'cancelled'),
    matchedReturns: matched ?? [],
    matchedReturnsFailed: matched === null,
  };
}

/** Saves the Portal-owned per-item notes. Never touches transfers, inventory, or outcome. */
export async function updateServiceTicketItemNotes(params: {
  serviceTicketAssetId: string;
  ticketId: string;
  diagnosisNotes: string;
  workPerformedNotes: string;
  actorUserId?: string | null;
}): Promise<void> {
  const supabase = getSupabaseClient();
  const saved = await runSupabaseQuery(
    () => supabase.from('service_ticket_assets').update({
      diagnosis_notes: normalizeText(params.diagnosisNotes) || null,
      work_performed_notes: normalizeText(params.workPerformedNotes) || null,
      updated_at: new Date().toISOString(),
    }).eq('service_ticket_asset_id', params.serviceTicketAssetId)
      .eq('service_ticket_id', params.ticketId)
      .select('service_ticket_asset_id')
      .maybeSingle(),
    'Unable to save the item notes.',
    'service.itemNotes.update',
    'mutation',
  );
  if (!saved) {
    throw new AppError('access_denied', 'The item notes were not saved because this item is unavailable or your account cannot process this ticket. Your draft is preserved. Refresh the ticket and check your Service access before trying again.');
  }
  await runSupabaseQuery(
    () => addServiceAction({
      ticketId: params.ticketId,
      action: 'Item notes updated',
      details: { service_ticket_asset_id: params.serviceTicketAssetId },
      actionBy: params.actorUserId ?? null,
      supabase,
    }),
    'Notes saved, but the history entry failed.',
    'service.itemNotes.history',
    'mutation',
  );
}
